    (async function() {
        // --- DELEGATED EVENT DISPATCH: replaces ~60 inline onclick="fn(args)"/onchange="..."
        // attributes across index.html and this file's own template strings. Needed to drop
        // 'unsafe-inline' from the CSP's script-src (inline event-handler attributes are governed
        // by script-src-attr, which falls back to script-src when unset - same rule that blocked
        // Cloudflare's own analytics beacon under script-src earlier). Markup keeps data-action
        // (the window.* function name to call), optional data-arg0/data-arg1/... (literal
        // arguments - 'true'/'false' are coerced to real booleans since the old inline attributes
        // passed them as actual JS tokens, not strings), and data-read-checked (append the
        // checkbox's live .checked as the last argument). data-action-change + a <select
        // onchange="fn(id, this.value)"> pattern works the same way on the change event, always
        // appending .value last.
        //
        // Registered at the very top of this IIFE, before anything else runs, so it's live from
        // the same instant static markup's inline handlers used to be. One behavioral note:
        // el.closest('[data-action]') finds the NEAREST matching ancestor-or-self, so a control
        // nested inside a larger clickable header (e.g. a badge inside an accordion's onclick
        // row) naturally resolves to its own action and never reaches the header's - the same
        // effect the old event.stopPropagation() calls existed for, so those were dropped as
        // redundant except where a nested control has no action of its own (see data-action="noop").
        function readActionArgs(el) {
            const args = [];
            let i = 0;
            while (el.dataset['arg' + i] !== undefined) {
                const raw = el.dataset['arg' + i];
                args.push(raw === 'true' ? true : raw === 'false' ? false : raw);
                i++;
            }
            return args;
        }
        window.noop = function() {};
        window.triggerPartnerImport = function() {
            document.getElementById('partnerImportInput').click();
        };
        // Realtor.com (NAR's licensed consumer directory) blocks automated access, so we can't
        // confirm or rely on its own search URL format - a Google search scoped to its agent
        // profile pages is the stable way to carry the typed name over. Runs on click, before the
        // link's default navigation, so the href always reflects the field's current value.
        //
        // In the installed Android app (display:standalone), target="_blank" opens a Custom Tab
        // layered over the app, so you can't flip between the lookup and the form. A page can't
        // force the real Chrome app open: an intent: URL aimed at Chrome was tried and shipped,
        // and Chrome just kept it in the Custom Tab (an intent to itself stays in-app). The Custom
        // Tab's own ⋮ → Open in Chrome does work, so #agentLookupAndroidTip says exactly that.
        window.prepareAgentLookupLink = function() {
            const link = document.getElementById('agentLookupLink');
            const name = document.getElementById('partnerNameInput').value.trim();
            link.href = name
                ? 'https://www.google.com/search?q=' + encodeURIComponent('site:realtor.com/realestateagents ' + name)
                : 'https://www.realtor.com/realestateagents/';
        };
        document.addEventListener('click', function(e) {
            const el = e.target.closest('[data-action]');
            if (!el) return;
            const fn = window[el.dataset.action];
            if (typeof fn !== 'function') return;
            const args = readActionArgs(el);
            if (el.dataset.readChecked !== undefined) args.push(el.checked);
            fn.apply(null, args);
        });
        // Keyboard half of the same dispatcher. A role="button" that isn't a real <button> (each
        // section header's arrow, the VISIBLE badge) gets Enter/Space like one - the click then
        // bubbles to the nearest data-action above, exactly as a mouse click on it would.
        document.addEventListener('keydown', function(e) {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            const el = e.target;
            if (!(el instanceof HTMLElement) || el.getAttribute('role') !== 'button' || el.tagName === 'BUTTON') return;
            e.preventDefault();
            el.click();
        });
        document.addEventListener('change', function(e) {
            const el = e.target.closest('[data-action-change]');
            if (!el) return;
            const fn = window[el.dataset.actionChange];
            if (typeof fn !== 'function') return;
            const args = readActionArgs(el);
            args.push(el.value);
            fn.apply(null, args);
        });
        // Replaces onerror="this.remove()" on every <img> that can 404 (a stale/deleted photo
        // URL) - falls back to no image rather than a broken-image icon. Resource-load `error`
        // events don't bubble, so this has to listen on the CAPTURE phase to see them at all.
        document.addEventListener('error', function(e) {
            if (e.target && e.target.dataset && e.target.dataset.onerror === 'remove') {
                e.target.remove();
            }
        }, true);

        // --- ISOCHRONE PROXY: real ORS key lives server-side in a Cloudflare Worker,
        // never in this file. See worker/DEPLOY.txt to stand up your own proxy.
        // APP_TOKEN is not a secret - it just keeps random bots off the proxy URL.
        const ISOCHRONE_PROXY_URL = 'https://referral-map-proxy.adamgrenville.workers.dev';
        const APP_TOKEN = 'referral-map-v1';

        // Loading a network fires one fetchLiveIsochrone call per colleague at once (see the
        // savedPartners.forEach that renders the initial map) - fine for a handful of colleagues,
        // but a large network (a big CSV import, say) burst hundreds of simultaneous requests at
        // the proxy Worker's own rate limit, and a one-429-retry couldn't recover from a
        // sustained flood like that - most just gave up permanently, leaving real gaps of
        // never-drawn coverage. This caps how many are ever in flight at once; everything still
        // gets fetched (queued, not dropped), just a handful at a time, and a success gets cached
        // (see updateCachedGeometry) so it's a one-time cost per colleague, not a recurring one.
        //
        // Declared here, at the very top of the file, on purpose - not down near where it's
        // conceptually grouped with the rest of the isochrone-fetching code. A returning user
        // (hasSavedProfile true) draws their own coverage synchronously during initial script
        // execution, and that call reaches into this queue before any `await` - if this `const`
        // sat later in the file, that reference would throw "Cannot access before initialization"
        // on every single page load for every returning user, silently killing the isochrone draw
        // via an uncaught promise rejection. Confirmed directly: a fresh profile save never hit
        // it (nothing calls the throttle synchronously before its own declaration executes), but
        // reloading with an already-saved profile hit it every time.
        const ISOCHRONE_MAX_CONCURRENT = 3;
        let isochroneActiveCount = 0;
        const isochroneWaiting = [];
        function runIsochroneQueue() {
            while (isochroneActiveCount < ISOCHRONE_MAX_CONCURRENT && isochroneWaiting.length) {
                const task = isochroneWaiting.shift();
                isochroneActiveCount++;
                task().finally(() => { isochroneActiveCount--; runIsochroneQueue(); });
            }
        }
        function withIsochroneThrottle(task) {
            return new Promise((resolve, reject) => {
                isochroneWaiting.push(() => task().then(resolve, reject));
                runIsochroneQueue();
            });
        }

        // --- GOOGLE GEOCODING: proxied through our own /api/geocode Pages Function rather than
        // called directly from the browser. Google's Geocoding API rejects any key with an HTTP
        // referrer restriction (that restriction type only works for browser-facing JS APIs), so
        // the key has to live server-side as a Cloudflare secret - same pattern as the isochrone
        // proxy Worker. Places Autocomplete isn't used here either: that widget is deprecated for
        // new Google accounts as of March 2025 (its predictions request fails outright), so
        // address entry stays plain text + this geocoder. ---
        async function googleGeocode(addressText) {
            const response = await fetch(`/api/geocode?address=${encodeURIComponent(addressText)}`);
            // /api/geocode rate-limits at 120 lookups/hour/IP. Left undetected, this looked
            // identical to "address not found" - which during a big CSV import meant every row
            // past the limit silently got a pin dropped at the map center, one after another,
            // with no indication anything had gone wrong. Distinguishing it lets the import loop
            // stop outright instead of dumping the rest of the file in the ocean.
            if (response.status === 429) {
                const err = new Error('Address lookup limit reached');
                err.rateLimited = true;
                throw err;
            }
            const data = await response.json();
            if (!data.found) return null;
            return { lat: data.lat, lng: data.lng, formattedAddress: data.formattedAddress, state: data.state };
        }

        // Reverse-geocodes coordinates to a formatted address string via our /api/geocode proxy.
        async function googleReverseGeocode(lat, lng) {
            const response = await fetch(`/api/geocode?lat=${lat}&lng=${lng}`);
            const data = await response.json();
            if (!data.found) return null;
            return { formattedAddress: data.formattedAddress, state: data.state };
        }

        // Geocodes an address, falling back to just the city/state portion if the exact street
        // isn't found (rare with Google's data, but still happens for brand-new subdivisions).
        // Never touches the address TEXT the user typed - only ever used to place a pin.
        async function geocodeWithFallback(addressText) {
            const result = await googleGeocode(addressText);
            if (result) return { lat: result.lat, lng: result.lng, state: result.state, usedFallback: false };

            const parts = addressText.split(',').map(s => s.trim()).filter(Boolean);
            if (parts.length < 2) return null;
            const cityState = parts.slice(-2).join(', ');

            const fallbackResult = await googleGeocode(cityState);
            if (fallbackResult) return { lat: fallbackResult.lat, lng: fallbackResult.lng, state: fallbackResult.state, usedFallback: true, fallbackLabel: cityState };
            return null;
        }

        // --- UI ACCORDION LOGIC ---
        // Animates to and from the content's real measured height, then lets go entirely
        // (max-height: none, from .accordion-visible) once open. The old fixed 800px ceiling
        // clipped anything taller - Your Profile on a phone, where every field is bumped to
        // 16px, runs well past it, and its bottom buttons disappeared behind the next section.
        // The classes stay the source of truth for open/closed; the inline max-height only
        // exists for the ~300ms the transition runs.
        function setAccordionOpen(section, open) {
            if (!section) return;
            if (open === section.classList.contains('accordion-visible')) return;
            clearTimeout(section._accordionTimer);
            if (open) {
                section.style.maxHeight = '0px';
                section.classList.remove('accordion-hidden');
                section.classList.add('accordion-visible');
                section.style.maxHeight = section.scrollHeight + 'px';
                section._accordionTimer = setTimeout(() => { section.style.maxHeight = ''; }, 320);
            } else {
                section.style.maxHeight = section.scrollHeight + 'px';
                void section.offsetHeight;  // commit the start height, or the collapse just snaps shut
                section.classList.remove('accordion-visible');
                section.classList.add('accordion-hidden');
                section.style.maxHeight = '';
            }
        }

        // Your Profile's open/closed state is remembered per device. It used to collapse itself on
        // every single load once a profile existed, so anyone who actually uses it (sharing their
        // link, checking their range) had to re-open it every visit.
        const PROFILE_OPEN_KEY = 'profile_section_open';

        function toggleAccordion(sectionId, arrowId) {
            const section = document.getElementById(sectionId);
            const open = !section.classList.contains('accordion-visible');
            setAccordionOpen(section, open);
            const arrow = document.getElementById(arrowId);
            if (arrow) {
                arrow.classList.toggle('rotate-180', !open);
                arrow.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
            if (sectionId === 'profileSection') {
                try { localStorage.setItem(PROFILE_OPEN_KEY, open ? '1' : '0'); } catch (e) { /* private mode - just don't remember */ }
                document.getElementById('profileSummary').classList.toggle('hidden', open);
            }
        }
        window.toggleAccordion = toggleAccordion;

        window.toggleNetworkIndex = function() {
            const content = document.getElementById('networkIndexContent');
            const arrow = document.getElementById('networkIndexArrow');
            const open = content.classList.contains('hidden');
            content.classList.toggle('hidden', !open);
            content.classList.toggle('flex', open);
            if (arrow) {
                arrow.classList.toggle('rotate-180', !open);
                arrow.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
        };

        window.togglePartnerAccordion = function(id) {
            const content = document.getElementById(`acc-content-${id}`);
            const open = content.classList.contains('accordion-hidden');
            setAccordionOpen(content, open);
            const arrow = document.getElementById(`arrow-${id}`);
            if (arrow) {
                arrow.classList.toggle('rotate-180', open);
                arrow.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
        };

        // --- EXPORT TO PHONE/CONTACTS (VCF) ---
        function buildVCard(p) {
            return `BEGIN:VCARD\nVERSION:3.0\nFN:${p.name}\nORG:${p.brokerage || ''}\nTEL:${p.phone || ''}\nEMAIL:${p.email || ''}\nADR:;;${p.baseName.replace(/,/g, ';')}\nURL:${p.social || ''}\nNOTE:${p.notes || ''}\nEND:VCARD`;
        }

        window.exportVCF = function(pId) {
            const p = savedPartners.find(x => x.id === pId);
            if(!p) return;
            const blob = new Blob([buildVCard(p)], {type: 'text/vcard'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${p.name.replace(/\s+/g, '_')}.vcf`;
            a.click();
            URL.revokeObjectURL(url);
        };

        // --- MASS IMPORT / EXPORT (CSV) ---
        // A minimal RFC4180-ish parser: quoted fields, embedded commas/newlines, and "" as an
        // escaped quote. Good enough for what Excel/Sheets/Numbers actually produce.
        // parseCSV, csvField now live in lib/pure.js (loaded before this script) - pulled out so
        // they're directly unit-testable. See tests/pure.spec.js.

        function downloadCSV(filename, headerRow, dataRows) {
            const lines = [headerRow, ...dataRows].map(row => row.map(csvField).join(','));
            const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        }

        // PARTNER_CSV_HEADER now lives in lib/pure.js too, shared with the same constant there.

        window.exportPartnersCSV = function() {
            if (!savedPartners.length) { alert('No colleagues to export yet.'); return; }
            const rows = savedPartners.map(p => [
                p.name, p.brokerage || '', p.baseName || '', p.base2Name || '', p.phone || '',
                p.email || '', p.social || '', p.notes || '', (p.groups || []).join(', '), p.radius || 45
            ]);
            downloadCSV(`colleagues_${new Date().toISOString().slice(0, 10)}.csv`, PARTNER_CSV_HEADER, rows);
        };

        // A blank starter file with the exact headers importPartnersCSV looks for, plus one
        // filled example row so the expected format is obvious without guesswork. Pairs with
        // Export CSV above: the template covers "I'm starting from scratch," the export covers
        // "I already have colleagues saved and want to add more offline."
        window.downloadPartnersTemplate = function() {
            const example = ['Jane Example', 'RE/MAX Premier', '123 Main St, Tampa, FL', '', '813-555-0100', 'jane@example.com', '@jane.realtor', 'Great with first-time buyers', 'Luxury, Referral Partner', 45];
            downloadCSV('colleague_import_template.csv', PARTNER_CSV_HEADER, [example]);
        };

        window.exportReferralsCSV = function() {
            if (!savedReferrals.length) { alert('No referrals to export yet.'); return; }
            const header = ['Client Name', 'Client Contact', 'Sent To', 'Location', 'Status', 'Est. Price', 'Commission %', 'Referral Fee %', 'Expected Fee', 'Notes', 'Sent Date', 'Last Updated'];
            const rows = savedReferrals.map(r => [
                r.clientName, r.clientContact || '', r.partnerName, r.locationText || '',
                (REFERRAL_STATUSES[r.status] || REFERRAL_STATUSES.sent).label,
                r.estPrice ?? '', r.commissionPct ?? '', r.feePct ?? '',
                referralExpectedFee(r) ? Math.round(referralExpectedFee(r)) : '',
                r.notes || '', r.sentAt ? new Date(r.sentAt).toISOString().slice(0, 10) : '',
                r.updatedAt ? new Date(r.updatedAt).toISOString().slice(0, 10) : ''
            ]);
            downloadCSV(`referrals_${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
        };

        // IMPORT_COLUMN_ALIASES, matchImportColumns now live in lib/pure.js.

        // Runs rows through addPartnerFromFields one at a time (not in parallel) - each row
        // burns one geocode lookup against a 120/hour rate limit, and serial keeps the progress
        // banner accurate instead of racing.
        window.importPartnersCSV = async function(file) {
            const text = await file.text();
            const rows = parseCSV(text).filter(r => r.some(c => c.trim() !== ''));
            if (rows.length < 2) {
                setShareBanner("⚠️ That file has no data rows to import.", 'amber');
                return;
            }

            const columns = matchImportColumns(rows[0]);
            if (columns.name === undefined || columns.address === undefined) {
                setShareBanner('⚠️ Couldn\'t find a "Name" and "Address" column in that file. Use the "Need the template?" link above for a starter file with the right headers.', 'amber');
                return;
            }

            const dataRows = rows.slice(1);
            if (dataRows.length > 100 && !confirm(`This file has ${dataRows.length} rows. Each one needs an address lookup, so a file this size may take a few minutes and could hit our address-lookup limit - if it does, the import stops cleanly and hands you a file to finish the rest later.\n\nSeparately, each colleague's drive-time coverage shape loads afterward in the background, a few at a time, and can keep filling in for a while after the import itself finishes - a colleague showing up as a plain pin with no colored area yet is normal for a file this size, not an error. Continue?`)) {
                return;
            }

            revealSidebar();
            const get = (row, key) => columns[key] !== undefined ? (row[columns[key]] || '').trim() : '';
            let imported = 0, warned = 0, skipped = 0, duplicateSkipped = 0, rateLimitedAt = -1;
            const skipReasons = [];

            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                const name = get(row, 'name');
                setShareBanner(`📇 Importing ${i + 1}/${dataRows.length}${name ? ': ' + name : ''}...`, 'blue');

                // Same by-name match importFromShareLink() already uses for a single share-link
                // add. Checked against savedPartners, which grows as the loop goes - so two rows
                // for the same person in one file catch each other too, not just existing
                // colleagues. Skips rather than overwriting: silently replacing a hand-curated
                // card's phone/notes/tags mid-bulk-import is a worse surprise than a duplicate -
                // use Merge (bulk-select two colleagues) afterward to combine them on purpose.
                if (isDuplicatePartnerName(name, savedPartners.map(p => p.name))) {
                    duplicateSkipped++;
                    continue;
                }

                const groupsRaw = get(row, 'groups');
                const radiusRaw = parseInt(get(row, 'radius'), 10);
                // Tagged "Imported" on arrival (deduped against whatever the CSV's own Groups
                // column already carried) so a bulk-imported colleague can be found later via the
                // same Filter Tags UI as any other tag, without a separate origin-tracking system.
                const csvTags = groupsRaw ? groupsRaw.split(/[,;]/).map(t => t.trim()).filter(Boolean) : [];
                const result = await addPartnerFromFields({
                    name,
                    brokerage: get(row, 'brokerage'),
                    addressOrCoord: get(row, 'address'),
                    address2: get(row, 'address2'),
                    phone: get(row, 'phone'),
                    email: get(row, 'email'),
                    social: get(row, 'social'),
                    notes: get(row, 'notes'),
                    tags: [...new Set([...csvTags, 'Imported'])],
                    radius: Number.isFinite(radiusRaw) && radiusRaw > 0 ? radiusRaw : 45,
                    silent: true
                });

                // Stop outright rather than plowing through the rest of the file - every
                // remaining row would fail the exact same way and land on the map's last known
                // center, which is how a big import used to end with dozens of colleagues dumped
                // in the same spot in open water. Save what's left as a ready-to-reimport CSV
                // instead of making the owner figure out which rows never got a turn.
                if (result && result.error === 'RATE_LIMITED') {
                    rateLimitedAt = i;
                    downloadCSV(`colleagues_remaining_${new Date().toISOString().slice(0, 10)}.csv`, rows[0], dataRows.slice(i));
                    break;
                }

                if (!result || result.error) {
                    skipped++;
                    skipReasons.push(`Row ${i + 2}${name ? ' (' + name + ')' : ''}: ${(result && result.error) || 'missing name or address'}`);
                } else if (result._importWarning) {
                    warned++;
                } else {
                    imported++;
                }
            }

            applyFilters();
            const total = imported + warned;

            if (rateLimitedAt !== -1) {
                const remaining = dataRows.length - rateLimitedAt;
                let msg = `⛔ Stopped after ${total} colleague${total === 1 ? '' : 's'} - hit our address-lookup limit (120/hour).`;
                if (duplicateSkipped) msg += ` (${duplicateSkipped} already in your network were skipped along the way.)`;
                msg += ` Downloaded a CSV of the ${remaining} still to do — re-import that file in about an hour to finish.`;
                setShareBanner(msg, 'amber');
            } else {
                let summary = total ? `✅ Imported ${total} colleague${total === 1 ? '' : 's'}` : '⚠️ Nothing was imported';
                if (warned) summary += ` (${warned} with an approximate pin - worth checking)`;
                if (duplicateSkipped) summary += ` · ${duplicateSkipped} already in your network`;
                if (skipped) summary += ` · ${skipped} skipped`;
                // Drive-time shapes fetch in the background, a few at a time, well after this
                // banner fires - for a big batch that can take a while and even bump into the
                // map provider's own limit. Said here, once, so a blank-looking pin two minutes
                // from now reads as "still loading" instead of "this is broken."
                if (total >= 15) summary += '. Drive-time shapes load in next, a few at a time — give it a bit for colored coverage areas to fill in.';
                setShareBanner(summary, total ? (skipped ? 'amber' : 'emerald') : 'amber');
            }
            if (skipReasons.length) console.warn('CSV import skipped rows:\n' + skipReasons.join('\n'));
        };

        document.getElementById('partnerImportInput').addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) window.importPartnersCSV(file);
            e.target.value = '';  // allow re-selecting the same filename on a later attempt
        });

        // One badge, two meanings, because `source` and `linkedUserId` answer different questions.
        //
        // `source` is how the row first appeared: 'reciprocated' means the colleague created it
        // themselves off a share link, which is worth flagging because it arrived without the
        // owner typing anything. It describes the row's origin and so can never change afterwards.
        //
        // `linkedUserId` is whether the person is a real account that has linked back. Someone
        // added by hand who later reciprocates stays source='manual' forever - correctly, they
        // didn't create the row - so keying the badge off `source` alone meant the app knew a
        // colleague was a connected user and said so nowhere. That's the Jay Quigley case: typed
        // in by hand, reciprocated weeks later, no badge.
        //
        // Kept to a single badge slot rather than two so a card doesn't sprout another chip.
        function partnerLinkBadge(partner) {
            const chip = 'bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 border border-indigo-500/20 text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase tracking-wider';
            if (partner.source === 'reciprocated') {
                return `<span class="${chip}" title="They added themselves back after importing your share link">🔁 Added Themselves</span>`;
            }
            if (partner.linkedUserId) {
                return `<span class="${chip}" title="They're on MapMyReferrals and sent their own info back to you">🔁 Connected</span>`;
            }
            return '';
        }

        // Readable full-detail view for a colleague, opened from the (deliberately tiny) map
        // popup. Fields mirror buildPartnerPopup() - see that function for why each is included.
        let partnerDetailCurrentId = null;
        window.openPartnerDetailModal = function(pId) {
            const p = savedPartners.find(x => x.id === pId);
            if (!p) return;
            partnerDetailCurrentId = pId;

            const photo = document.getElementById('partnerDetailPhoto');
            photo.src = p.photoUrl || '';
            photo.classList.toggle('hidden', !p.photoUrl);

            document.getElementById('partnerDetailName').textContent = p.name;
            document.getElementById('partnerDetailBrokerage').textContent = p.brokerage || '';
            document.getElementById('partnerDetailBrokerage').classList.toggle('hidden', !p.brokerage);

            const tagsEl = document.getElementById('partnerDetailTags');
            const tags = p.groups || [];
            const linkBadge = partnerLinkBadge(p);
            tagsEl.innerHTML = linkBadge + tags.map(t => `<span class="bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20 text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase tracking-wider">${escapeHtml(t)}</span>`).join(' ');
            tagsEl.classList.toggle('hidden', tags.length === 0 && !linkBadge);

            document.getElementById('partnerDetailBase').textContent = p.baseName;
            const base2 = document.getElementById('partnerDetailBase2');
            base2.textContent = p.base2Name ? `🏢 ${p.base2Name}` : '';
            base2.classList.toggle('hidden', !p.base2Name);

            const phone = document.getElementById('partnerDetailPhone');
            phone.href = `tel:${p.phone || ''}`;
            phone.querySelector('span').textContent = p.phone || '';
            phone.classList.toggle('hidden', !p.phone);

            const email = document.getElementById('partnerDetailEmail');
            email.href = `mailto:${p.email || ''}`;
            email.querySelector('span').textContent = p.email || '';
            email.classList.toggle('hidden', !p.email);

            const social = document.getElementById('partnerDetailSocial');
            social.href = sanitizeUrl(p.social) || '#';
            social.classList.toggle('hidden', !p.social);

            const notes = document.getElementById('partnerDetailNotes');
            notes.textContent = p.notes ? `📝 ${p.notes}` : '';
            notes.classList.toggle('hidden', !p.notes);

            const modal = document.getElementById('partnerDetailModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
        };
        function closePartnerDetailModal() {
            const modal = document.getElementById('partnerDetailModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
        }
        document.getElementById('partnerDetailCloseBtn').addEventListener('click', closePartnerDetailModal);
        document.getElementById('partnerDetailModal').addEventListener('click', (e) => {
            if (e.target === document.getElementById('partnerDetailModal')) closePartnerDetailModal();
        });

        // Hands the colleague's contact info to a client as a plain vCard - deliberately NOT
        // sharePartnerCard()'s mapmyreferrals.com invite link, which is for onboarding another
        // agent into your network, not for giving a client someone to call.
        document.getElementById('partnerDetailShareBtn').addEventListener('click', async () => {
            const p = savedPartners.find(x => x.id === partnerDetailCurrentId);
            if (!p) return;

            const vcfFile = new File([buildVCard(p)], `${p.name.replace(/\s+/g, '_')}.vcf`, { type: 'text/vcard' });

            if (navigator.canShare && navigator.canShare({ files: [vcfFile] })) {
                try {
                    await navigator.share({ files: [vcfFile], title: p.name });
                    return;
                } catch (err) {
                    if (err.name === 'AbortError') return; // user cancelled the share sheet - not an error
                    // fall through to download on any other failure
                }
            }
            exportVCF(p.id);
        });

        // --- ACCOUNTS: resolve login state first, then decide whether profile/partner data
        // comes from this browser's localStorage or from the server. Logged-out behavior is
        // untouched below - it reads/writes localStorage exactly as it always has. ---
        const DATA_PERSISTENCE_KEY = 'referralNetworkV11_CRM';
        const REFERRALS_KEY = 'referralTrackerV1';
        const defaultUserProfile = {
            hubName: '',
            addressText: '',
            lat: 39.8283,  // geographic center of the contiguous US - neutral until they set a real hub
            lng: -98.5795,
            hub2AddressText: '',  // optional second base (office) - own pin + coverage shape
            hub2Lat: null,
            hub2Lng: null,
            tags: '',
            brokerage: '',
            phone: '',
            email: '',
            social: '',
            photoUrl: ''
        };

        async function apiPost(url, body) {
            return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' }, body: JSON.stringify(body) });
        }
        async function apiPut(url, body) {
            return fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' }, body: JSON.stringify(body) });
        }
        async function apiDelete(url) {
            return fetch(url, { method: 'DELETE', headers: { 'X-Requested-With': 'fetch' } });
        }

        let isLoggedIn = false;
        let userEmail = null;
        const localProfileRaw = localStorage.getItem('user_map_profile_v10');

        let userProfile = localProfileRaw ? JSON.parse(localProfileRaw) : { ...defaultUserProfile };
        let savedPartners = JSON.parse(localStorage.getItem(DATA_PERSISTENCE_KEY)) || [];
        // Bulk-select state for the Active Network Index - see toggleSelectMode().
        let selectModeActive = false;
        const selectedPartnerIds = new Set();
        let savedReferrals = JSON.parse(localStorage.getItem(REFERRALS_KEY)) || [];
        let hasSavedProfile = !!localProfileRaw;

        let userPicture = null;
        let userGoogleName = null;
        try {
            const meRes = await fetch('/api/auth/me');
            const me = await meRes.json();
            isLoggedIn = !!me.loggedIn;
            userEmail = me.email || null;
            userPicture = me.picture || null;
            userGoogleName = me.name || null;
        } catch (err) { isLoggedIn = false; }

        // Built-in browsers of apps where Google blocks OAuth (403 disallowed_useragent). "; wv)"
        // is Android's generic WebView marker - Chrome Custom Tabs don't carry it, and sign-in
        // works there, so it isn't flagged.
        const IN_APP_BROWSERS = [
            [/Instagram/i, 'Instagram'], [/FBAN|FBAV|FB_IAB|FBIOS/i, 'Facebook'], [/LinkedInApp/i, 'LinkedIn'],
            [/TikTok|musical_ly|BytedanceWebview/i, 'TikTok'], [/Snapchat/i, 'Snapchat'], [/\bLine\//i, 'LINE'],
            [/Twitter/i, 'X'], [/; wv\)/i, 'this app']
        ];

        // Street numbers off, city kept: "6446 Silver Oaks Dr, zephyrhills" -> "Zephyrhills". The
        // card shows where someone works, not where they live.
        function areaFromAddress(address) {
            const parts = (address || '').split(',').map(s => s.trim()).filter(Boolean);
            if (parts.length > 1 && /^\d/.test(parts[0])) parts.shift();
            const city = parts[0] || '';
            if (!city || /^\d/.test(city) || /^[A-Z]{2}(\s+\d{5})?$/.test(city)) return '';
            return city === city.toLowerCase() ? city.replace(/\b\w/g, c => c.toUpperCase()) : city;
        }

        function formatPhoneForDisplay(digits) {
            const d = digits.replace(/^\+?1(?=\d{10}$)/, '');
            return /^\d{10}$/.test(d) ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : digits;
        }

        function showShareLanding(params) {
            const fullName = (params.get('name') || '').trim();
            // "Adam Grenville, REALTOR" - name and designation shown separately (same split as
            // functions/api/vcard.js, which files the contact the same way).
            const [displayName, ...titleParts] = fullName.split(',');
            const display = displayName.trim() || fullName;
            const title = titleParts.join(',').trim();
            const first = display.split(/\s+/)[0] || display;
            const brokerage = (params.get('brokerage') || '').trim();
            const phone = (params.get('phone') || '').replace(/[^\d+]/g, '');
            const email = (params.get('email') || '').trim();
            const social = (params.get('social') || '').trim();
            const shareId = params.get('shareId');
            const el = (id) => document.getElementById(id);

            document.title = `${display} · MapMyReferrals`;
            el('slName').textContent = display;
            if (title) { el('slTitle').textContent = title; el('slTitle').classList.remove('hidden'); }
            el('slBrokerage').textContent = brokerage;

            // Our own uploads only, same rule as the link-preview card.
            const photo = params.get('photo') || '';
            if (/^\/photos\/[A-Za-z0-9_-]{1,100}\.(jpe?g|png|webp)$/i.test(photo)) {
                el('slPhoto').src = photo;
                el('slPhoto').alt = display;
                el('slPhoto').onerror = () => el('slPhotoWrap').classList.add('hidden');
                el('slPhotoWrap').classList.remove('hidden');
            }

            const minutes = parseInt(params.get('radius') || '', 10);
            const range = minutes > 0 ? (minutes % 60 === 0 ? `${minutes / 60}-hour` : `${minutes}-minute`) : '';
            const areas = [...new Set([areaFromAddress(params.get('address')), areaFromAddress(params.get('address2'))].filter(Boolean))];
            if (range || areas.length) {
                el('slCoverage').textContent = `🚗 ${range ? `${range} drive-time coverage` : 'Drive-time coverage'}${areas.length ? ` · ${areas.join(' & ')}` : ''}`;
                el('slCoverage').classList.remove('hidden');
            }

            const tags = (params.get('tags') || '').split(',').map(t => t.trim()).filter(t => t && t !== 'Imported').slice(0, 6);
            if (tags.length) {
                tags.forEach(tag => {
                    const chip = document.createElement('span');
                    chip.className = 'rounded-full bg-white/5 border border-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-slate-300';
                    chip.textContent = tag;
                    el('slTags').appendChild(chip);
                });
                el('slTags').classList.remove('hidden');
            }

            if (phone) {
                el('slCall').href = `tel:${phone}`;
                el('slCall').title = formatPhoneForDisplay(phone);
                el('slText').href = `sms:${phone}`;
                el('slCall').classList.remove('hidden');
                el('slText').classList.remove('hidden');
            }
            if (/^[^\s@]+@[^\s@]+$/.test(email)) {
                el('slEmail').href = `mailto:${email}`;
                el('slEmail').classList.remove('hidden');
            }
            el('slSave').href = '/api/vcard' + window.location.search;
            // The button row is a 4-column grid; with fewer contact methods, close the gaps.
            const shown = ['slCall', 'slText', 'slEmail', 'slSave'].filter(id => !el(id).classList.contains('hidden')).length;
            el('slSave').parentElement.style.gridTemplateColumns = `repeat(${shown}, minmax(0, 1fr))`;

            if (/^https?:\/\//i.test(social)) {
                let label = 'Website';
                try {
                    const host = new URL(social).hostname.replace(/^www\./, '');
                    label = { 'facebook.com': 'Facebook', 'instagram.com': 'Instagram', 'linkedin.com': 'LinkedIn', 'youtube.com': 'YouTube', 'tiktok.com': 'TikTok' }[host] || host;
                } catch (err) { /* keep "Website" */ }
                el('slSocial').href = social;
                el('slSocial').textContent = `${label} →`;
                el('slSocial').classList.remove('hidden');
            }

            el('slAddBtn').textContent = `Add ${first} to my referral map`;
            el('slAddBtn').href = '/api/auth/google/start?returnTo=' + encodeURIComponent(window.location.pathname + window.location.search);
            // Only a personal link (shareId) can take the visitor's info back - a forwarded
            // colleague card can't, so don't promise it.
            el('slPitch').textContent = shareId
                ? `See exactly where ${first} covers on a real drive-time map, so you know when to send a lead, then send your info back so ${first} can refer clients to you.`
                : `See exactly where ${first} covers on a real drive-time map, so you know when to send a lead.`;

            const inApp = IN_APP_BROWSERS.find(([pattern]) => pattern.test(navigator.userAgent));
            if (inApp) {
                const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                el('slAddBtn').classList.add('hidden');
                el('slAddHint').classList.add('hidden');
                el('slInAppText').textContent = `Google doesn't allow sign-in inside ${inApp[1]}'s built-in browser. Tap the ${isIOS ? '•••' : '⋮'} menu and choose "Open in ${isIOS ? 'Safari' : 'browser'}" (or copy the link below into ${isIOS ? 'Safari' : 'Chrome'}) to add ${first} to your map. Call, text and save contact work right here.`;
                el('slInApp').classList.remove('hidden');
                const shareLink = shareId ? `${window.location.origin}/s/${shareId}` : window.location.href;
                el('slCopyLink').addEventListener('click', () => {
                    navigator.clipboard.writeText(shareLink)
                        .then(() => { el('slCopyLink').textContent = '✅ Copied - paste it into your browser'; })
                        .catch(() => { prompt('Copy this link:', shareLink); });
                });
            }

            // The un-booted app is still underneath - keep keyboard focus and screen readers off it.
            document.querySelectorAll('body > header, body > main').forEach(node => node.setAttribute('inert', ''));
            el('shareLanding').classList.remove('hidden');
        }

        // --- SHARE LINK LOGGED-OUT LANDING: a share link only pays off long-term if the
        // colleague it adds is saved to a real account, not just this one browser's
        // localStorage - so the import still only happens after Google sign-in. The OAuth
        // "state" round-trip carries this exact URL (path + share params) as "returnTo", and the
        // callback lands them right back here, now logged in, where importFromShareLink() below
        // runs and completes normally.
        //
        // This used to redirect straight to Google. At a conference that meant a scanned QR
        // opened on a bare sign-in page with no sign of whose link it was - and inside LinkedIn's
        // or Instagram's own browser, Google refuses sign-in outright (403 disallowed_useragent),
        // a dead end. Now the visitor sees the agent's card first: photo, coverage, call/text/
        // email/save-contact (all of which work with no account), and sign-in is the one button
        // that adds them to a map. The rest of the app never boots for this visit. ---
        if (!isLoggedIn && new URLSearchParams(window.location.search).has('name')) {
            showShareLanding(new URLSearchParams(window.location.search));
            return;
        }

        // Shown to anyone who hasn't set a hub yet, then never again once they have.
        //
        // This used to key off being logged OUT, which meant it vanished the instant someone
        // signed in - so a brand-new account staring at a blank map, the exact person it's for,
        // never saw it. Signing in isn't the same as understanding the app. Keying on "no hub
        // yet" targets the drop-off instead: the largest single gap in the funnel is accounts
        // created but never given a base address.
        //
        // Deferred until after the profile loads below, since hasSavedProfile isn't known yet.
        function updateNewHereBanner() {
            const banner = document.getElementById('howItWorksBanner');
            const show = !hasSavedProfile && localStorage.getItem('how_it_works_banner_dismissed') !== 'true';
            banner.classList.toggle('hidden', !show);
            banner.classList.toggle('flex', show);
        }
        window.dismissHowItWorksBanner = function() {
            localStorage.setItem('how_it_works_banner_dismissed', 'true');
            document.getElementById('howItWorksBanner').classList.add('hidden');
            document.getElementById('howItWorksBanner').classList.remove('flex');
        };

        // iPadOS 13+ dropped a distinct UA string and reports as a Mac - maxTouchPoints is the
        // standard way to tell it apart from an actual Mac, which reports 0.
        function isIOS() {
            return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        }
        function isAndroid() {
            return /Android/i.test(navigator.userAgent);
        }
        function isStandaloneDisplay() {
            return window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
        }
        // Only iOS lacks a native "install this" prompt (Chrome/Android already nag on their
        // own), so this is the one platform worth an in-app nudge - and only worth it once
        // someone has an actual reason to come back, not on a first visit that's already
        // competing with the how-it-works banner above. See importFromShareLink().
        function maybeShowAddHomeScreenBanner() {
            if (!isIOS() || isStandaloneDisplay()) return;
            if (localStorage.getItem('add_home_screen_banner_dismissed') === 'true') return;
            const banner = document.getElementById('addHomeScreenBanner');
            banner.classList.remove('hidden');
            banner.classList.add('flex');
        }
        window.dismissAddHomeScreenBanner = function() {
            localStorage.setItem('add_home_screen_banner_dismissed', 'true');
            document.getElementById('addHomeScreenBanner').classList.add('hidden');
            document.getElementById('addHomeScreenBanner').classList.remove('flex');
        };

        if (isLoggedIn) {
            const [serverProfile, serverPartners, serverReferrals] = await Promise.all([
                fetch('/api/profile').then(r => r.ok ? r.json() : null).catch(() => null),
                fetch('/api/partners').then(r => r.ok ? r.json() : []).catch(() => []),
                fetch('/api/referrals').then(r => r.ok ? r.json() : []).catch(() => [])
            ]);
            const alreadyMigrated = localStorage.getItem('migrated_v1') === 'true';

            if (!serverProfile && !alreadyMigrated && (localProfileRaw || savedPartners.length > 0)) {
                // First login on this browser, with existing local data and nothing on the
                // server yet - seed the account once, silently, then never do this again.
                let migrationOk = true;
                if (localProfileRaw) {
                    const res = await apiPut('/api/profile', userProfile).catch(() => null);
                    if (!res || !res.ok) migrationOk = false;
                }
                const migratedPartners = [];
                for (const p of savedPartners) {
                    try {
                        const res = await apiPost('/api/partners', p);
                        if (res.ok) {
                            const created = await res.json();
                            migratedPartners.push({ ...p, id: created.id });
                        } else {
                            migrationOk = false;
                            migratedPartners.push(p);
                        }
                    } catch (err) {
                        migrationOk = false;
                        migratedPartners.push(p);
                    }
                }
                savedPartners = migratedPartners;
                if (migrationOk) localStorage.setItem('migrated_v1', 'true');
            } else {
                // Server already has data (or this browser already migrated) - server wins,
                // no merge. localStorage stops being read from this point on for this account.
                userProfile = serverProfile || { ...defaultUserProfile };
                savedPartners = serverPartners || [];
                localStorage.setItem('migrated_v1', 'true');
            }
            // Referrals get their own one-time migration flag (not migrated_v1) because the
            // tracker shipped later - existing accounts already have migrated_v1 set, and any
            // referrals they logged while signed out still need a first seed of their own.
            const referralsMigrated = localStorage.getItem('migrated_referrals_v1') === 'true';
            if (serverReferrals.length === 0 && !referralsMigrated && savedReferrals.length > 0) {
                let refMigrationOk = true;
                const migratedReferrals = [];
                for (const r of savedReferrals) {
                    try {
                        const res = await apiPost('/api/referrals', r);
                        if (res.ok) {
                            const created = await res.json();
                            migratedReferrals.push({ ...r, id: created.id });
                        } else {
                            refMigrationOk = false;
                            migratedReferrals.push(r);
                        }
                    } catch (err) {
                        refMigrationOk = false;
                        migratedReferrals.push(r);
                    }
                }
                savedReferrals = migratedReferrals;
                if (refMigrationOk) localStorage.setItem('migrated_referrals_v1', 'true');
            } else {
                savedReferrals = serverReferrals || [];
                localStorage.setItem('migrated_referrals_v1', 'true');
            }

            hasSavedProfile = !!userProfile.hubName;
        }

        updateNewHereBanner();
        updateSignInNudge();

        // Default avatar: anyone signed in with Google already has a profile photo - borrow it
        // unless they've uploaded their own. Only synced to the server once a real profile row
        // exists (creating a placeholder row here would trip the first-login migration's
        // "server already has data" check on their other devices).
        if (isLoggedIn && userPicture && !userProfile.photoUrl) {
            userProfile.photoUrl = userPicture;
            if (userProfile.hubName) {
                persistProfile();
            } else {
                localStorage.setItem('user_map_profile_v10', JSON.stringify(userProfile));
            }
        }

        function persistProfile() {
            localStorage.setItem('user_map_profile_v10', JSON.stringify(userProfile));
            if (isLoggedIn) apiPut('/api/profile', userProfile).catch(err => console.error('Profile sync failed', err));
        }
        function persistPartnersLocal() {
            localStorage.setItem(DATA_PERSISTENCE_KEY, JSON.stringify(savedPartners));
            updateSignInNudge();
        }

        // Signed out, everything lives in this one browser's localStorage - clear site data or
        // pick up your phone and it's gone. Nothing used to say so. Shown once there's something
        // worth losing; signing in seeds the account from it (the first-login migration above).
        // Dismissing only lasts the session, since the risk doesn't go away.
        function updateSignInNudge() {
            const nudge = document.getElementById('signInNudge');
            let dismissed = false;
            try { dismissed = sessionStorage.getItem('sign_in_nudge_dismissed') === 'true'; } catch (err) { /* blocked storage - just show it */ }
            const show = !isLoggedIn && !dismissed && (hasSavedProfile || savedPartners.length > 0);
            nudge.classList.toggle('hidden', !show);
            nudge.classList.toggle('flex', show);
        }
        window.dismissSignInNudge = function() {
            try { sessionStorage.setItem('sign_in_nudge_dismissed', 'true'); } catch (err) { /* blocked storage */ }
            updateSignInNudge();
        };
        window.startGoogleSignIn = function() {
            window.location.href = '/api/auth/google/start?returnTo=' + encodeURIComponent(window.location.pathname);
        };
        function syncPartnerUpdate(partner) {
            if (!isLoggedIn) return;
            apiPut(`/api/partners/${partner.id}`, partner).catch(err => console.error('Partner sync failed', err));
        }
        function syncPartnerDelete(id) {
            if (!isLoggedIn) return;
            apiDelete(`/api/partners/${id}`).catch(err => console.error('Partner delete sync failed', err));
        }
        function persistReferralsLocal() {
            localStorage.setItem(REFERRALS_KEY, JSON.stringify(savedReferrals));
        }
        function syncReferralUpdate(referral) {
            if (!isLoggedIn) return;
            apiPut(`/api/referrals/${referral.id}`, referral).catch(err => console.error('Referral sync failed', err));
        }
        function syncReferralDelete(id) {
            if (!isLoggedIn) return;
            apiDelete(`/api/referrals/${id}`).catch(err => console.error('Referral delete sync failed', err));
        }

        const isMobileViewport = window.innerWidth < 768;
        const initialZoom = hasSavedProfile ? (isMobileViewport ? 7 : 9.5) : 4;
        let map = L.map('map', { zoomControl: false, attributionControl: false }).setView([userProfile.lat, userProfile.lng], initialZoom);
        L.control.zoom({ position: 'topright' }).addTo(map);

        // Mobile browsers often report the wrong container size at the exact moment Leaflet
        // initializes (dynamic viewport height as the address bar collapses/expands), which
        // makes the map look incorrectly zoomed out until a resize/orientation event fires.
        window.addEventListener('load', () => map.invalidateSize());
        window.addEventListener('resize', () => map.invalidateSize());
        window.addEventListener('orientationchange', () => setTimeout(() => map.invalidateSize(), 200));

        // Voyager (light mode) carries far more road/label detail than the flat "light_all"
        // style did, which was the core of the "hard to read" complaint. Dark mode keeps
        // CARTO's dark tileset (there's no dark Voyager equivalent) but gets a CSS contrast
        // boost below so roads and labels stand out more against the near-black background.
        // CARTO now requires a (free) API key on every raster tile request - anonymous requests
        // still return 200 OK, but the tile image itself is replaced with an "API KEY REQUIRED"
        // watermark. Key is meant to be public/client-side (CARTO's own docs embed it straight in
        // the Leaflet tile URL like this); it's scoped to this domain, not a backend secret.
        const CARTO_API_KEY = 'cb1_2x9k_1_110a2ab26b6f6d728a041647';
        const mapStyles = {
            dark: `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`,
            light: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`
        };

        let baseTileLayer = L.tileLayer(mapStyles.dark, { attribution: '&copy; CARTO &copy; OpenStreetMap' }).addTo(map);

        // --- STATE / COUNTY OUTLINE GEOJSON: self-hosted, not fetched from
        // raw.githubusercontent.com - that CDN rate-limits (429s) under real traffic since every
        // visitor's browser hit it directly. Source: US Census 2010 cartographic boundary files
        // (20m/generalized resolution), split per state and stripped of unused properties - see
        // the split script this was generated with if the source ever needs to be regenerated.
        //
        // State outlines are nationwide and load unconditionally, same as the old FL-only
        // overlay did - ~1MB, loaded once per session and then browser-cached. County outlines
        // are ~40-60KB each but there are 51 of them, so only the one matching the user's own
        // home base state is ever fetched - nobody pays for the other 50 states' county data.
        fetch('/data/us-states.json')
            .then(res => res.json())
            .then(data => {
                L.geoJSON(data, {
                    style: { color: '#9ca3af', weight: 2, fillOpacity: 0, dashArray: '' }
                }).addTo(map);
            })
            .catch(err => console.error("Error loading US state outlines GeoJSON", err));

        // Tracks the currently-drawn county overlay so switching states (moving, or correcting a
        // typo'd address) replaces it instead of layering a second state's counties on top.
        let countyOutlineLayer = null;
        function loadCountyOutlinesForState(stateAbbr) {
            if (countyOutlineLayer) { map.removeLayer(countyOutlineLayer); countyOutlineLayer = null; }
            if (!stateAbbr) return;
            fetch(`/data/counties/${stateAbbr.toLowerCase()}.json`)
                .then(res => { if (!res.ok) throw new Error(`No county file for ${stateAbbr}`); return res.json(); })
                .then(data => {
                    countyOutlineLayer = L.geoJSON(data, {
                        style: { color: '#6b7280', weight: 1.5, dashArray: '4, 4', fillOpacity: 0.02 }
                    }).addTo(map);
                })
                .catch(err => console.error("Error loading county outlines GeoJSON", err));
        }

        // Determines the state from the home base's own coordinates (reverse-geocoded, since the
        // free-text address the user typed isn't reliably parseable) and (re)draws that state's
        // counties. Safe to call anytime the home base moves - a no-op if there's no real hub yet
        // (checking hasSavedProfile, not just lat/lng - the default profile centers on Kansas as a
        // neutral placeholder, which is a real lat/lng but not a real home base to key off of).
        async function refreshCountyOutlinesForHomeBase() {
            if (!hasSavedProfile) return;
            try {
                const result = await googleReverseGeocode(userProfile.lat, userProfile.lng);
                if (result && result.state) loadCountyOutlinesForState(result.state);
            } catch (err) { console.error('Could not resolve home base state for county outlines', err); }
        }
        refreshCountyOutlinesForHomeBase();

        // --- THEME SELECTOR ---
        const themeSelector = document.getElementById('themeSelector');
        function applyThemeStyle(theme) {
            const body = document.body;
            const header = document.getElementById('appHeader');
            let chosenStyle = theme === 'system' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme;

            // z-[2000], not z-50: this rewrites the header's class list wholesale, and the z-50 it
            // used to set silently contradicted the z-[2000] in the markup. That was harmless while
            // the header held nothing but flat chips, but z-50 traps the account menu in a stacking
            // context below the sidebar's z-[1000], so the dropdown would open behind it.
            if (chosenStyle === 'dark') {
                body.className = "bg-gray-950 font-sans text-gray-100 overflow-hidden antialiased theme-dark";
                header.className = "bg-gray-900 border-b border-gray-800/60 p-4 flex flex-wrap items-center justify-between shadow-xl relative z-[2000] text-white";
                baseTileLayer.setUrl(mapStyles.dark);
            } else {
                body.className = "bg-slate-50 font-sans text-gray-900 overflow-hidden antialiased theme-light";
                header.className = "bg-white border-b border-gray-200 p-4 flex flex-wrap items-center justify-between shadow-md relative z-[2000] text-slate-800";
                baseTileLayer.setUrl(mapStyles.light);
            }
            localStorage.setItem('map_theme_preference', theme);
        }
        const savedThemePref = localStorage.getItem('map_theme_preference') || 'dark';
        themeSelector.value = savedThemePref;
        applyThemeStyle(savedThemePref);
        themeSelector.addEventListener('change', (e) => applyThemeStyle(e.target.value));

        // --- TEXT SIZE ---
        // Writes one data attribute on <body>. Every size in the stylesheet is calc(base *
        // --ui-scale), so this single write resizes the sidebar, the Leaflet popups and every map
        // label at once - including labels rendered after the fact, which is exactly why it's a
        // CSS variable rather than a class stamped onto each element at render time.
        const TEXT_SIZE_KEY = 'map_text_size_preference';
        const TEXT_SIZES = ['normal', 'large', 'xlarge'];
        function applyTextSize(size) {
            const chosen = TEXT_SIZES.includes(size) ? size : 'normal';
            document.body.setAttribute('data-text-size', chosen);
            localStorage.setItem(TEXT_SIZE_KEY, chosen);
            document.querySelectorAll('.text-size-btn').forEach(btn => {
                const isActive = btn.dataset.textSize === chosen;
                btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
                btn.classList.toggle('bg-blue-600', isActive);
                btn.classList.toggle('text-white', isActive);
                btn.classList.toggle('opacity-50', !isActive);
            });
        }
        applyTextSize(localStorage.getItem(TEXT_SIZE_KEY) || 'normal');
        document.querySelectorAll('.text-size-btn').forEach(btn => {
            btn.addEventListener('click', () => applyTextSize(btn.dataset.textSize));
        });

        // --- DYNAMIC SLIDER & DRAGGABLE USER HUB ---
        const driveSlider = document.getElementById('driveTimeSlider');
        const sliderDisplay = document.getElementById('sliderDisplayValue');
        
        let currentServicePolygon = null;
        let isHomeAreaVisible = true;
        let homeMarker = null;
        let homeCachedShapes = {};
        let drawRequestCounter = 0;

        // Second anchor (office). Coverage is the UNION of both drive-time shapes - somewhere
        // an hour from the office is in range even if it's 1.5 hours from home.
        let officeMarker = null;
        let officePolygon = null;
        let officeCachedShapes = {};

        // Both your own pins are locked by default (same reason as colleague pins: an
        // accidental drag while panning the map used to silently move your home/office base).
        // Reposition is a one-shot unlock via the marker's own popup, not a standing state.
        const hubRepositionState = { home: false, office: false };
        window.setHubReposition = function(which, enabled) {
            const marker = which === 'home' ? homeMarker : officeMarker;
            if (!marker) return;
            hubRepositionState[which] = enabled;
            if (enabled) marker.dragging.enable(); else marker.dragging.disable();

            const el = marker.getElement();
            const iconEl = el && el.querySelector('.hub-icon');
            if (iconEl) iconEl.classList.toggle('reposition-active', enabled);

            if (which === 'home') updateHomePopup(); else renderOfficePopup();
            marker.openPopup();
        }

        document.getElementById('userHubName').value = userProfile.hubName;
        document.getElementById('userHubAddress').value = userProfile.addressText;
        document.getElementById('userHub2Address').value = userProfile.hub2AddressText || '';
        if (userProfile.hub2Lat != null) document.getElementById('clearUserHub2Btn').classList.remove('hidden');
        document.getElementById('userHubTags').value = userProfile.tags || '';
        document.getElementById('userHubBrokerage').value = userProfile.brokerage || '';
        document.getElementById('userHubPhone').value = userProfile.phone || '';
        document.getElementById('userHubEmail').value = userProfile.email || '';
        document.getElementById('userHubSocial').value = userProfile.social || '';
        if (!hasSavedProfile) document.getElementById('firstVisitPrompt').classList.remove('hidden');

        // A brand-new account already told Google who they are - don't make them type it again.
        // Only fills empty fields, and only into the form: nothing is saved until Save Profile.
        if (isLoggedIn && !hasSavedProfile) {
            const nameInput = document.getElementById('userHubName');
            const emailInput = document.getElementById('userHubEmail');
            if (!nameInput.value && userGoogleName) nameInput.value = userGoogleName;
            if (!emailInput.value && userEmail) emailInput.value = userEmail;
        }

        // On mobile the whole Profile card - including the welcome prompt right above - lives
        // inside the sidebar drawer, which stays closed until the hamburger is tapped. Nothing
        // ever opened it for a brand-new visitor, so the entire onboarding form was invisible
        // behind an icon with no hint it held anything important - the root of "signed in, now
        // what?" on phones. Only fires for someone who hasn't set a hub yet, so a returning user
        // doesn't get the drawer forced open on every visit.
        if (!hasSavedProfile && window.innerWidth < 768) revealSidebar();

        function updatePhotoPreview(url) {
            const preview = document.getElementById('userHubPhotoPreview');
            if (url) {
                preview.src = url;
                preview.classList.remove('hidden');
            } else {
                preview.classList.add('hidden');
                preview.removeAttribute('src');
            }
        }
        updatePhotoPreview(userProfile.photoUrl);

        // The one-line view of Your Profile shown while the card is collapsed.
        function updateProfileSummary() {
            document.getElementById('profileSummaryName').textContent = (userProfile.hubName || '').trim() || 'Your profile';
            const rangeSelect = document.getElementById('driveTimeSlider');
            const rangeText = rangeSelect.options[rangeSelect.selectedIndex].text.replace(/\s*\(estimate\)/, '');
            document.getElementById('profileSummaryMeta').textContent =
                [userProfile.brokerage, `${rangeText} range`].filter(Boolean).join(' · ');
            const photo = document.getElementById('profileSummaryPhoto');
            photo.classList.toggle('hidden', !userProfile.photoUrl);
            if (userProfile.photoUrl) photo.src = userProfile.photoUrl;
        }

        function renderUserHubNode() {
            if (homeMarker) map.removeLayer(homeMarker);
            hubRepositionState.home = false;

            const homeIcon = L.divIcon({
                html: `<div class="relative flex items-center justify-center" title="Home base - locked by default, unlock from the popup below to drag"><div class="absolute w-10 h-10 bg-blue-500/20 rounded-full animate-ping"></div><div class="hub-icon bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-2 rounded-full shadow-2xl border border-blue-400 flex items-center justify-center font-bold w-9 h-9 z-50 shadow-blue-500/30 hover:scale-110 transition-transform">🏠</div></div>`,
                className: '', iconSize: [36, 36], iconAnchor: [18, 18]
            });

            homeMarker = L.marker([userProfile.lat, userProfile.lng], { icon: homeIcon, draggable: false }).addTo(map);
            updateHomePopup();

            homeMarker.on('dragend', async function(e) {
                const pos = e.target.getLatLng();
                userProfile.lat = pos.lat;
                userProfile.lng = pos.lng;

                // Only auto-fill the address text if it's currently empty - never overwrite an
                // address you've deliberately typed (e.g. your real mailing address), even if it
                // doesn't match what reverse-geocoding thinks is at this exact pin location.
                if (!userProfile.addressText) {
                    try {
                        const result = await googleReverseGeocode(pos.lat, pos.lng);
                        if (result) {
                            userProfile.addressText = result.formattedAddress;
                            document.getElementById('userHubAddress').value = userProfile.addressText;
                        }
                    } catch(err) { console.error("Reverse geocode failed on hub drag."); }
                }

                persistProfile();
                homeCachedShapes = {};
                drawMyServiceArea(driveSlider.value);
                refreshCountyOutlinesForHomeBase(); // dragging can cross a state line

                // Self-resets to locked after every drag - reposition is a one-shot unlock, not
                // a standing state, so the next accidental grab while panning can't move it again.
                setHubReposition('home', false);
            });
        }

        function updateHomePopup() {
            if (!homeMarker) return;
            const label = isHomeAreaVisible ? "🔒 Hide Service Area" : "👁️ Show Service Area";
            const btnColor = isHomeAreaVisible ? "bg-red-600 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-500";
            const repoLabel = hubRepositionState.home ? "🔓 Lock Position" : "📍 Enable Reposition";
            const repoColor = hubRepositionState.home ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800";

            homeMarker.bindPopup(`
                <div class="p-1 text-center min-w-[150px]">
                    ${userProfile.photoUrl ? `<img src="${userProfile.photoUrl}" data-onerror="remove" class="w-12 h-12 rounded-full object-cover mx-auto mb-1.5 border border-gray-300" />` : ''}
                    <b class="text-gray-900 block mb-1 text-sm">${userProfile.hubName}</b>
                    <span class="text-gray-500 text-[11px] block mb-2">${userProfile.addressText}</span>
                    <button data-action="toggleHomeArea" class="text-white text-[10px] font-bold px-3 py-1 rounded-md ${btnColor} transition shadow active:scale-95">
                        ${label}
                    </button>
                    <button data-action="setHubReposition" data-arg0="home" data-arg1="${!hubRepositionState.home}" class="text-[10px] font-bold px-3 py-1 rounded-md ${repoColor} transition shadow active:scale-95 mt-1.5 block w-full">
                        ${repoLabel}
                    </button>
                    <button data-action="isolateOwnCoverage" class="text-[10px] font-bold px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 transition shadow active:scale-95 mt-1.5 block w-full" title="Hide every colleague so only your own coverage shows">
                        🔎 Isolate My Coverage
                    </button>
                    <div class="text-[9px] text-gray-400 mt-2 italic">Locked by default - unlock above to drag the pin</div>
                </div>
            `);
        }

        function renderOfficePopup() {
            if (!officeMarker) return;
            const repoLabel = hubRepositionState.office ? "🔓 Lock Position" : "📍 Enable Reposition";
            const repoColor = hubRepositionState.office ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800";

            officeMarker.bindPopup(`
                <div class="p-1 text-center min-w-[150px]">
                    <b class="text-gray-900 block mb-1 text-sm">🏢 Office / Second Base</b>
                    <span class="text-gray-500 text-[11px] block mb-2">${userProfile.hub2AddressText}</span>
                    <button data-action="setHubReposition" data-arg0="office" data-arg1="${!hubRepositionState.office}" class="text-[10px] font-bold px-3 py-1 rounded-md ${repoColor} transition shadow active:scale-95 block w-full">
                        ${repoLabel}
                    </button>
                    <div class="text-[9px] text-gray-400 mt-2 italic">Locked by default - unlock above to drag the pin</div>
                </div>
            `);
        }

        function renderOfficeNode() {
            if (officeMarker) map.removeLayer(officeMarker);
            hubRepositionState.office = false;
            if (userProfile.hub2Lat == null || userProfile.hub2Lng == null) { officeMarker = null; return; }

            const officeIcon = L.divIcon({
                html: `<div class="relative flex items-center justify-center" title="Office base - locked by default, unlock from the popup below to drag"><div class="hub-icon bg-gradient-to-br from-indigo-500 to-purple-600 text-white p-2 rounded-full shadow-2xl border border-indigo-400 flex items-center justify-center font-bold w-9 h-9 z-50 shadow-indigo-500/30 hover:scale-110 transition-transform">🏢</div></div>`,
                className: '', iconSize: [36, 36], iconAnchor: [18, 18]
            });

            officeMarker = L.marker([userProfile.hub2Lat, userProfile.hub2Lng], { icon: officeIcon, draggable: false }).addTo(map);
            renderOfficePopup();

            officeMarker.on('dragend', function(e) {
                const pos = e.target.getLatLng();
                userProfile.hub2Lat = pos.lat;
                userProfile.hub2Lng = pos.lng;
                persistProfile();
                officeCachedShapes = {};
                drawMyServiceArea(driveSlider.value);
                setHubReposition('office', false);
            });
        }

        // Now a fixed-stop <select>, not a free-drag range input - picking an option fires exactly
        // one `change` event, so the old debounce (a range input used to fire `input` on every drag
        // step, up to 22 proxy requests in five seconds, half of them 429ing) no longer has anything
        // to guard against. The dropdown's own selected option already shows the value ("1 hr"), so
        // there's nothing left for sliderDisplay to write on this path - it's reserved for the
        // unavailable-state ⚠ below.
        driveSlider.addEventListener('change', (e) => {
            drawMyServiceArea(e.target.value);
            updateProfileSummary();
        });

        // Reports a failed lookup on the range readout itself - where the user is already looking,
        // having just changed the range - rather than in a sidebar banner they may have collapsed.
        // Purely a class toggle: the ⚠ comes from CSS, so it can't stack up on repeated calls.
        // Two different failures, deliberately reported differently.
        //
        // Past 60 minutes the routing provider refuses the request outright, so no amount of
        // retrying will ever produce a measured shape - there, an estimated circle is better than
        // an empty map, provided it is unmistakably labelled as an estimate.
        //
        // At or below 60 a failure is transient (rate limit, network), and a real answer does
        // exist. Substituting a guess there would hide a temporary problem behind a permanent
        // lie, so nothing is drawn and the readout says to try again.
        //
        // MEASURABLE_MAX_MINUTES, isEstimateRange now live in lib/pure.js.

        function setRangeState(state) {   // 'ok' | 'estimated' | 'unavailable'
            const el = document.getElementById('sliderDisplayValue');
            if (!el) return;
            el.classList.toggle('range-estimated', state === 'estimated');
            el.classList.toggle('range-unavailable', state === 'unavailable');
            el.title =
                state === 'estimated'
                    ? 'Estimated area. Drive-time shapes are only available up to 60 minutes, so beyond that this is a straight-line estimate, not a measured drive time.'
                : state === 'unavailable'
                    ? "Couldn't get the drive-time shape just now, so nothing is drawn rather than guessing. Try again in a moment."
                : '';
        }

        // Draws the user's own coverage: the home isochrone plus, if a second base is set, the
        // office isochrone. One "My Range" slider governs both - the union of the two shapes is
        // the real referral territory.
        async function drawMyServiceArea(minutes, { fitToArea = false } = {}) {
            const requestId = ++drawRequestCounter;
            if (currentServicePolygon) { map.removeLayer(currentServicePolygon); currentServicePolygon = null; }
            if (officePolygon) { map.removeLayer(officePolygon); officePolygon = null; }
            if (!isHomeAreaVisible) return;

            // Home anchor (blue)
            let homeShape = homeCachedShapes[minutes];
            if (!homeShape) {
                homeShape = await fetchLiveIsochrone(userProfile.lat, userProfile.lng, parseInt(minutes));
                if (requestId !== drawRequestCounter) return;
                if (homeShape) homeCachedShapes[minutes] = homeShape;
            }
            if (homeShape) {
                currentServicePolygon = L.geoJSON(homeShape, {
                    style: { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.14, weight: 2 }
                }).addTo(map);
                setRangeState('ok');
            } else if (isEstimateRange(minutes)) {
                // Dotted and fainter than any measured shape in the app, so the two never read
                // as the same kind of thing at a glance.
                currentServicePolygon = L.circle([userProfile.lat, userProfile.lng], {
                    radius: convertDriveTimeToMeters(minutes), color: '#3b82f6', fillColor: '#3b82f6',
                    fillOpacity: 0.05, weight: 1.5, dashArray: '1, 6'
                }).addTo(map);
                setRangeState('estimated');
            } else {
                setRangeState('unavailable');
            }

            // Office anchor (indigo, dashed outline to tell the two shapes apart where they overlap)
            if (userProfile.hub2Lat != null && userProfile.hub2Lng != null) {
                let officeShape = officeCachedShapes[minutes];
                if (!officeShape) {
                    officeShape = await fetchLiveIsochrone(userProfile.hub2Lat, userProfile.hub2Lng, parseInt(minutes));
                    if (requestId !== drawRequestCounter) return;
                    if (officeShape) officeCachedShapes[minutes] = officeShape;
                }
                if (officeShape) {
                    officePolygon = L.geoJSON(officeShape, {
                        style: { color: '#6366f1', fillColor: '#6366f1', fillOpacity: 0.12, weight: 2, dashArray: '6, 4' }
                    }).addTo(map);
                } else if (isEstimateRange(minutes)) {
                    officePolygon = L.circle([userProfile.hub2Lat, userProfile.hub2Lng], {
                        radius: convertDriveTimeToMeters(minutes), color: '#6366f1', fillColor: '#6366f1',
                        fillOpacity: 0.05, weight: 1.5, dashArray: '1, 6'
                    }).addTo(map);
                    setRangeState('estimated');
                } else {
                    setRangeState('unavailable');
                }
            }

            // Saving a profile used to setView() at a fixed zoom, where an hour's drive is bigger
            // than the screen - a brand-new user's first look at their coverage was a faint blue
            // tint with no edges, until they happened to zoom out twice.
            // The open sidebar floats over the map's left edge, so fit into what's left beside it -
            // unless that's a sliver (a phone with the drawer open), where fitting would be pointless.
            if (fitToArea) {
                const bounds = L.latLngBounds([]);
                [currentServicePolygon, officePolygon].forEach(layer => { if (layer) bounds.extend(layer.getBounds()); });
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { paddingTopLeft: [sidebarMapCover() + 24, 24], paddingBottomRight: [24, 24] });
                }
            }
        }

        // How many px of the map's left edge the open sidebar floats over - 0 when it's closed,
        // or when it would leave only a sliver beside it (a phone with the drawer open), where
        // working around it is pointless. Read from the classes, not the live rect: the drawer
        // may be mid-slide.
        function sidebarMapCover() {
            const sidebarEl = document.getElementById('sidebar');
            const sidebarOpen = window.innerWidth < 768 ? sidebarEl.classList.contains('mobile-open') : !sidebarEl.classList.contains('sidebar-collapsed');
            const sidebarRight = sidebarOpen ? sidebarEl.offsetWidth : 0;
            return map.getSize().x - sidebarRight > 200 ? sidebarRight : 0;
        }

        // setView, but centered in the part of the map the sidebar isn't covering.
        function setViewBesideSidebar(latlng, zoom) {
            const shift = sidebarMapCover() / 2;
            const center = map.unproject(map.project(latlng, zoom).subtract([shift, 0]), zoom);
            map.setView(center, zoom);
        }

        // Every explicit save button gets the same brief, unambiguous confirmation - the whole
        // point of moving off silent autosave-on-blur was that a user should never have to
        // wonder whether something took effect.
        let flashSaveTimer = null;
        function flashSaveConfirmation(btn, restoreText) {
            clearTimeout(flashSaveTimer);
            btn.textContent = '✅ Saved!';
            flashSaveTimer = setTimeout(() => { btn.textContent = restoreText; }, 1600);
        }

        document.getElementById('saveUserHubBtn').addEventListener('click', async () => {
            const hubName = document.getElementById('userHubName').value.trim();
            const addressText = document.getElementById('userHubAddress').value.trim();
            if (!hubName || !addressText) return alert("Fill out both Profile fields.");

            try {
                const geo = await geocodeWithFallback(addressText);
                if (!geo) return alert("Could not find that location at all. Try a nearby city/state, then drag the pin to your exact address.");

                const isFirstSave = !hasSavedProfile;

                // Assigns onto the existing object rather than replacing it outright - a wholesale
                // `userProfile = {...}` here silently dropped hub2AddressText/hub2Lat/hub2Lng (this
                // handler never mentions them at all), so saving your own name/phone/tags wiped out
                // your office/second base on every single save, both in memory and then in the
                // database via the persistProfile() call right below. photoUrl already avoided this
                // by explicitly carrying its own value forward - hub2 needed the same treatment,
                // just via not touching it in the first place.
                userProfile.hubName = hubName;
                userProfile.addressText = addressText;  // always exactly what you typed - never auto-reformatted or replaced
                userProfile.lat = geo.lat;
                userProfile.lng = geo.lng;
                userProfile.tags = document.getElementById('userHubTags').value.trim();
                userProfile.brokerage = document.getElementById('userHubBrokerage').value.trim();
                userProfile.phone = document.getElementById('userHubPhone').value.trim();
                userProfile.email = document.getElementById('userHubEmail').value.trim();
                userProfile.social = document.getElementById('userHubSocial').value.trim();

                persistProfile();
                homeCachedShapes = {};
                hasSavedProfile = true;
                updateNewHereBanner();  // they've set a base - they're past "new here"
                updateSignInNudge();

                // First-ever save with no colleagues yet: swap the welcome message for a
                // "here's what's next" pointer instead of just hiding it - this is exactly the
                // gap that had people scrolling down and hitting "Build Boundary & Save" on an
                // empty Add Colleague form, thinking it was still part of their own profile.
                const firstVisitPrompt = document.getElementById('firstVisitPrompt');
                if (isFirstSave && savedPartners.length === 0) {
                    firstVisitPrompt.textContent = '✅ Profile saved! Next, use "Add Referral Colleague" below to save people you refer business to.';
                    firstVisitPrompt.classList.remove('hidden');
                    // It sits above the form, which the user has scrolled past to reach Save.
                    firstVisitPrompt.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    // On a phone the open drawer covers nearly the whole map - the payoff of
                    // saving (seeing your coverage) was hidden behind the form that produced it.
                    if (window.innerWidth < 768) showMobileNextStep();
                } else {
                    firstVisitPrompt.classList.add('hidden');
                    flashSaveConfirmation(document.getElementById('saveUserHubBtn'), '💾 Save Profile');
                }
                renderUserHubNode();
                updateProfileSummary();
                // Not animated: Leaflet drops any zoom requested mid-animation, so a fast (cached)
                // drive-time shape arriving during this one would silently lose its fitBounds.
                map.setView([userProfile.lat, userProfile.lng], window.innerWidth < 768 ? 7 : 10, { animate: false });
                drawMyServiceArea(driveSlider.value, { fitToArea: true });
                // geo.state comes straight from this same geocode call - no need to reverse-geocode
                // again just to find out what we already know.
                loadCountyOutlinesForState(geo.state);

                if (geo.usedFallback) {
                    alert(`Couldn't find that exact street, so your pin was placed near ${geo.fallbackLabel}. Drag the 🏠 pin to your exact location - your address text stays exactly as you typed it.`);
                }
            } catch(e) { alert("Profile setup network timeout."); }
        });

        document.getElementById('saveUserHub2Btn').addEventListener('click', async () => {
            const addr = document.getElementById('userHub2Address').value.trim();
            if (!addr) return alert("Enter your office / second base address first.");

            try {
                const geo = await geocodeWithFallback(addr);
                if (!geo) return alert("Couldn't find that location. Try a nearby city/state, then drag the 🏢 pin to the exact spot.");

                userProfile.hub2AddressText = addr;  // exactly as typed, same rule as the home address
                userProfile.hub2Lat = geo.lat;
                userProfile.hub2Lng = geo.lng;
                officeCachedShapes = {};
                persistProfile();
                renderOfficeNode();
                drawMyServiceArea(driveSlider.value);
                document.getElementById('clearUserHub2Btn').classList.remove('hidden');

                if (geo.usedFallback) {
                    alert(`Couldn't find that exact street, so the 🏢 pin was placed near ${geo.fallbackLabel}. Drag it to the exact location - the address text stays as typed.`);
                } else {
                    flashSaveConfirmation(document.getElementById('saveUserHub2Btn'), 'Set');
                }
            } catch(e) { alert("Network error setting your second base."); }
        });

        document.getElementById('clearUserHub2Btn').addEventListener('click', () => {
            userProfile.hub2AddressText = '';
            userProfile.hub2Lat = null;
            userProfile.hub2Lng = null;
            officeCachedShapes = {};
            persistProfile();
            if (officeMarker) { map.removeLayer(officeMarker); officeMarker = null; }
            if (officePolygon) { map.removeLayer(officePolygon); officePolygon = null; }
            document.getElementById('userHub2Address').value = '';
            document.getElementById('clearUserHub2Btn').classList.add('hidden');
        });

        document.getElementById('userHubPhotoInput').addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                alert('Please choose a JPEG, PNG, WEBP, or GIF image.');
                e.target.value = '';
                return;
            }
            if (file.size > 3 * 1024 * 1024) {
                alert('Image must be under 3MB.');
                e.target.value = '';
                return;
            }

            const label = document.getElementById('uploadPhotoLabel');
            const originalLabel = label.textContent;
            label.textContent = '⏳ Uploading...';

            try {
                const response = await fetch('/api/upload-photo', {
                    method: 'POST',
                    headers: { 'Content-Type': file.type },
                    body: file
                });
                if (response.status === 429) {
                    const err = await response.json().catch(() => ({}));
                    alert(err.error || 'Upload limit reached - you can upload up to 3 photos per hour.');
                    label.textContent = originalLabel;
                    e.target.value = '';
                    return;
                }
                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    throw new Error(err.error || `Upload failed (server said ${response.status}).`);
                }
                const data = await response.json();

                userProfile.photoUrl = data.url;
                persistProfile();
                updatePhotoPreview(`${window.location.origin}${data.url}`);
                label.textContent = '✅ Uploaded!';
                setTimeout(() => { label.textContent = originalLabel; }, 2000);
            } catch (err) {
                // Was a hardcoded "this feature needs Cloudflare Pages" message written for local
                // dev, where R2 genuinely isn't available - left in place after that stopped being
                // the likely cause, so a real production failure (wrong format, oversized file, a
                // dropped connection) showed a categorically wrong explanation. Now surfaces
                // whatever the server actually said, or names a real network failure honestly.
                alert(err.message || "Photo upload failed - couldn't reach the server. Check your connection and try again.");
                label.textContent = originalLabel;
            }
            e.target.value = '';
        });

        if (hasSavedProfile) {
            renderUserHubNode();
            renderOfficeNode();
            drawMyServiceArea(60);
            // Collapsed by default once there's an actual profile to collapse, unless this device
            // last left it open (PROFILE_OPEN_KEY). Left expanded (the static markup's default)
            // for a brand-new user, since that's the form they're here to fill in. Collapsed isn't
            // hidden any more, either: the summary row (name, range, share link, QR) stays on
            // screen, since those are what people open the card for. Safe to toggle here: nothing
            // touches this section before page load reaches this point, so it's still open.
            let profileOpenPref = null;
            try { profileOpenPref = localStorage.getItem(PROFILE_OPEN_KEY); } catch (e) { /* no storage */ }
            if (profileOpenPref !== '1') toggleAccordion('profileSection', 'profileArrow');
        }
        updateProfileSummary();
        // Same reasoning as Profile above, keyed on having colleagues rather than hasSavedProfile
        // - someone with zero colleagues yet is here specifically to use this form, so it stays
        // open for them same as it always has. Once there's a real network to browse, this form
        // competing with the colleague list for space by defaulting open every visit was the
        // other half of why the list never got meaningful room to scroll in on its own.
        if (savedPartners.length > 0) {
            toggleAccordion('addPartnerSection', 'addPartnerArrow');
        }

        // How long to wait before retrying a 429, preferring the provider's own reset time over a
        // guess. The proxy (worker/isochrone-proxy.js) passes through ORS's X-Ratelimit-Reset
        // header - a Unix epoch second marking when the limit clears. A per-minute limit needs up
        // to ~60s to reset; the flat 1.4s/2.8s backoff this replaced gave up long before that could
        // ever happen, which is exactly why one still-uncached colleague could lose this race on
        // every single visit for weeks (see the "map provider limit reached" banner) even though
        // the provider itself was never actually broken. Bounded at 20s per wait either way, so two
        // retries can never turn into a minute-long stall on one colleague's shape.
        function isochroneRetryDelayMs(response, attempt) {
            const resetEpochSeconds = parseInt(response.headers.get('x-ratelimit-reset'), 10);
            if (!Number.isNaN(resetEpochSeconds)) {
                const waitMs = resetEpochSeconds * 1000 - Date.now();
                if (waitMs > 0) return Math.min(waitMs + 250, 20000);
            }
            return Math.min(3000 * (attempt + 1), 20000); // no usable header - fall back to a guess
        }

        // Returns a real drive-time shape or nothing at all. Callers must never substitute a
        // circle for a null - a guessed radius is the exact thing this product exists to replace,
        // and drawing one silently makes the map claim something it hasn't measured.
        //
        // Two retries on 429 or a 5xx, not one: with the throttle above already keeping
        // steady-state load low, a failure that still gets through is more likely the
        // provider's own transient blip than sustained overload, so it's worth real patience
        // before giving up (see isochroneRetryDelayMs above). A larger drive-time range takes
        // real computation time on the provider's end - measured directly at 3.5-4.5s for a
        // 60-minute isochrone versus near-instant for 20 minutes - and that same load
        // occasionally produces an outright 500, not just a 429; confirmed directly by hand,
        // retrying the exact same request seconds later succeeded every time. Only a 500 (or
        // other 5xx) gets this same retry now - it used to fall through with 429's rule
        // ("not 429 -> give up"), so a single transient blip on a longer, slower isochrone was
        // treated as permanent, which is exactly why longer ranges failed far more often than
        // short ones even though the provider was never actually down. A 400 (range above the
        // provider's 3600s ceiling) or a network failure is still returned as null immediately,
        // because retrying won't change either.
        async function fetchLiveIsochrone(lat, lng, minutes) {
            if (!ISOCHRONE_PROXY_URL || ISOCHRONE_PROXY_URL.includes('REPLACE-ME')) return null;
            return withIsochroneThrottle(async () => {
                for (let attempt = 0; attempt < 3; attempt++) {
                    let response;
                    try {
                        response = await fetch(ISOCHRONE_PROXY_URL, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-App-Token': APP_TOKEN },
                            body: JSON.stringify({ locations: [[lng, lat]], range: [minutes * 60], range_type: 'time', smoothing: 4.5 })
                        });
                    } catch (err) { return null; }
                    if (response.ok) return await response.json();
                    if (response.status !== 429 && response.status < 500) return null;
                    if (attempt < 2) await new Promise(r => setTimeout(r, isochroneRetryDelayMs(response, attempt)));
                }
                return null;
            });
        }

        // --- POINT AND CLICK REVERSE GEOCODER ENGINE ---
        const pickOnMapBtn = document.getElementById('pickOnMapBtn');
        const mapElement = document.getElementById('map');
        let isPicking = false;

        pickOnMapBtn.addEventListener('click', () => {
            if(isPicking) {
                isPicking = false;
                mapElement.classList.remove('crosshair-cursor-enabled');
                pickOnMapBtn.classList.remove('bg-emerald-600', 'border-emerald-400');
                pickOnMapBtn.classList.add('bg-gray-700', 'border-gray-600');
                pickOnMapBtn.innerHTML = '<span>📍</span> <span>Pick</span>';
                map.off('click', handleMapPick);
            } else {
                isPicking = true;
                mapElement.classList.add('crosshair-cursor-enabled');
                pickOnMapBtn.classList.remove('bg-gray-700', 'border-gray-600');
                pickOnMapBtn.classList.add('bg-emerald-600', 'border-emerald-400');
                pickOnMapBtn.innerHTML = '<span>⏳</span> <span>Click Map!</span>';
                map.once('click', handleMapPick);
                
                const addSection = document.getElementById('addPartnerSection');
                if(addSection.classList.contains('accordion-hidden')) {
                    toggleAccordion('addPartnerSection', 'addPartnerArrow');
                }
            }
        });

        async function handleMapPick(e) {
            isPicking = false;
            mapElement.classList.remove('crosshair-cursor-enabled');
            pickOnMapBtn.classList.remove('bg-emerald-600', 'border-emerald-400');
            pickOnMapBtn.classList.add('bg-gray-700', 'border-gray-600');
            pickOnMapBtn.innerHTML = '<span>📍</span> <span>Pick</span>';

            const lat = e.latlng.lat;
            const lng = e.latlng.lng;
            document.getElementById('partnerAddressInput').value = "Loading location...";

            try {
                const result = await googleReverseGeocode(lat, lng);
                if (result) {
                    document.getElementById('partnerAddressInput').value = result.formattedAddress;
                } else {
                    document.getElementById('partnerAddressInput').value = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                }
            } catch(err) {
                document.getElementById('partnerAddressInput').value = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
            }
            saveAddPartnerDraft();
        }

        // --- CORE PERSISTENCE & DATA MATRICES ---
        let mapMarkers = {};
        let mapPolygons = {};
        let mapMarkers2 = {};   // partners' second-base dot markers
        let mapPolygons2 = {};  // partners' second-base coverage shapes
        let searchTargetMarker = null;
        // The address of the lead last looked up in the Partner Locator - carried into the
        // referral form's Lead Location, which used to make you type it a second time.
        let lastLeadAddress = '';
        let activeGroupFilter = 'ALL';
        let textSearchTerm = '';
        let hiddenPartnersState = {};
        let repositionState = {}; // per-partner: is map-drag temporarily unlocked?
        // Per-partner: is the shape on the map a straight-line estimate rather than a measured
        // drive time? Only ever true past 60 minutes, where the provider can't measure at all.
        // Read by the sidebar card and by the lead search, so an estimate is never presented as
        // a confirmed match.
        let estimatedCoverage = {};

        // Listen for Text Search input
        document.getElementById('contactSearchInput').addEventListener('input', (e) => {
            textSearchTerm = e.target.value.toLowerCase();
            applyFilters();
        });

        // convertDriveTimeToMeters now lives in lib/pure.js.

        // Partner/referral fields can originate from someone else's share link (or a raw URL
        // param an attacker hand-crafted), not just the current user's own typing - so anything
        // interpolated into innerHTML/attribute strings has to be escaped here, not trusted.
        function escapeHtml(str) {
            return String(str ?? '').replace(/[&<>"']/g, c => ({
                '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
            }[c]));
        }

        // --- TAG AUTOCOMPLETE: suggests tags already used anywhere in your network as you type
        // into a comma-separated tag field, so "Florida Realtors" doesn't end up duplicated as
        // "FL Realtors" / "FR" on different cards. Only ever looks at the segment after the last
        // comma in the field's current value (not the caret position) - covers the normal case of
        // typing tags in order, and simply doesn't offer suggestions if you go back and edit an
        // earlier one. One shared #tagSuggestBox is repositioned under whichever field is active,
        // rather than building a dropdown per input. ---
        function getAllKnownTags() {
            let all = [];
            savedPartners.forEach(p => { if (p.groups) all = all.concat(p.groups); });
            if (userProfile.tags) all = all.concat(userProfile.tags.split(',').map(t => t.trim()));
            return [...new Set(all)].filter(t => t);
        }

        const tagSuggestBox = document.getElementById('tagSuggestBox');
        let tagSuggestTarget = null;
        let tagSuggestActiveIndex = -1;

        function hideTagSuggestions() {
            tagSuggestBox.classList.add('hidden');
            tagSuggestTarget = null;
            tagSuggestActiveIndex = -1;
        }

        function lastTagSegment(value) {
            const parts = value.split(',');
            return parts[parts.length - 1].trim();
        }

        function updateTagSuggestActive() {
            [...tagSuggestBox.children].forEach((el, i) => el.classList.toggle('active', i === tagSuggestActiveIndex));
        }

        function renderTagSuggestions(input, matches) {
            tagSuggestTarget = input;
            tagSuggestActiveIndex = -1;
            tagSuggestBox.innerHTML = matches.map(t =>
                `<div class="tag-suggest-item" data-tag="${escapeHtml(t)}">${escapeHtml(t)}</div>`
            ).join('');
            const rect = input.getBoundingClientRect();
            tagSuggestBox.style.left = rect.left + 'px';
            tagSuggestBox.style.top = (rect.bottom + 4) + 'px';
            tagSuggestBox.style.width = Math.max(rect.width, 160) + 'px';
            tagSuggestBox.classList.remove('hidden');
        }

        function handleTagInput(e) {
            const input = e.target;
            const segment = lastTagSegment(input.value);
            if (!segment) return hideTagSuggestions();

            const alreadyUsed = input.value.split(',').map(t => t.trim().toLowerCase());
            const matches = getAllKnownTags()
                .filter(t => t.toLowerCase().includes(segment.toLowerCase()) && !alreadyUsed.includes(t.toLowerCase()))
                .slice(0, 6);

            if (!matches.length) return hideTagSuggestions();
            renderTagSuggestions(input, matches);
        }

        function pickTagSuggestion(tag) {
            const input = tagSuggestTarget;
            if (!input) return;
            const parts = input.value.split(',').map(t => t.trim()).filter(t => t !== '');
            parts[parts.length - 1] = tag;
            input.value = parts.join(', ') + ', ';
            hideTagSuggestions();
            input.focus();
        }

        function handleTagKeydown(e) {
            if (tagSuggestBox.classList.contains('hidden') || tagSuggestTarget !== e.target) return;
            const items = [...tagSuggestBox.children];
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                tagSuggestActiveIndex = Math.min(tagSuggestActiveIndex + 1, items.length - 1);
                updateTagSuggestActive();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                tagSuggestActiveIndex = Math.max(tagSuggestActiveIndex - 1, 0);
                updateTagSuggestActive();
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                if (tagSuggestActiveIndex >= 0 && items[tagSuggestActiveIndex]) {
                    e.preventDefault();
                    pickTagSuggestion(items[tagSuggestActiveIndex].dataset.tag);
                }
            } else if (e.key === 'Escape') {
                hideTagSuggestions();
            }
        }

        tagSuggestBox.addEventListener('mousedown', (e) => {
            // mousedown (not click) fires before the input's blur, so the tag is captured before
            // hideTagSuggestions() would otherwise run and remove these items first.
            const item = e.target.closest('.tag-suggest-item');
            if (!item) return;
            e.preventDefault();
            pickTagSuggestion(item.dataset.tag);
        });

        // Wrong-separator guard. The `title` tooltips on these fields never appear on a phone
        // (nothing to hover), so on mobile the placeholder is the only guidance - and it's gone
        // the moment you start typing. This catches the mistake as it's being made instead.
        //
        // Prompted by a real case: a colleague pasted their marketing tagline, "Experience |
        // Education | Excellence", into their own profile's tag field. Split on commas, that's
        // one 35-character tag, which then rides the share link into every colleague's filter bar
        // as a single unfilterable chip.
        //
        // Only pipes and semicolons trigger it - both are unambiguous "I meant to separate these"
        // characters. Slashes are deliberately excluded: "Buyer/Seller" and "Residential/
        // Commercial" are perfectly good single tags, and a false alarm on a correct entry would
        // teach people to ignore the warning.
        function updateTagSeparatorHint(input) {
            const existing = input.nextElementSibling;
            const isHint = existing && existing.classList && existing.classList.contains('tag-separator-hint');

            if (!/[|;]/.test(input.value)) {
                if (isHint) existing.remove();
                return;
            }
            if (isHint) return;

            const hint = document.createElement('p');
            hint.className = 'tag-separator-hint text-[10px] leading-snug mt-1 text-amber-400';
            hint.textContent = 'Separate tags with commas — each one becomes its own filter chip, so a slogan lands as a single tag nobody can filter by.';
            input.insertAdjacentElement('afterend', hint);
        }

        function attachTagAutocomplete(input) {
            if (!input || input.dataset.tagAutocomplete) return;
            input.dataset.tagAutocomplete = 'true';
            input.addEventListener('input', handleTagInput);
            input.addEventListener('input', () => updateTagSeparatorHint(input));
            input.addEventListener('keydown', handleTagKeydown);
            input.addEventListener('blur', () => setTimeout(hideTagSuggestions, 150));
        }
        attachTagAutocomplete(document.getElementById('userHubTags'));
        attachTagAutocomplete(document.getElementById('partnerGroupInput'));

        // For values placed inside an href/src - escapeHtml alone doesn't stop a "javascript:"
        // scheme from being clickable, so only http(s) urls (or plain relative/local paths) pass.
        function sanitizeUrl(url) {
            if (!url) return '';
            const trimmed = String(url).trim();
            if (/^https?:\/\//i.test(trimmed)) return escapeHtml(trimmed);
            if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return ''; // any other scheme (javascript:, data:, vbscript:, ...)
            return escapeHtml(trimmed);
        }

        // --- PER-PARTNER COLOR SYSTEM: stable, distinct color per partner so overlapping
        // territories stay visually distinguishable on the map and in ranked results ---
        const PARTNER_PALETTE = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16'];
        function getPartnerColor(id) {
            let hash = 0;
            for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
            return PARTNER_PALETTE[hash % PARTNER_PALETTE.length];
        }

        // Full contact card for the map popup - clicking a colleague's pin should be enough to
        // reach them (tel:/mailto: links are tappable straight from a phone).
        function buildPartnerPopup(partner) {
            const rows = [];
            if (partner.phone) rows.push(`<a href="tel:${escapeHtml(partner.phone)}" class="block text-blue-600 font-semibold no-underline">📞 ${escapeHtml(partner.phone)}</a>`);
            if (partner.email) rows.push(`<a href="mailto:${escapeHtml(partner.email)}" class="block text-blue-600 font-semibold no-underline">✉️ ${escapeHtml(partner.email)}</a>`);
            if (partner.social) rows.push(`<a href="${sanitizeUrl(partner.social)}" target="_blank" rel="noopener" class="block text-blue-600 font-semibold no-underline">🌐 Social Profile</a>`);
            return `
                <div class="p-1 min-w-[180px] max-w-[220px]">
                    ${partner.photoUrl ? `<img src="${sanitizeUrl(partner.photoUrl)}" data-onerror="remove" class="w-11 h-11 rounded-full object-cover mb-1.5 border border-gray-300" />` : ''}
                    <b class="text-gray-900 block text-sm leading-tight">${escapeHtml(partner.name)}</b>
                    ${partner.brokerage ? `<span class="text-gray-500 text-[11px] block">${escapeHtml(partner.brokerage)}</span>` : ''}
                    <span class="text-gray-400 text-[10px] block italic ${partner.base2Name ? '' : 'mb-1.5'}">${escapeHtml(partner.baseName)}</span>
                    ${partner.base2Name ? `<span class="text-gray-400 text-[10px] block italic mb-1.5">🏢 ${escapeHtml(partner.base2Name)}</span>` : ''}
                    ${rows.length ? `<div class="space-y-1 text-[11px] border-t border-gray-200 pt-1.5">${rows.join('')}</div>` : ''}
                    ${partner.notes ? `<div class="text-[10px] text-gray-500 mt-1.5 leading-snug">📝 ${escapeHtml(partner.notes)}</div>` : ''}
                    <div class="flex mt-1.5 pt-1.5 border-t border-gray-200">
                        <button data-action="openPartnerDetailModal" data-arg0="${partner.id}" class="flex-1 text-center text-blue-600 font-bold text-[10px] no-underline hover:underline">⤢ Full Details</button>
                        <button data-action="isolatePartner" data-arg0="${partner.id}" class="flex-1 text-center text-indigo-600 font-bold text-[10px] no-underline hover:underline border-l border-gray-200" title="Hide everyone else so only this colleague's coverage shows">🔎 Isolate</button>
                    </div>
                    <button data-action="openPartnerInSidebar" data-arg0="${partner.id}" class="block w-full text-center text-emerald-600 font-bold text-[10px] mt-1 pt-1.5 border-t border-gray-200 no-underline hover:underline">✏️ Edit / Delete in Sidebar →</button>
                </div>`;
        }

        // Draws a partner's second base (if their share link carried one): a small dot marker
        // plus its own coverage shape in the partner's color with a tighter dash, so their full
        // two-base territory reads at a glance. Safe to call repeatedly - clears old layers first.
        async function renderPartnerSecondBase(partner) {
            const pId = partner.id;
            if (mapMarkers2[pId]) { map.removeLayer(mapMarkers2[pId]); delete mapMarkers2[pId]; }
            if (mapPolygons2[pId]) { map.removeLayer(mapPolygons2[pId]); delete mapPolygons2[pId]; }
            if (partner.lat2 == null || partner.lng2 == null) return;

            const color = getPartnerColor(pId);

            let shape2 = partner.geoJson2Data;
            if (!shape2) {
                shape2 = await fetchLiveIsochrone(partner.lat2, partner.lng2, partner.radius);
                if (shape2) {
                    const index = savedPartners.findIndex(p => p.id === pId);
                    if (index !== -1) {
                        savedPartners[index].geoJson2Data = shape2;
                        persistPartnersLocal();
                        syncPartnerUpdate(savedPartners[index]);
                    }
                }
            }

            if (shape2) {
                mapPolygons2[pId] = L.geoJSON(shape2, {
                    style: { color: color, fillColor: color, fillOpacity: 0.07, weight: 1.5, dashArray: '2, 7' }
                }).addTo(map);
            } else if (isEstimateRange(partner.radius)) {
                mapPolygons2[pId] = L.circle([partner.lat2, partner.lng2], {
                    radius: convertDriveTimeToMeters(partner.radius), color: color, fillColor: color,
                    fillOpacity: 0.04, weight: 1.5, dashArray: '1, 6'
                }).addTo(map);
                estimatedCoverage[pId] = true;
            }
            // Below 60 a missing shape draws nothing at all - the failure is transient and a real
            // answer exists, so the card says "not measured" rather than showing a guess.

            mapMarkers2[pId] = L.circleMarker([partner.lat2, partner.lng2], {
                radius: 6, color: '#ffffff', fillColor: color, fillOpacity: 0.95, weight: 2
            }).addTo(map).bindPopup(`<b class='text-gray-900'>${escapeHtml(partner.name)}</b><br><span class='text-gray-500 text-xs'>🏢 2nd base: ${escapeHtml(partner.base2Name || '')}</span>`);
        }

        async function renderPartnerAndCoverage(partner) {
            const pId = partner.id;
            const color = getPartnerColor(pId);
            hiddenPartnersState[pId] = false;

            const partnerIcon = L.divIcon({
                // No text-xs here on purpose: .partner-map-label sets the size from --ui-scale,
                // and a utility class alongside it would win or lose depending on where Tailwind's
                // runtime injects its stylesheet.
                html: `<div id="map-label-${pId}" class="partner-map-label bg-slate-900/95 border p-1.5 px-3 rounded-lg shadow-xl font-bold whitespace-nowrap transition-all flex items-center space-x-1" style="color:${color}; border-color:${color}80;"><span>🤝</span> <span>${escapeHtml(partner.name)}</span></div>`,
                className: '', iconAnchor: [30, 14]
            });

            // Locked by default - dragging only unlocks via the "Reposition" toggle in edit mode,
            // so panning/zooming the map can no longer accidentally relocate a colleague.
            mapMarkers[pId] = L.marker([partner.lat, partner.lng], { icon: partnerIcon, draggable: false }).addTo(map);
            mapMarkers[pId].bindPopup(buildPartnerPopup(partner));

            let geoJsonFeature = partner.geoJsonData;
            if (!geoJsonFeature) {
                geoJsonFeature = await fetchLiveIsochrone(partner.lat, partner.lng, partner.radius);
                if (geoJsonFeature) updateCachedGeometry(pId, geoJsonFeature);
            }

            delete estimatedCoverage[pId];
            if (geoJsonFeature) {
                mapPolygons[pId] = L.geoJSON(geoJsonFeature, {
                    style: { color: color, fillColor: color, fillOpacity: 0.1, weight: 1.5, dashArray: '4, 6' }
                }).addTo(map);
            } else if (isEstimateRange(partner.radius)) {
                // Past 60 minutes the provider can't measure at all, so a straight-line estimate
                // is the only thing available. Drawn dotted and faint, flagged on the card, and
                // reported as an estimate in search - never dressed up as a measured shape.
                mapPolygons[pId] = L.circle([partner.lat, partner.lng], {
                    radius: convertDriveTimeToMeters(partner.radius), color: color, fillColor: color,
                    fillOpacity: 0.06, weight: 1.5, dashArray: '1, 6'
                }).addTo(map);
                estimatedCoverage[pId] = true;
            }
            // At or below 60 a missing shape draws nothing: the failure is transient and a real
            // answer exists, so guessing would hide a temporary problem behind a permanent one.

            mapMarkers[pId].on('dragend', async function (event) {
                const marker = event.target;
                const position = marker.getLatLng();
                if (mapPolygons[pId]) map.removeLayer(mapPolygons[pId]);

                const freshApiData = await fetchLiveIsochrone(position.lat, position.lng, partner.radius);
                delete estimatedCoverage[pId];
                if (freshApiData) {
                    mapPolygons[pId] = L.geoJSON(freshApiData, { style: { color: color, fillColor: color, fillOpacity: 0.1, weight: 1.5, dashArray: '4, 6' } }).addTo(map);
                } else if (isEstimateRange(partner.radius)) {
                    mapPolygons[pId] = L.circle(position, { radius: convertDriveTimeToMeters(partner.radius), color: color, fillColor: color, fillOpacity: 0.06, weight: 1.5, dashArray: '1, 6' }).addTo(map);
                    estimatedCoverage[pId] = true;
                }

                const index = savedPartners.findIndex(p => p.id === pId);
                if (index !== -1) {
                    savedPartners[index].lat = position.lat;
                    savedPartners[index].lng = position.lng;
                    savedPartners[index].geoJsonData = freshApiData || null;

                    // Dragging only fine-tunes position - it never touches the saved address text,
                    // so a real mailing address is never silently replaced by a reverse-geocode guess.
                    if (!savedPartners[index].baseName) {
                        const fallbackLabel = `Manually Repositioned [${position.lat.toFixed(3)}, ${position.lng.toFixed(3)}]`;
                        savedPartners[index].baseName = fallbackLabel;
                        document.getElementById(`display-base-${pId}`).innerHTML = fallbackLabel;
                        document.getElementById(`edit-base-input-${pId}`).value = fallbackLabel;
                    }

                    persistPartnersLocal();
                    syncPartnerUpdate(savedPartners[index]);
                }

                // Self-resets to locked after every drag - reposition mode is a one-shot
                // unlock, not a standing state, so the next accidental grab can't move it again.
                setPartnerReposition(pId, false);
            });

            renderPartnerSecondBase(partner);
            buildSidebarCard(partner);
            updateGroupFilters();

            // Tells the initial-load summary (see the savedPartners.forEach below) whether this
            // colleague ended up with nothing drawn at all - only possible at or under 60 minutes,
            // where a missing shape is deliberately left blank rather than guessed at.
            return { shapeFailed: !geoJsonFeature && !isEstimateRange(partner.radius) };
        }

        // Flips a colleague's map pin between locked (default) and drag-to-reposition. Centralized
        // here so the edit-mode toggle button and the post-drag auto-relock share one code path.
        function setPartnerReposition(id, enabled) {
            const marker = mapMarkers[id];
            if (!marker) return;
            repositionState[id] = enabled;
            if (enabled) marker.dragging.enable(); else marker.dragging.disable();

            const label = document.getElementById(`map-label-${id}`);
            if (label) label.classList.toggle('reposition-active', enabled);

            const btn = document.getElementById(`reposition-btn-${id}`);
            if (btn) {
                btn.textContent = enabled ? '🔓 Lock Position' : '📍 Enable Reposition';
                btn.classList.toggle('bg-blue-600', enabled);
                btn.classList.toggle('text-white', enabled);
                btn.classList.toggle('bg-gray-300', !enabled);
                btn.classList.toggle('dark:bg-gray-800', !enabled);
            }
        }

        window.togglePartnerReposition = function(id) {
            setPartnerReposition(id, !repositionState[id]);
        };

        function updateCachedGeometry(id, geoJson) {
            const index = savedPartners.findIndex(p => p.id === id);
            if (index !== -1) {
                savedPartners[index].geoJsonData = geoJson;
                persistPartnersLocal();
                syncPartnerUpdate(savedPartners[index]);
            }
        }

        // getLastName now lives in lib/pure.js.

        // Active Network Index order: last name (the long-standing default), newest added, or
        // most recently updated - the last two newest-first, which is what puts a colleague who
        // just added themselves at the top of the list instead of somewhere mid-alphabet.
        // Remembered per device. Cards carry their own sort keys as data attributes so placing
        // or re-sorting never has to look partners back up by id. A colleague with no date
        // (saved before dates were recorded, on a signed-out device) sorts as oldest, then by
        // last name.
        const PARTNER_SORT_KEY = 'partner_sort_v1';
        let partnerSort = 'name';
        try {
            const storedSort = localStorage.getItem(PARTNER_SORT_KEY);
            if (['name', 'added', 'updated'].includes(storedSort)) partnerSort = storedSort;
        } catch (e) { /* no storage - default order */ }
        document.getElementById('partnerSortSelect').value = partnerSort;
        document.getElementById('partnerList').classList.add('sort-' + partnerSort);

        function compareCards(a, b) {
            if (partnerSort !== 'name') {
                const attr = partnerSort === 'added' ? 'data-created' : 'data-updated';
                const diff = Number(b.getAttribute(attr) || 0) - Number(a.getAttribute(attr) || 0);
                if (diff) return diff;
            }
            const aName = a.getAttribute('data-lastname'), bName = b.getAttribute('data-lastname');
            return aName < bName ? -1 : aName > bName ? 1 : 0;
        }

        // Re-run on every card build (not just new ones), so renaming or editing a colleague
        // moves their card too.
        function insertSorted(list, item) {
            const endMarker = document.getElementById('partnerListEndMarker');
            const siblings = [...list.children].filter(el => el !== item && el.hasAttribute('data-lastname'));
            const next = siblings.find(el => compareCards(item, el) < 0);
            list.insertBefore(item, next || endMarker);
        }

        function resortPartnerList() {
            const list = document.getElementById('partnerList');
            const endMarker = document.getElementById('partnerListEndMarker');
            [...list.querySelectorAll('li[data-lastname]')]
                .sort(compareCards)
                .forEach(card => list.insertBefore(card, endMarker));
        }

        window.setPartnerSort = function(mode) {
            const list = document.getElementById('partnerList');
            list.classList.remove('sort-' + partnerSort);
            partnerSort = mode;
            list.classList.add('sort-' + partnerSort);
            try { localStorage.setItem(PARTNER_SORT_KEY, mode); } catch (e) { /* just don't remember */ }
            resortPartnerList();
        };

        // "today" / "3d ago" for the recent past, a plain date after that - shown on a card only
        // while the list is sorted by that same date (see .card-date in custom.css).
        function shortDateLabel(ts) {
            if (!ts) return '';
            const days = Math.floor((Date.now() - ts) / 86400000);
            if (days <= 0) return 'today';
            if (days === 1) return 'yesterday';
            if (days < 7) return `${days}d ago`;
            const d = new Date(ts);
            const sameYear = d.getFullYear() === new Date().getFullYear();
            return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
        }

        function buildSidebarCard(partner) {
            const pId = partner.id;
            const color = getPartnerColor(pId);
            const list = document.getElementById('partnerList');
            let item = document.getElementById("sidebar-item-" + pId) || document.createElement('li');
            item.id = "sidebar-item-" + pId;
            item.setAttribute('data-partner-id', pId);
            item.setAttribute('data-lastname', getLastName(partner.name));
            item.setAttribute('data-created', partner.createdAt || 0);
            item.setAttribute('data-updated', partner.updatedAt || partner.createdAt || 0);
            insertSorted(list, item);
            const addedLabel = shortDateLabel(partner.createdAt);
            const updatedLabel = shortDateLabel(partner.updatedAt || partner.createdAt);

            const tagsArray = partner.groups || [];
            let tagBadgesHtml = tagsArray.map(t => `<span class="bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20 text-[9px] px-1.5 py-0.5 rounded font-mono font-bold uppercase tracking-wider">${escapeHtml(t)}</span>`).join(' ');
            const linkBadgeHtml = partnerLinkBadge(partner);

            item.className = "text-[11px] p-2.5 rounded-lg border transition-all duration-200 partner-card sidebar-card shadow-sm";

            item.innerHTML = `
                <div id="view-mode-${pId}" class="flex flex-col">
                    <div class="flex items-center justify-between cursor-pointer" data-action="togglePartnerAccordion" data-arg0="${pId}">
                        <input type="checkbox" class="partner-select-cb mr-2 w-4 h-4 accent-red-500 cursor-pointer flex-shrink-0" data-partner-id="${pId}" data-action="togglePartnerSelection" data-arg0="${pId}" data-read-checked ${selectedPartnerIds.has(pId) ? 'checked' : ''} />
                        <div class="truncate pr-2 space-y-1">
                            <div class="flex items-center gap-1.5">
                                ${partner.photoUrl ? `<img src="${sanitizeUrl(partner.photoUrl)}" data-onerror="remove" class="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-gray-300/30 dark:border-gray-700" />` : ''}
                                <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" style="background:${color};"></span>
                                <strong class="font-bold text-xs tracking-tight">${escapeHtml(partner.name)}</strong>
                            </div>
                            <div class="flex flex-wrap gap-1">
                                ${linkBadgeHtml}
                                ${tagBadgesHtml || (linkBadgeHtml ? '' : '<span class="text-[9px] opacity-40 italic">No Tags</span>')}
                            </div>
                            <span id="display-base-${pId}" class="opacity-70 font-medium italic truncate block text-[10px]">${escapeHtml(partner.baseName)}</span>
                            ${partner.base2Name ? `<span class="opacity-60 font-medium italic truncate block text-[10px]">🏢 ${escapeHtml(partner.base2Name)}</span>` : ''}
                            <span id="distance-label-${pId}" class="hidden font-mono font-bold text-[9px] text-amber-400"></span>
                            ${addedLabel ? `<span class="card-date card-date-added text-[9px] opacity-50 font-mono">Added ${addedLabel}</span>` : ''}
                            ${updatedLabel ? `<span class="card-date card-date-updated text-[9px] opacity-50 font-mono">Updated ${updatedLabel}</span>` : ''}
                        </div>
                        <div class="flex flex-col items-end gap-1 flex-shrink-0 pl-1">
                            <span id="rank-badge-${pId}" class="hidden text-[9px] font-black px-1.5 py-0.5 rounded-full text-white whitespace-nowrap"></span>
                            <span id="arrow-${pId}" role="button" tabindex="0" aria-label="${escapeHtml(partner.name)} details" aria-expanded="false" aria-controls="acc-content-${pId}" class="text-gray-500 text-xs transition-transform duration-300">▼</span>
                        </div>
                    </div>

                    <div id="acc-content-${pId}" class="accordion-content accordion-hidden mt-2 border-t border-gray-700/50 pt-2 space-y-1">
                        <p class="text-[10px]"><strong>Brokerage:</strong> ${escapeHtml(partner.brokerage) || 'N/A'}</p>
                        <p class="text-[10px]"><strong>Phone:</strong> ${escapeHtml(partner.phone) || 'N/A'}</p>
                        <p class="text-[10px]"><strong>Email:</strong> ${escapeHtml(partner.email) || 'N/A'}</p>
                        <p class="text-[10px]"><strong>Social:</strong> <a href="${sanitizeUrl(partner.social)}" target="_blank" class="text-blue-400 underline hover:text-blue-300">${partner.social ? 'View Profile' : 'N/A'}</a></p>
                        <p class="text-[10px] mt-1 text-gray-300"><strong>Notes:</strong> ${escapeHtml(partner.notes) || 'N/A'}</p>
                        
                        <div class="flex flex-wrap gap-1 mt-2 border-t border-gray-700/50 pt-2">
                            <button data-action="exportVCF" data-arg0="${pId}" class="bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-bold px-2 py-1 rounded shadow">Export Contact</button>
                            <div class="flex ml-auto space-x-1">
                                <button data-action="openReferralModal" data-arg0="${pId}" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800" title="Send & track a referral to this colleague">📤</button>
                                <button data-action="sharePartnerCard" data-arg0="${pId}" id="share-btn-${pId}" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800" title="Copy a share link with this colleague's info">🔗</button>
                                <button data-action="toggleSinglePartnerVisibility" data-arg0="${pId}" id="toggle-eye-${pId}" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800" title="Show/hide this colleague's pin and coverage on the map">👁️</button>
                                <button data-action="isolatePartner" data-arg0="${pId}" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800" title="Hide everyone else so only this colleague's coverage shows on the map">🔎</button>
                                <button data-action="toggleEditMode" data-arg0="${pId}" data-arg1="true" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800" title="Edit this colleague's details">✏️</button>
                                <button data-action="deletePartner" data-arg0="${pId}" class="action-icon-btn opacity-60 hover:opacity-100 text-base p-1.5 transition rounded hover:bg-black/10 dark:hover:bg-gray-800 text-red-400" title="Remove this colleague">🗑️</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="edit-mode-${pId}" class="hidden space-y-2 pt-2 border-t border-gray-700/20 mt-1.5">
                    <div class="flex space-x-1">
                        <input type="text" id="edit-base-input-${pId}" value="${escapeHtml(partner.baseName)}" class="flex-1 bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                        <button type="button" data-action="resetPartnerPinToAddress" data-arg0="${pId}" id="reset-pin-btn-${pId}" title="Snap the map pin back to match this address - use this if the pin drifted (e.g. from a drag) but the address text above is still correct" class="flex-shrink-0 bg-gray-700 hover:bg-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-white text-[9px] px-2 rounded-md font-bold transition-all active:scale-95 border border-gray-600">🎯 Reset Pin</button>
                    </div>
                    <input type="text" id="edit-brokerage-input-${pId}" value="${escapeHtml(partner.brokerage)}" placeholder="Brokerage" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                    <input type="text" id="edit-phone-input-${pId}" value="${escapeHtml(partner.phone)}" placeholder="Phone" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                    <input type="text" id="edit-email-input-${pId}" value="${escapeHtml(partner.email)}" placeholder="Email" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                    <input type="text" id="edit-social-input-${pId}" value="${escapeHtml(partner.social)}" placeholder="Social URL" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                    <textarea id="edit-notes-input-${pId}" placeholder="Notes" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md">${escapeHtml(partner.notes)}</textarea>
                    <input type="text" id="edit-group-input-${pId}" value="${escapeHtml(tagsArray.join(', '))}" placeholder="Tags, comma separated (Luxury, Waterfront)" title="Short categories for grouping and filtering this colleague - specialty, market, referral status. One per comma." class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md" />
                    <select id="edit-radius-input-${pId}" title="How far this colleague will drive for a lead - sets the size of their coverage circle on the map" class="w-full bg-black/10 dark:bg-gray-950 border border-gray-300 dark:border-gray-800 text-[10px] p-1.5 rounded-md text-current">
                        <option value="15" ${partner.radius === 15 ? 'selected' : ''}>15 Min</option>
                        <option value="30" ${partner.radius === 30 ? 'selected' : ''}>30 Min</option>
                        <option value="45" ${partner.radius === 45 ? 'selected' : ''}>45 Min</option>
                        <option value="60" ${partner.radius === 60 ? 'selected' : ''}>1 Hour</option>
                        <option value="75" ${partner.radius === 75 ? 'selected' : ''}>1 Hr 15 Min</option>
                        <option value="90" ${partner.radius === 90 ? 'selected' : ''}>1 Hr 30 Min</option>
                        <option value="105" ${partner.radius === 105 ? 'selected' : ''}>1 Hr 45 Min</option>
                        <option value="120" ${partner.radius === 120 ? 'selected' : ''}>2 Hours</option>
                    </select>
                    <div class="flex items-center justify-between bg-black/5 dark:bg-gray-950/40 border border-gray-300/30 dark:border-gray-800 rounded-md px-2 py-1.5">
                        <span class="text-[9px] opacity-70" title="The map pin is locked by default so panning/zooming can't accidentally move it">📍 Map pin position</span>
                        <button type="button" data-action="togglePartnerReposition" data-arg0="${pId}" id="reposition-btn-${pId}" class="text-[9px] font-bold px-2 py-0.5 rounded bg-gray-300 dark:bg-gray-800 text-current transition-colors">📍 Enable Reposition</button>
                    </div>
                    <div class="flex items-center justify-end space-x-2 pt-1">
                        <button data-action="toggleEditMode" data-arg0="${pId}" data-arg1="false" class="text-[10px] opacity-70 hover:underline px-2 py-0.5">Cancel</button>
                        <button data-action="savePartnerEdits" data-arg0="${pId}" class="text-[10px] bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1 rounded shadow transition-all">Save</button>
                    </div>
                </div>
            `;
            attachTagAutocomplete(document.getElementById('edit-group-input-' + pId));
            applyFilters();
            updatePartnerListEmptyState();
        }

        // Toggles the "no colleagues yet" hint vs. the "end of list" marker based on whether
        // anyone's been added - #partnerList always has these two <li>s as its first/last child.
        function updatePartnerListEmptyState() {
            const isEmpty = savedPartners.length === 0;
            const emptyState = document.getElementById('partnerListEmptyState');
            const endMarker = document.getElementById('partnerListEndMarker');
            if (emptyState) emptyState.classList.toggle('hidden', !isEmpty);
            if (endMarker) endMarker.classList.toggle('hidden', isEmpty);
            updateNetworkCount(savedPartners.filter(p => {
                const card = document.getElementById(`sidebar-item-${p.id}`);
                return !card || !card.classList.contains('hidden');
            }).length);
        }

        window.toggleSinglePartnerVisibility = function(id) {
            hiddenPartnersState[id] = !hiddenPartnersState[id];
            const eyeBtn = document.getElementById(`toggle-eye-${id}`);
            const card = document.getElementById(`sidebar-item-${id}`);

            if (hiddenPartnersState[id]) {
                if (mapMarkers[id]) map.removeLayer(mapMarkers[id]);
                if (mapPolygons[id]) map.removeLayer(mapPolygons[id]);
                if (mapMarkers2[id]) map.removeLayer(mapMarkers2[id]);
                if (mapPolygons2[id]) map.removeLayer(mapPolygons2[id]);
                eyeBtn.innerHTML = "🙈";
                card.classList.add('opacity-30');
            } else {
                if (mapMarkers[id]) mapMarkers[id].addTo(map);
                if (mapPolygons[id]) mapPolygons[id].addTo(map);
                if (mapMarkers2[id]) mapMarkers2[id].addTo(map);
                if (mapPolygons2[id]) mapPolygons2[id].addTo(map);
                eyeBtn.innerHTML = "👁️";
                card.classList.remove('opacity-30');
            }
        };

        // --- ISOLATE: with enough colleagues saved, overlapping coverage shapes make it hard to
        // tell whose shape actually covers a given spot. Isolating hides everyone else's map
        // layers (not their saved hidden/visible state - see toggleSinglePartnerVisibility above,
        // which this deliberately doesn't touch) so the isolated colleague's shape reads clearly
        // against a blank map, then restores exactly what was showing before on exit. ---
        let isolatedPartnerId = null;

        window.isolatePartner = function(id) {
            if (isolatedPartnerId === id) { exitIsolateMode(); return; }
            isolatedPartnerId = id;

            savedPartners.forEach(p => {
                const isTarget = p.id === id;
                [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                    const layer = store[p.id];
                    if (!layer) return;
                    if (isTarget) { if (!map.hasLayer(layer)) layer.addTo(map); }
                    else map.removeLayer(layer);
                });
                const card = document.getElementById(`sidebar-item-${p.id}`);
                if (card) card.classList.toggle('opacity-30', !isTarget);
            });

            const target = savedPartners.find(p => p.id === id);
            if (mapMarkers[id]) mapMarkers[id].closePopup();
            document.getElementById('isolateBannerName').textContent = target ? target.name : '';
            const banner = document.getElementById('isolateBanner');
            banner.classList.remove('hidden');
            banner.classList.add('flex');
        };

        window.exitIsolateMode = function() {
            if (isolatedPartnerId === null) return;
            isolatedPartnerId = null;

            savedPartners.forEach(p => {
                const card = document.getElementById(`sidebar-item-${p.id}`);
                if (card) card.classList.toggle('opacity-30', !!hiddenPartnersState[p.id]);
                if (hiddenPartnersState[p.id]) return;  // was manually hidden before isolating - leave it that way
                [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                    const layer = store[p.id];
                    if (layer && !map.hasLayer(layer)) layer.addTo(map);
                });
            });

            const banner = document.getElementById('isolateBanner');
            banner.classList.add('hidden');
            banner.classList.remove('flex');
        };

        // Mirror of isolatePartner, but the "one thing shown" is your own coverage rather than a
        // colleague's - hides every colleague layer, leaves your home/office markers and shapes
        // untouched since those aren't in savedPartners/mapMarkers to begin with. Shares
        // isolatedPartnerId/exitIsolateMode/the banner rather than a parallel set of state, so
        // clicking a colleague's own Isolate afterward transitions cleanly (isolatePartner's own
        // forEach doesn't care what the previous mode was).
        const OWN_COVERAGE_ISOLATE_ID = '__own_coverage__';
        window.isolateOwnCoverage = function() {
            if (isolatedPartnerId === OWN_COVERAGE_ISOLATE_ID) { exitIsolateMode(); return; }
            isolatedPartnerId = OWN_COVERAGE_ISOLATE_ID;

            savedPartners.forEach(p => {
                [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                    const layer = store[p.id];
                    if (layer) map.removeLayer(layer);
                });
                const card = document.getElementById(`sidebar-item-${p.id}`);
                if (card) card.classList.add('opacity-30');
            });

            if (homeMarker) homeMarker.closePopup();
            document.getElementById('isolateBannerName').textContent = 'Your Coverage';
            const banner = document.getElementById('isolateBanner');
            banner.classList.remove('hidden');
            banner.classList.add('flex');
        };

        // --- MANAGE FROM THE MAP: clicking a colleague's pin only ever opened a read-only popup
        // - editing or deleting them meant remembering their name, closing the popup, hunting for
        // them in a (possibly long) sidebar list, and expanding their card by hand. This jumps
        // straight there: opens the sidebar, clears whatever tag/text filter would otherwise hide
        // the card, expands it, scrolls it into view, and flashes it so it's obvious which one -
        // landing exactly where the existing ✏️ Edit / 🗑️ Delete icons already live. ---
        window.openPartnerInSidebar = function(id) {
            if (mapMarkers[id]) mapMarkers[id].closePopup();
            revealSidebar();

            const indexContent = document.getElementById('networkIndexContent');
            if (indexContent.classList.contains('hidden')) toggleNetworkIndex();

            const card = document.getElementById(`sidebar-item-${id}`);
            if (!card) return;

            if (card.classList.contains('hidden')) {
                document.getElementById('contactSearchInput').value = '';
                textSearchTerm = '';
                activeGroupFilter = 'ALL';
                applyFilters();
                updateGroupFilters();
            }

            const acc = document.getElementById(`acc-content-${id}`);
            if (acc && acc.classList.contains('accordion-hidden')) togglePartnerAccordion(id);

            // Give the drawer/collapse-panel open animation (and, on mobile, its own reflow) a
            // moment to finish before scrolling - scrolling mid-transition lands in the wrong spot.
            setTimeout(() => {
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                card.classList.add('ring-2', 'ring-emerald-400');
                setTimeout(() => card.classList.remove('ring-2', 'ring-emerald-400'), 1600);
            }, 300);
        };

        // Explicit "snap pin to this address" action, independent of Save. savePartnerEdits only
        // re-geocodes when the address text actually changes - by design, so editing an unrelated
        // field (phone, notes) never triggers a needless geocode call. But that means if a pin
        // drifted (drag) while the address text was already correct, retyping the same correct
        // text never re-triggers a geocode, and there was no way to fix it. This button always
        // geocodes the current field text, regardless of whether it changed.
        window.resetPartnerPinToAddress = async function(id) {
            const input = document.getElementById(`edit-base-input-${id}`);
            const address = input.value.trim();
            if (!address) return alert("Enter an address first.");

            const btn = document.getElementById(`reset-pin-btn-${id}`);
            const originalLabel = '🎯 Reset Pin';
            btn.disabled = true;
            btn.textContent = 'Locating...';

            try {
                const geo = await geocodeWithFallback(address);
                if (!geo) {
                    alert("Couldn't find that address at all. Try a nearby city/state, then drag the pin to the exact spot.");
                    return;
                }

                const index = savedPartners.findIndex(p => p.id === id);
                if (index === -1) return;
                const partner = savedPartners[index];
                const color = getPartnerColor(id);

                partner.lat = geo.lat;
                partner.lng = geo.lng;
                partner.baseName = address;

                if (mapMarkers[id]) {
                    mapMarkers[id].setLatLng([geo.lat, geo.lng]);
                    mapMarkers[id].setPopupContent(buildPartnerPopup(partner));
                }
                if (mapPolygons[id]) map.removeLayer(mapPolygons[id]);
                const freshShape = await fetchLiveIsochrone(geo.lat, geo.lng, partner.radius);
                partner.geoJsonData = freshShape || null;
                delete estimatedCoverage[id];
                if (freshShape) {
                    mapPolygons[id] = L.geoJSON(freshShape, { style: { color, fillColor: color, fillOpacity: 0.1, weight: 1.5, dashArray: '4, 6' } }).addTo(map);
                } else if (isEstimateRange(partner.radius)) {
                    mapPolygons[id] = L.circle([geo.lat, geo.lng], { radius: convertDriveTimeToMeters(partner.radius), color, fillColor: color, fillOpacity: 0.06, weight: 1.5, dashArray: '1, 6' }).addTo(map);
                    estimatedCoverage[id] = true;
                }
                map.panTo([geo.lat, geo.lng]);

                document.getElementById(`display-base-${id}`).innerHTML = escapeHtml(address);
                input.value = address;

                persistPartnersLocal();
                syncPartnerUpdate(partner);

                if (geo.usedFallback) {
                    alert(`Couldn't find that exact street, so the pin was placed near ${geo.fallbackLabel}. Drag it to the exact spot if needed.`);
                }
                btn.textContent = '✅ Reset!';
                setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 1600);
                return;
            } catch (err) {
                alert("Network error while locating that address. Try again.");
            }
            btn.textContent = originalLabel;
            btn.disabled = false;
        };

        window.toggleEditMode = function(id, isEditing) {
            document.getElementById("view-mode-" + id).classList.toggle('hidden', isEditing);
            document.getElementById("edit-mode-" + id).classList.toggle('hidden', !isEditing);
            // Leaving edit mode (Cancel or Save) always relocks the pin, so reposition mode
            // can never stay silently enabled once the panel that exposes it is closed.
            if (!isEditing && repositionState[id]) setPartnerReposition(id, false);
        };

        window.savePartnerEdits = async function(id) {
            const newBase = document.getElementById("edit-base-input-" + id).value.trim();
            const tagsString = document.getElementById("edit-group-input-" + id).value;
            const newRadius = parseInt(document.getElementById("edit-radius-input-" + id).value);
            
            if (!newBase) return alert("Label cannot be empty.");

            const newTags = tagsString.split(',').map(t => t.trim()).filter(t => t !== '');
            const index = savedPartners.findIndex(p => p.id === id);
            
            if (index !== -1) {
                let lat = savedPartners[index].lat;
                let lng = savedPartners[index].lng;
                let forceShapeRefresh = false;

                if (newBase !== savedPartners[index].baseName && !newBase.includes('Manually Repositioned')) {
                    try {
                        const result = await googleGeocode(newBase);
                        if (result) {
                            lat = result.lat;
                            lng = result.lng;
                            forceShapeRefresh = true;
                            map.panTo([lat, lng]);
                        }
                    } catch(e) {}
                }

                if (newRadius !== savedPartners[index].radius || forceShapeRefresh) {
                    savedPartners[index].geoJsonData = null;
                    savedPartners[index].geoJson2Data = null;  // second-base shape scales with the same radius
                }

                savedPartners[index].baseName = newBase;
                savedPartners[index].brokerage = document.getElementById(`edit-brokerage-input-${id}`).value.trim();
                savedPartners[index].phone = document.getElementById(`edit-phone-input-${id}`).value.trim();
                savedPartners[index].email = document.getElementById(`edit-email-input-${id}`).value.trim();
                savedPartners[index].social = document.getElementById(`edit-social-input-${id}`).value.trim();
                savedPartners[index].notes = document.getElementById(`edit-notes-input-${id}`).value.trim();
                savedPartners[index].groups = newTags;
                savedPartners[index].radius = newRadius;
                savedPartners[index].lat = lat;
                savedPartners[index].lng = lng;
                savedPartners[index].updatedAt = Date.now();

                if (mapMarkers[id]) {
                    mapMarkers[id].setLatLng([lat, lng]);
                    mapMarkers[id].setPopupContent(buildPartnerPopup(savedPartners[index]));
                }
                if (mapPolygons[id]) map.removeLayer(mapPolygons[id]);
                
                let freshShape = savedPartners[index].geoJsonData;
                if (!freshShape) {
                    freshShape = await fetchLiveIsochrone(lat, lng, newRadius);
                    savedPartners[index].geoJsonData = freshShape || null;
                }

                persistPartnersLocal();
                syncPartnerUpdate(savedPartners[index]);

                const editColor = getPartnerColor(id);
                delete estimatedCoverage[id];
                if (freshShape) {
                    mapPolygons[id] = L.geoJSON(freshShape, { style: { color: editColor, fillColor: editColor, fillOpacity: 0.1, weight: 1.5, dashArray: '4, 6' } });
                } else if (isEstimateRange(newRadius)) {
                    mapPolygons[id] = L.circle([lat, lng], { radius: convertDriveTimeToMeters(newRadius), color: editColor, fillColor: editColor, fillOpacity: 0.06, weight: 1.5, dashArray: '1, 6' });
                    estimatedCoverage[id] = true;
                }
                if (mapPolygons[id] && !hiddenPartnersState[id]) mapPolygons[id].addTo(map);

                renderPartnerSecondBase(savedPartners[index]);
                buildSidebarCard(savedPartners[index]);
                updateGroupFilters();
            }
        };

        // IDs deleted this session, kept for the session's lifetime (never pruned - a real
        // partner id is a UUID and is never reused, so there's no future point where seeing this
        // id again would legitimately mean a different, new colleague). Exists because
        // checkForNewPartners() polls the server every 90s / on tab refocus, and if that poll's
        // request is already in flight when a delete happens, its response can still contain the
        // colleague who was just removed - and since a colleague the owner added themselves (as
        // opposed to one who reciprocated) was never marked "seen", the poll had no way to tell
        // "just deleted" apart from "genuinely new" and would recreate their marker. This is the
        // exact bug behind "deleted her, she still shows on the map but not in the list" - the
        // stray marker came from a re-render the poll triggered after the delete had already
        // cleared the sidebar card, and deletePartner below left the old mapMarkers/mapPolygons
        // *references* behind even though the layers were removed from the map, so the objects
        // were still live enough for that stray re-render to hang new state off of.
        const recentlyDeletedPartnerIds = new Set();

        window.deletePartner = function(id) {
            recentlyDeletedPartnerIds.add(id);
            [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                if (store[id]) { map.removeLayer(store[id]); delete store[id]; }
            });
            const card = document.getElementById("sidebar-item-" + id);
            if (card) card.remove();
            savedPartners = savedPartners.filter(p => p.id !== id);
            persistPartnersLocal();
            syncPartnerDelete(id);
            updateGroupFilters();
            updatePartnerListEmptyState();
        };

        // --- BULK SELECT / DELETE: added after a large CSV import left no easy way back out -
        // removing more than one colleague meant opening each card individually. ---
        window.toggleSelectMode = function() {
            selectModeActive = !selectModeActive;
            document.getElementById('partnerList').classList.toggle('select-mode', selectModeActive);
            document.getElementById('toggleSelectModeBtn').textContent = selectModeActive ? '✕ Cancel' : '☑ Select';
            if (!selectModeActive) {
                selectedPartnerIds.clear();
                document.querySelectorAll('#partnerList .partner-select-cb').forEach(cb => cb.checked = false);
            }
            updateBulkSelectionUI();
        };

        window.togglePartnerSelection = function(id, checked) {
            if (checked) selectedPartnerIds.add(id); else selectedPartnerIds.delete(id);
            updateBulkSelectionUI();
        };

        function updateBulkSelectionUI() {
            const n = selectedPartnerIds.size;
            document.getElementById('bulkActionBar').classList.toggle('hidden', !selectModeActive);
            document.getElementById('bulkActionBar').classList.toggle('flex', selectModeActive);
            document.getElementById('bulkSelectedCount').textContent = `${n} selected`;
            document.getElementById('bulkDeleteBtn').disabled = n === 0;
            // Merging only makes sense pairwise - showing it for any other count would just invite
            // "why doesn't this work" for 1 or 3+ selected.
            document.getElementById('bulkMergeBtn').classList.toggle('hidden', n !== 2);
        }

        // Selects/clears only what's currently visible in the list - respects whatever the
        // search box or tag filters have narrowed it down to, same as "delete what I'm looking
        // at" would suggest, rather than silently reaching past a filter to hidden colleagues.
        window.selectAllPartners = function() {
            document.querySelectorAll('#partnerList li[data-partner-id]:not(.hidden) .partner-select-cb').forEach(cb => {
                cb.checked = true;
                selectedPartnerIds.add(cb.dataset.partnerId);
            });
            updateBulkSelectionUI();
        };

        window.selectNonePartners = function() {
            selectedPartnerIds.clear();
            document.querySelectorAll('#partnerList .partner-select-cb').forEach(cb => cb.checked = false);
            updateBulkSelectionUI();
        };

        window.bulkDeleteSelectedPartners = function() {
            const ids = [...selectedPartnerIds];
            if (!ids.length) return;
            const names = ids.map(id => savedPartners.find(p => p.id === id)?.name).filter(Boolean);
            const preview = names.slice(0, 5).join(', ') + (names.length > 5 ? `, and ${names.length - 5} more` : '');
            if (!confirm(`Remove ${ids.length} colleague${ids.length === 1 ? '' : 's'}?\n\n${preview}\n\nThis can't be undone.`)) return;

            ids.forEach(id => deletePartner(id));
            toggleSelectMode();  // exits select mode, clears the now-stale selection, hides the bar
            setShareBanner(`🗑️ Removed ${ids.length} colleague${ids.length === 1 ? '' : 's'}.`, 'gray');
        };

        // --- MERGE: two saved records for the same person - a real CSV import produced exactly
        // this ("Wayne Gabriel" showed up twice) - can be combined into one instead of picking a
        // winner and deleting the other outright and losing whatever only the loser had right.
        // Field-by-field choice rather than "newest wins" because there's no reliable way to know
        // which of two duplicates has the better phone number, notes, etc. ---
        let mergeCandidateIds = [];
        // MERGE_SCALAR_FIELDS now lives in lib/pure.js, shared with mergePartners() there.

        function mergeFieldRow(label, key, valA, valB) {
            const preferB = !valA && !!valB;  // default to whichever side is non-empty; A wins if both are (or neither is)
            const show = v => (v === null || v === undefined || v === '') ? '<span class="opacity-40 italic">(empty)</span>' : escapeHtml(String(v));
            return `
                <div class="text-[11px]">
                    <div class="font-bold opacity-70 mb-1">${escapeHtml(label)}</div>
                    <label class="flex items-start gap-1.5 mb-1 cursor-pointer">
                        <input type="radio" name="merge-${key}" value="a" ${!preferB ? 'checked' : ''} class="mt-0.5 flex-shrink-0" />
                        <span class="flex-1 break-words">${show(valA)}</span>
                    </label>
                    <label class="flex items-start gap-1.5 cursor-pointer">
                        <input type="radio" name="merge-${key}" value="b" ${preferB ? 'checked' : ''} class="mt-0.5 flex-shrink-0" />
                        <span class="flex-1 break-words">${show(valB)}</span>
                    </label>
                </div>`;
        }

        window.openMergeModal = function() {
            const ids = [...selectedPartnerIds];
            if (ids.length !== 2) return;
            const [a, b] = ids.map(id => savedPartners.find(p => p.id === id));
            if (!a || !b) return;
            mergeCandidateIds = ids;

            let html = `<div class="text-[10px] opacity-70 mb-1">Combining <strong>${escapeHtml(a.name)}</strong> and <strong>${escapeHtml(b.name)}</strong> into one colleague.</div>`;
            MERGE_SCALAR_FIELDS.forEach(([key, label]) => { html += mergeFieldRow(label, key, a[key], b[key]); });
            html += mergeFieldRow('Address', 'address', a.baseName, b.baseName);
            html += mergeFieldRow('Second Base', 'base2', a.base2Name, b.base2Name);

            const combinedTags = [...new Set([...(a.groups || []), ...(b.groups || [])])];
            html += `<div class="text-[11px]"><div class="font-bold opacity-70 mb-1">Tags</div><div class="opacity-80">${combinedTags.length ? combinedTags.map(t => escapeHtml(t)).join(', ') : '<span class="opacity-40 italic">(none)</span>'} <span class="opacity-50 italic">— combined automatically, not a choice</span></div></div>`;

            document.getElementById('mergeFieldsContainer').innerHTML = html;
            const modal = document.getElementById('mergePartnersModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
        };

        window.closeMergeModal = function() {
            const modal = document.getElementById('mergePartnersModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
            mergeCandidateIds = [];
        };

        window.confirmMergePartners = async function() {
            const [a, b] = mergeCandidateIds.map(id => savedPartners.find(p => p.id === id));
            if (!a || !b) { closeMergeModal(); return; }

            const pick = (key) => {
                const sel = document.querySelector(`input[name="merge-${key}"]:checked`);
                return (sel && sel.value === 'b') ? 'b' : 'a';
            };
            // Reading which radio is checked stays here (needs the DOM); the actual field-by-field
            // combination logic is mergePartners() in lib/pure.js, directly unit-tested there.
            const choices = { address: pick('address'), base2: pick('base2') };
            MERGE_SCALAR_FIELDS.forEach(([key]) => { choices[key] = pick(key); });

            const merged = mergePartners(a, b, choices);
            const keepId = merged.id;
            const dropId = b.id;

            const index = savedPartners.findIndex(p => p.id === keepId);
            if (index === -1) { closeMergeModal(); return; }
            savedPartners[index] = merged;

            [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                if (store[keepId]) { map.removeLayer(store[keepId]); delete store[keepId]; }
            });

            closeMergeModal();
            if (selectModeActive) toggleSelectMode();

            await renderPartnerAndCoverage(merged);
            persistPartnersLocal();
            syncPartnerUpdate(merged);
            deletePartner(dropId);

            setShareBanner(`🔀 Merged into ${escapeHtml(merged.name)}.`, 'emerald');
        };

        // Not tags - these filter on how a colleague arrived. Kept out of the tag namespace with a
        // prefix no real tag would ever carry.
        const SELF_ADDED_FILTER = '__self_added__';
        const CONNECTED_FILTER = '__connected__';
        const isSelfAdded = p => p.source === 'reciprocated';
        const isConnected = p => p.source === 'reciprocated' || !!p.linkedUserId;

        function partnerMatchesActiveFilter(partner) {
            if (activeGroupFilter === 'ALL') return true;
            if (activeGroupFilter === SELF_ADDED_FILTER) return isSelfAdded(partner);
            if (activeGroupFilter === CONNECTED_FILTER) return isConnected(partner);
            return (partner.groups || []).includes(activeGroupFilter);
        }

        let tagSearchTerm = '';
        document.getElementById('tagSearchInput').addEventListener('input', (e) => {
            tagSearchTerm = e.target.value.toLowerCase();
            updateGroupFilters();
        });

        function updateGroupFilters() {
            const filterBar = document.getElementById('groupFilterBar');
            let allTags = [];
            savedPartners.forEach(p => { if (p.groups) allTags = allTags.concat(p.groups); });
            let tags = [...new Set(allTags)].filter(t => t && t.trim() !== '');
            if (tagSearchTerm) tags = tags.filter(t => t.toLowerCase().includes(tagSearchTerm));

            const btnBaseClass = "text-[9px] px-2 py-1 rounded-md border font-bold transition duration-150 active:scale-95 shadow-sm uppercase tracking-wide font-mono select-none";

            filterBar.innerHTML = `<button data-tag="ALL" title="Show every colleague" class="${btnBaseClass} ${activeGroupFilter === 'ALL' ? 'bg-blue-600 border-blue-500 text-white shadow-blue-900/30' : 'bg-gray-200 dark:bg-gray-900 border-gray-300 dark:border-gray-800 text-current opacity-70'}">All</button>`;

            // Always shown, count included even at zero - "has anyone added me?" deserves a
            // straight answer, and a chip that only appears once the answer is yes can't give one.
            const arrivalChips = [
                [SELF_ADDED_FILTER, '🔁 Added Themselves', savedPartners.filter(isSelfAdded).length, 'Colleagues who added themselves to your network from your share link or QR code'],
                [CONNECTED_FILTER, '🔗 Connected', savedPartners.filter(isConnected).length, "Colleagues who are on MapMyReferrals and linked back to you - including anyone who added themselves"]
            ];
            arrivalChips.forEach(([key, label, count, title]) => {
                const isActive = activeGroupFilter === key;
                filterBar.innerHTML += `<button data-tag="${key}" title="${escapeHtml(title)}" class="${btnBaseClass} ${isActive ? 'bg-indigo-600 border-indigo-500 text-white shadow-indigo-900/30' : 'bg-indigo-500/10 border-indigo-500/30 text-indigo-500 dark:text-indigo-300'}">${label} ${count}</button>`;
            });

            tags.forEach(tag => {
                const isActive = activeGroupFilter === tag;
                filterBar.innerHTML += `<button data-tag="${escapeHtml(tag)}" title="Show only colleagues tagged '${escapeHtml(tag)}'" class="${btnBaseClass} ${isActive ? 'bg-blue-600 border-blue-500 text-white shadow-blue-900/30' : 'bg-gray-200 dark:bg-gray-900 border-gray-300 dark:border-gray-800 text-current opacity-70'} truncate max-w-[100px]">${escapeHtml(tag)}</button>`;
            });
        }

        window.filterGroup = function(tag) {
            activeGroupFilter = tag;
            updateGroupFilters();
            applyFilters();
        };

        // Delegated (not inline onclick) so a tag containing a quote can't break out of an
        // inline event-handler attribute - filterBar's children are fully replaced on every
        // updateGroupFilters() call, but the container itself persists, so one listener suffices.
        document.getElementById('groupFilterBar').addEventListener('click', (e) => {
            const btn = e.target.closest('button[data-tag]');
            if (btn) filterGroup(btn.dataset.tag);
        });

        function applyFilters() {
            let visibleCount = 0;
            savedPartners.forEach(partner => {
                const pId = partner.id;
                try {
                    const card = document.getElementById(`sidebar-item-${pId}`);
                    if (card) {
                        const partnerTags = partner.groups || [];
                        const matchesTag = partnerMatchesActiveFilter(partner);

                        // The badge words are searchable too - typing "added themselves" or
                        // "connected" finds exactly who the card badges say it would.
                        const arrivalWords = `${isSelfAdded(partner) ? 'added themselves self-added reciprocated' : ''} ${isConnected(partner) ? 'connected' : ''}`;
                        const searchContent = `${partner.name} ${partner.baseName} ${partner.brokerage || ''} ${partner.notes || ''} ${partnerTags.join(' ')} ${arrivalWords}`.toLowerCase();
                        const matchesSearch = searchContent.includes(textSearchTerm);

                        if (matchesTag && matchesSearch) {
                            visibleCount++;
                            card.classList.remove('hidden');
                            if (!hiddenPartnersState[pId]) {
                                if (mapMarkers[pId]) mapMarkers[pId].addTo(map);
                                if (mapPolygons[pId]) mapPolygons[pId].addTo(map);
                                if (mapMarkers2[pId]) mapMarkers2[pId].addTo(map);
                                if (mapPolygons2[pId]) mapPolygons2[pId].addTo(map);
                            }
                        } else {
                            card.classList.add('hidden');
                            if (mapMarkers[pId]) map.removeLayer(mapMarkers[pId]);
                            if (mapPolygons[pId]) map.removeLayer(mapPolygons[pId]);
                            if (mapMarkers2[pId]) map.removeLayer(mapMarkers2[pId]);
                            if (mapPolygons2[pId]) map.removeLayer(mapPolygons2[pId]);
                        }
                    }
                } catch (err) {
                    // One malformed partner record must never block the rest of the list from
                    // being filterable/searchable - log and move on instead of aborting the loop.
                    console.error(`applyFilters failed for partner ${pId}`, err);
                }
            });
            updateNetworkCount(visibleCount);
        }

        // "8" normally, "3 of 8" while a filter or search is narrowing the list - plus a line in
        // the list itself when nothing matches, so an empty result reads as an answer rather than
        // as the list failing to load.
        function updateNetworkCount(visibleCount) {
            const total = savedPartners.length;
            const filtering = activeGroupFilter !== 'ALL' || !!textSearchTerm;
            const badge = document.getElementById('networkCountBadge');
            badge.classList.toggle('hidden', total === 0);
            badge.textContent = filtering ? `${visibleCount} of ${total}` : String(total);

            const noMatches = document.getElementById('partnerListNoMatches');
            const showNoMatches = total > 0 && filtering && visibleCount === 0;
            noMatches.classList.toggle('hidden', !showNoMatches);
            if (showNoMatches) {
                noMatches.textContent = activeGroupFilter === SELF_ADDED_FILTER && !textSearchTerm
                    ? "No one has added themselves yet. When someone opens your share link or scans your QR code and taps \"Share My Info\", they'll land here - and at the top of \"Newest added\"."
                    : 'No colleagues match this filter.';
            }
        }

        // Reports once, after every colleague's initial render has settled, if any ended up with
        // no coverage shape at all - almost always the map provider's own rate/quota limit (see
        // fetchLiveIsochrone's throttle+retry above), not a real per-address failure. Left silent
        // on success; only speaks up when there's something to explain, so a normal load stays
        // quiet. Skipped if something else (e.g. a share-link import) already has the banner -
        // that message matters more right now than this one.
        let pendingShapeLoads = savedPartners.length;
        let shapeLoadFailures = 0;
        savedPartners.forEach(partner => {
            renderPartnerAndCoverage(partner)
                .then(result => { if (result && result.shapeFailed) shapeLoadFailures++; })
                .catch(err => console.error(`renderPartnerAndCoverage failed for partner ${partner.id}`, err))
                .finally(() => {
                    pendingShapeLoads--;
                    if (pendingShapeLoads > 0 || shapeLoadFailures === 0) return;
                    const banner = document.getElementById('shareCardBanner');
                    if (!banner.classList.contains('hidden')) return;
                    const plural = shapeLoadFailures === 1 ? "colleague's coverage shape" : "colleagues' coverage shapes";
                    setShareBanner(`🗺️ ${shapeLoadFailures} ${plural} couldn't load right now (map provider limit reached) — they'll fill in automatically on a later visit once that resets. Nothing you need to do.`, 'amber');
                });
        });
        updatePartnerListEmptyState();

        // --- NEW CONTACT NOTIFICATIONS: this is a single-load app - the partner list is
        // fetched once at boot and never polled, so a colleague reciprocating your share link
        // was otherwise invisible until you happened to reload. Diffs against a "seen" set in
        // localStorage and re-checks on tab focus, so a new contact surfaces on its own. ---
        const SEEN_PARTNERS_KEY = 'seen_partner_ids_v1';
        function getSeenPartnerIds() {
            try { return new Set(JSON.parse(localStorage.getItem(SEEN_PARTNERS_KEY) || '[]')); }
            catch (err) { return new Set(); }
        }
        function markPartnersSeen(ids) {
            const seen = getSeenPartnerIds();
            ids.forEach(id => seen.add(id));
            localStorage.setItem(SEEN_PARTNERS_KEY, JSON.stringify([...seen]));
        }
        // First run of this feature for an existing account: seed from what's already there
        // instead of announcing every existing colleague as "new".
        if (isLoggedIn && localStorage.getItem(SEEN_PARTNERS_KEY) === null) {
            markPartnersSeen(savedPartners.map(p => p.id));
        }

        // Everything a colleague's card actually displays, as one comparable string.
        //
        // Cached isochrone geometry is deliberately excluded. It's a derived cache, and the server
        // stores null for any polygon over the 50KB cap - so including it would make those
        // colleagues look changed on every single poll: re-render, refetch the shape, re-cache,
        // round again every 90 seconds.
        //
        // Comparing content rather than an updated_at timestamp also avoids an echo: this client's
        // own edits bump the server's timestamp, so a timestamp check would flag every colleague
        // the user had just edited as having been changed remotely.
        //
        // partnerContentSignature now lives in lib/pure.js.

        async function checkForNewPartners() {
            if (!isLoggedIn) return;
            try {
                const res = await fetch('/api/partners');
                if (!res.ok) return;
                const serverPartners = await res.json();

                const seen = getSeenPartnerIds();
                // recentlyDeletedPartnerIds guards against a request that was already in flight
                // when a delete happened - see the comment on deletePartner() for the full story.
                const newOnes = serverPartners.filter(p => !seen.has(p.id) && !savedPartners.some(sp => sp.id === p.id) && !recentlyDeletedPartnerIds.has(p.id));

                // Colleagues edited somewhere else - another device, or a reciprocated update
                // accepted on a phone. Their id is already known, so the new-colleague check above
                // skips them by design, and this session went on showing stale details (an old
                // address, a corrected phone number) until someone happened to reload.
                const changed = [];
                serverPartners.forEach(incoming => {
                    const localIndex = savedPartners.findIndex(lp => lp.id === incoming.id);
                    if (localIndex === -1) return;
                    const local = savedPartners[localIndex];
                    if (partnerContentSignature(local) === partnerContentSignature(incoming)) return;

                    // Never yank a card out from under someone who's mid-edit - their unsaved
                    // typing would vanish. It'll refresh on a later pass once they're done.
                    const editPanel = document.getElementById(`edit-mode-${incoming.id}`);
                    if (editPanel && !editPanel.classList.contains('hidden')) return;

                    // Keep a locally cached shape when the base and range didn't move: the server
                    // holds null for over-cap polygons, and refetching one on every unrelated edit
                    // is pure waste.
                    if (!incoming.geoJsonData && local.geoJsonData && incoming.lat === local.lat && incoming.lng === local.lng && incoming.radius === local.radius) {
                        incoming.geoJsonData = local.geoJsonData;
                    }
                    if (!incoming.geoJson2Data && local.geoJson2Data && incoming.lat2 === local.lat2 && incoming.lng2 === local.lng2 && incoming.radius === local.radius) {
                        incoming.geoJson2Data = local.geoJson2Data;
                    }

                    savedPartners[localIndex] = incoming;
                    changed.push(incoming);
                });

                if (newOnes.length === 0 && changed.length === 0) return;

                changed.forEach(p => {
                    // renderPartnerAndCoverage resets visibility, so a colleague the user had
                    // hidden with the 👁 toggle would silently reappear - put that back afterwards.
                    const wasHidden = !!hiddenPartnersState[p.id];
                    [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                        if (store[p.id]) { map.removeLayer(store[p.id]); delete store[p.id]; }
                    });
                    renderPartnerAndCoverage(p)
                        .then(() => { if (wasHidden) toggleSinglePartnerVisibility(p.id); })
                        .catch(err => console.error(`renderPartnerAndCoverage failed for partner ${p.id}`, err));
                });

                newOnes.forEach(p => {
                    savedPartners.push(p);
                    renderPartnerAndCoverage(p).catch(err => console.error(`renderPartnerAndCoverage failed for partner ${p.id}`, err));
                });
                updatePartnerListEmptyState();
                persistPartnersLocal();
                if (newOnes.length) markPartnersSeen(newOnes.map(p => p.id));

                if (newOnes.length === 1) {
                    const p = newOnes[0];
                    const verb = p.source === 'reciprocated' ? 'added themselves to' : 'just joined';
                    setShareBanner(`🎉 ${p.name} ${verb} your network!`, 'emerald');
                } else if (newOnes.length > 1) {
                    setShareBanner(`🎉 ${newOnes.length} new colleagues joined your network!`, 'emerald');
                } else {
                    setShareBanner(changed.length === 1
                        ? `🔄 ${changed[0].name}'s details were updated.`
                        : `🔄 ${changed.length} colleagues' details were updated.`, 'blue');
                }

                // Only a genuinely new colleague is worth forcing the panel open for. A quiet
                // detail refresh shouldn't reopen a sidebar somebody deliberately collapsed.
                if (newOnes.length) revealSidebar();
            } catch (err) { /* silent - background convenience check, not worth alarming over */ }
        }

        // Re-check on tab refocus AND on a timer.
        //
        // The refocus trigger alone quietly assumed every session gets backgrounded sooner or
        // later. A desktop tab that simply stays visible - second monitor, its own window, the way
        // people actually leave this open while working - never fires visibilitychange at all, so
        // the only check it ever ran was the one at page load. That's how a pending update showed
        // up instantly on a phone and never appeared on a desktop left open the whole time: the
        // desktop wasn't failing to render it, it was never asking for it again.
        //
        // The 30s throttle still guards rapid tab-switching; the interval is well clear of it.
        const BACKGROUND_CHECK_MS = 90000;
        let lastPartnerCheck = Date.now();
        function runBackgroundChecks() {
            if (!isLoggedIn) return;
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastPartnerCheck < 30000) return;
            lastPartnerCheck = Date.now();
            checkForNewPartners();
            loadPendingUpdates();
        }
        document.addEventListener('visibilitychange', runBackgroundChecks);
        setInterval(runBackgroundChecks, BACKGROUND_CHECK_MS);

        // --- PENDING RECIPROCATED UPDATES ---
        // When a colleague reciprocates but you already have a card for them under the same name,
        // the server parks their info instead of overwriting your card (you're not there to
        // approve a merge) - see functions/api/reciprocate.js. It used to be dropped outright,
        // which meant a hand-typed card could keep a wrong phone/email/address forever while the
        // sender was told they were "already in your network". Now it surfaces here as a diff.
        //
        // Note this deliberately does NOT ride on checkForNewPartners() above: that only fires on
        // a brand-new partner row appearing, and a pending update by definition creates no new row.
        const PENDING_DIFF_FIELDS = [
            ['baseName', 'Address'],
            ['phone', 'Phone'],
            ['email', 'Email'],
            ['brokerage', 'Brokerage'],
            ['social', 'Social'],
            ['notes', 'Notes'],
            ['radius', 'Drive-time range'],
            ['groups', 'Tags'],
            ['photoUrl', 'Photo']
        ];

        // Only fields the payload actually offers AND that differ from what's saved - mirrors the
        // server's merge rules, so the prompt can't advertise a change the merge wouldn't make.
        // Each entry is independently selectable: a colleague's payload routinely mixes a
        // correction you want with one you don't (their tagline arriving in the tags you filter
        // your own map by), and a single yes/no on the whole thing makes the good half unusable.
        function pendingFieldDiff(current, incoming) {
            const diffs = [];
            PENDING_DIFF_FIELDS.forEach(([key, label]) => {
                let from = current[key];
                let to = incoming[key];
                if (key === 'groups') {
                    from = (Array.isArray(from) ? from : []).join(', ');
                    to = (Array.isArray(to) ? to : []).join(', ');
                }
                if (to === undefined || to === null || to === '') return;
                if (String(from == null ? '' : from) === String(to)) return;
                diffs.push({
                    key,
                    label,
                    from: String(from == null ? '' : from).trim(),
                    to: String(to),
                    // Photos can't be judged as text - this row renders as before/after thumbnails.
                    isPhoto: key === 'photoUrl'
                });
            });
            return diffs;
        }

        async function loadPendingUpdates() {
            if (!isLoggedIn) return;
            try {
                const res = await fetch('/api/pending-updates');
                if (!res.ok) return;
                renderPendingUpdates(await res.json());
            } catch (err) { /* background check - the prompt just won't appear this load */ }
        }

        // Which updates were on screen last time. Now that the checks run on a timer rather than
        // only on refocus, re-revealing a collapsed sidebar every cycle would drag it back open
        // every 90 seconds for as long as someone left an update unanswered - so the panel is only
        // forced into view when the set actually changes, not on every re-render.
        let lastPendingSignature = '';

        function renderPendingUpdates(pending) {
            const container = document.getElementById('pendingUpdatesPrompt');
            const signature = (pending || []).map(p => p.id).sort().join(',');
            const isNewToThisSession = signature && signature !== lastPendingSignature;
            lastPendingSignature = signature;

            if (!pending || pending.length === 0) {
                container.innerHTML = '';
                container.classList.add('hidden');
                return;
            }

            container.innerHTML = pending.map(item => {
                const diffs = pendingFieldDiff(item.current, item.incoming);

                const rows = diffs.map(d => {
                    const boxId = `pending-field-${item.id}-${d.key}`;
                    const body = d.isPhoto
                        ? `<span class="inline-flex items-center gap-1.5 align-middle">
                               ${d.from ? `<img src="${sanitizeUrl(d.from)}" alt="Current photo" class="w-7 h-7 rounded-full object-cover opacity-50 border border-current/30" /> &rarr;` : ''}
                               <img src="${sanitizeUrl(d.to)}" alt="Photo they sent" class="w-7 h-7 rounded-full object-cover border border-current/30" />
                           </span>`
                        : `${d.from ? `<span class="line-through opacity-50">${escapeHtml(d.from)}</span> &rarr; ` : ''}<span>${escapeHtml(d.to)}</span>`;
                    return `
                        <li class="flex items-start gap-2">
                            <input type="checkbox" id="${boxId}" data-pending-id="${item.id}" data-pending-field="${d.key}" checked
                                   class="mt-0.5 flex-shrink-0 accent-amber-500 cursor-pointer" />
                            <label for="${boxId}" class="leading-snug cursor-pointer">
                                <span class="font-bold">${escapeHtml(d.label)}:</span> ${body}
                            </label>
                        </li>`;
                }).join('');

                // Nothing differs: there's no merge to offer, only an acknowledgement. Showing
                // an "apply" button here would imply a change that wouldn't happen.
                const actions = diffs.length
                    ? `<div class="flex space-x-1.5">
                           <button data-action="applyPendingUpdate" data-arg0="${item.id}" class="flex-1 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-bold py-1.5 rounded-lg transition-all active:scale-95">Use Checked</button>
                           <button data-action="dismissPendingUpdate" data-arg0="${item.id}" class="flex-1 bg-black/10 dark:bg-gray-800 hover:bg-black/20 dark:hover:bg-gray-700 text-current text-[10px] font-bold py-1.5 rounded-lg transition-all active:scale-95 border border-gray-300/30 dark:border-gray-700">Keep Mine</button>
                       </div>`
                    : `<button data-action="dismissPendingUpdate" data-arg0="${item.id}" class="w-full bg-black/10 dark:bg-gray-800 hover:bg-black/20 dark:hover:bg-gray-700 text-current text-[10px] font-bold py-1.5 rounded-lg transition-all active:scale-95 border border-gray-300/30 dark:border-gray-700">Got It</button>`;

                return `
                    <div class="text-[11px] font-semibold leading-relaxed p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">
                        <p class="mb-1.5">🔁 <strong>${escapeHtml(item.current.name)}</strong> sent their own info back, and you already had a card for them. Nothing changed yet — untick anything you'd rather keep as-is:</p>
                        <ul class="space-y-1.5 mb-2 pl-0.5">${rows || '<li class="opacity-60 italic">Everything they sent already matches your card.</li>'}</ul>
                        ${actions}
                    </div>`;
            }).join('');

            container.classList.remove('hidden');
            if (isNewToThisSession) revealSidebar();
        }

        window.applyPendingUpdate = async function(id) {
            const fields = [...document.querySelectorAll(`input[data-pending-id="${id}"]:checked`)]
                .map(el => el.dataset.pendingField);
            if (fields.length === 0) {
                setShareBanner('ℹ️ Nothing is checked — tick at least one field, or choose "Keep Mine".', 'amber');
                return;
            }
            try {
                const res = await apiPost(`/api/pending-updates/${id}`, { fields });
                if (!res.ok) {
                    setShareBanner(`⚠️ ${(await res.text()) || "Couldn't apply that update - try again."}`, 'amber');
                    loadPendingUpdates();
                    return;
                }
                const updated = await res.json();

                const index = savedPartners.findIndex(p => p.id === updated.id);
                if (index !== -1) {
                    savedPartners[index] = updated;
                    persistPartnersLocal();

                    // Full re-render rather than patching in place: the merge can move their base,
                    // which changes the pin, the coverage shape and the second-base marker at once.
                    [mapMarkers, mapPolygons, mapMarkers2, mapPolygons2].forEach(store => {
                        if (store[updated.id]) {
                            map.removeLayer(store[updated.id]);
                            delete store[updated.id];
                        }
                    });
                    await renderPartnerAndCoverage(updated);
                }

                setShareBanner(`✅ ${updated.name}'s card updated with the ${fields.length === 1 ? 'detail' : fields.length + ' details'} you checked.`, 'emerald');
                loadPendingUpdates();
            } catch (err) {
                setShareBanner('⚠️ Network error applying that update - try again later.', 'amber');
            }
        };

        window.dismissPendingUpdate = async function(id) {
            try {
                await fetch(`/api/pending-updates/${id}`, { method: 'DELETE', headers: { 'X-Requested-With': 'fetch' } });
            } catch (err) { /* reload below either way - a failed dismiss just leaves the prompt up */ }
            loadPendingUpdates();
        };

        loadPendingUpdates();

        // --- REFERRAL PARTNER LOCATOR ENGINE ---
        document.getElementById('searchLocationBtn').addEventListener('click', async () => {
            const searchAddress = document.getElementById('searchLocationInput').value.trim();
            if (!searchAddress) return alert("Specify an address to run lookups.");

            const feedback = document.getElementById('searchFeedback');
            const clearBtn = document.getElementById('clearSearchBtn');

            try {
                const result = await googleGeocode(searchAddress);
                if (!result) return alert("Address lookup failure.");

                const clientPoint = L.latLng(result.lat, result.lng);

                if (searchTargetMarker) map.removeLayer(searchTargetMarker);
                searchTargetMarker = L.marker(clientPoint, {
                    icon: L.divIcon({
                        html: `<div class="relative flex items-center justify-center"><div class="absolute w-10 h-10 bg-red-500/20 rounded-full animate-ping"></div><div class="bg-gradient-to-br from-red-500 to-pink-600 text-white p-2 rounded-full shadow-2xl border border-red-400 text-sm font-bold z-50 shadow-red-500/40">🎯</div></div>`,
                        iconSize: [36, 36], iconAnchor: [18, 18]
                    })
                }).addTo(map).bindPopup("<b class='text-gray-900'>Lead:</b><br><span class='text-gray-700 text-xs'>" + escapeHtml(searchAddress) + "</span>").openPopup();

                setViewBesideSidebar(clientPoint, 10);
                lastLeadAddress = searchAddress;

                // --- RANKED MATCH + COVERAGE GAP ANALYSIS ---
                // Unlike a plain in/out check, this ranks every matching partner by actual
                // distance to the lead, and surfaces a clear "coverage gap" when nobody covers it.
                const evaluated = [];

                // Ray-cast point-in-polygon against a rendered coverage layer
                const polyContains = (layerGroup) => {
                    const layer = layerGroup.getLayers()[0];
                    const polygonCoords = layer.getLatLngs()[0];
                    let x = clientPoint.lat, y = clientPoint.lng;
                    let vs = polygonCoords.map(pt => [pt.lat, pt.lng]);
                    let inside = false;
                    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
                        let xi = vs[i][0], yi = vs[i][1];
                        let xj = vs[j][0], yj = vs[j][1];
                        let intersect = ((yi > y) != (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
                        if (intersect) inside = !inside;
                    }
                    return inside;
                };

                savedPartners.forEach(partner => {
                    const cardElement = document.getElementById("sidebar-item-" + partner.id);
                    if (!cardElement) return;

                    // A lead is "in range" if EITHER of the partner's bases covers it.
                    //
                    // Three states, not two. Without a measured shape the honest answer is "we
                    // don't know" - not "no", and emphatically not the straight-line radius check
                    // that used to sit here. Deciding who receives a real client from a crow-flies
                    // guess is the worst place in this whole app to approximate, and it looked
                    // identical to a real match in the results.
                    const checkBase = (polyLayer, shape, lat, lng) => {
                        if (polyLayer && shape) {
                            try { return polyContains(polyLayer) ? 'in' : 'out'; }
                            catch (err) { return 'unknown'; }
                        }
                        // Past 60 minutes there is no measured shape to test against, only the
                        // estimated circle. Answer from it, but under its own label so a
                        // straight-line guess is never counted as a confirmed match.
                        if (estimatedCoverage[partner.id] && lat != null && lng != null) {
                            return L.latLng(lat, lng).distanceTo(clientPoint) <= convertDriveTimeToMeters(partner.radius)
                                ? 'in-estimated' : 'out';
                        }
                        return 'unknown';
                    };

                    // Best evidence wins: a measured hit beats an estimated one, which beats not
                    // knowing, which beats a measured miss.
                    const confidence = { 'in': 3, 'in-estimated': 2, 'unknown': 1, 'out': 0 };
                    let coverage = checkBase(mapPolygons[partner.id], partner.geoJsonData, partner.lat, partner.lng);
                    if (coverage !== 'in' && partner.lat2 != null && partner.lng2 != null) {
                        const second = checkBase(mapPolygons2[partner.id], partner.geoJson2Data, partner.lat2, partner.lng2);
                        if (confidence[second] > confidence[coverage]) coverage = second;
                    }

                    // Rank by whichever base is closer to the lead
                    let distanceMiles = L.latLng(partner.lat, partner.lng).distanceTo(clientPoint) / 1609.34;
                    if (partner.lat2 != null && partner.lng2 != null) {
                        distanceMiles = Math.min(distanceMiles, L.latLng(partner.lat2, partner.lng2).distanceTo(clientPoint) / 1609.34);
                    }
                    evaluated.push({ partner, cardElement, coverage, distanceMiles });
                });

                const byDistance = (a, b) => a.distanceMiles - b.distanceMiles;
                const matched = evaluated.filter(r => r.coverage === 'in').sort(byDistance);
                const estimated = evaluated.filter(r => r.coverage === 'in-estimated').sort(byDistance);
                const unchecked = evaluated.filter(r => r.coverage === 'unknown').sort(byDistance);
                const unmatched = evaluated.filter(r => r.coverage === 'out');
                const rankColors = ['#f59e0b', '#94a3b8', '#b45309'];

                matched.forEach((r, idx) => {
                    const badge = document.getElementById(`rank-badge-${r.partner.id}`);
                    const distLabel = document.getElementById(`distance-label-${r.partner.id}`);
                    r.cardElement.classList.remove('opacity-20', 'bg-gray-900/60', 'border-gray-800/80');
                    r.cardElement.classList.add('bg-emerald-500/10', 'border-emerald-500/40', 'ring-1', 'ring-emerald-500/20', 'shadow-lg');
                    if (badge) {
                        badge.textContent = `#${idx + 1}`;
                        badge.style.background = idx < 3 ? rankColors[idx] : '#475569';
                        badge.classList.remove('hidden');
                    }
                    if (distLabel) {
                        distLabel.textContent = `📍 ${r.distanceMiles.toFixed(1)} mi from lead`;
                        distLabel.classList.remove('hidden');
                    }
                });

                // Matched against an estimated circle rather than a measured shape. Ranked below
                // every confirmed match and labelled ≈, so the distinction survives into the
                // moment someone actually picks who to send the client to.
                estimated.forEach(r => {
                    r.cardElement.classList.remove('opacity-20', 'bg-gray-900/60', 'border-gray-800/80');
                    r.cardElement.classList.add('bg-amber-500/10', 'border-amber-500/40', 'ring-1', 'ring-amber-500/20');
                    const badge = document.getElementById(`rank-badge-${r.partner.id}`);
                    const distLabel = document.getElementById(`distance-label-${r.partner.id}`);
                    if (badge) badge.classList.add('hidden');
                    if (distLabel) {
                        distLabel.textContent = `≈ estimated match — ${r.distanceMiles.toFixed(1)} mi (over 1 hr, not measured)`;
                        distLabel.classList.remove('hidden');
                    }
                });

                // Couldn't be checked - shown in amber, NOT dimmed away with the misses. Burying
                // these would quietly turn "unknown" into "no", which is the same false certainty
                // the straight-line fallback used to produce, just in the other direction.
                unchecked.forEach(r => {
                    r.cardElement.classList.remove('opacity-20', 'bg-emerald-500/10', 'border-emerald-500/40', 'ring-emerald-500/20');
                    r.cardElement.classList.add('bg-amber-500/10', 'border-amber-500/40', 'ring-1', 'ring-amber-500/20');
                    const badge = document.getElementById(`rank-badge-${r.partner.id}`);
                    const distLabel = document.getElementById(`distance-label-${r.partner.id}`);
                    if (badge) badge.classList.add('hidden');
                    if (distLabel) {
                        distLabel.textContent = `⚠ coverage not measured — ${r.distanceMiles.toFixed(1)} mi away`;
                        distLabel.classList.remove('hidden');
                    }
                });

                unmatched.forEach(r => {
                    r.cardElement.classList.remove('bg-emerald-500/10', 'border-emerald-500/40', 'ring-1', 'ring-emerald-500/20', 'bg-amber-500/10', 'border-amber-500/40', 'ring-amber-500/20');
                    r.cardElement.classList.add('opacity-20');
                    const badge = document.getElementById(`rank-badge-${r.partner.id}`);
                    const distLabel = document.getElementById(`distance-label-${r.partner.id}`);
                    if (badge) badge.classList.add('hidden');
                    if (distLabel) distLabel.classList.add('hidden');
                });

                // Reorder sidebar by strength of evidence: measured matches, then estimated ones,
                // then anyone who couldn't be checked, then confirmed misses.
                const list = document.getElementById('partnerList');
                matched.forEach(r => list.appendChild(r.cardElement));
                estimated.forEach(r => list.appendChild(r.cardElement));
                unchecked.forEach(r => list.appendChild(r.cardElement));
                unmatched.forEach(r => list.appendChild(r.cardElement));

                // Anyone we couldn't measure is called out explicitly. A "coverage gap" verdict
                // means nothing if some colleagues were never actually checked.
                const caveats = [];
                if (estimated.length) caveats.push(`${estimated.length} estimated (over 1 hr)`);
                if (unchecked.length) caveats.push(`${unchecked.length} couldn't be checked`);
                const caveatNote = caveats.length ? ` · ${caveats.join(', ')}` : '';

                if (matched.length > 0) {
                    const best = matched[0];
                    feedback.innerHTML = `${matched.length} match${matched.length === 1 ? '' : 'es'} — best fit: <strong>${escapeHtml(best.partner.name)}</strong> (${best.distanceMiles.toFixed(1)} mi)${caveatNote}`;
                    feedback.className = "text-[11px] font-semibold mt-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-2 rounded-md text-center";
                } else if (estimated.length > 0) {
                    const best = estimated[0];
                    feedback.innerHTML = `≈ No measured match, but <strong>${escapeHtml(best.partner.name)}</strong> covers it on an estimate (${best.distanceMiles.toFixed(1)} mi). Drive times can't be measured past 1 hour — worth confirming with them${unchecked.length ? ` · ${unchecked.length} couldn't be checked` : ''}`;
                    feedback.className = "text-[11px] font-semibold mt-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 p-2 rounded-md text-center";
                } else if (unchecked.length > 0) {
                    feedback.innerHTML = `No confirmed match — but ${unchecked.length} colleague${unchecked.length === 1 ? "'s coverage couldn't" : "s' coverage couldn't"} be measured, so this isn't a definite gap. Reopen their card to retry.`;
                    feedback.className = "text-[11px] font-semibold mt-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 p-2 rounded-md text-center";
                } else {
                    const nearest = evaluated.slice().sort((a, b) => a.distanceMiles - b.distanceMiles)[0];
                    feedback.innerHTML = nearest
                        ? `⚠️ Coverage gap — no partner reaches this lead. Nearest: <strong>${escapeHtml(nearest.partner.name)}</strong> (${nearest.distanceMiles.toFixed(1)} mi away)`
                        : `⚠️ Coverage gap — no partners in your network yet.`;
                    feedback.className = "text-[11px] font-semibold mt-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 p-2 rounded-md text-center";
                }
                feedback.classList.remove('hidden');
                clearBtn.classList.remove('hidden');
                renderSearchMatches(matched, estimated);

            } catch (err) { console.error(err); }
        });

        // Enter searches - typing the lead's address and hitting Enter did nothing before.
        document.getElementById('searchLocationInput').addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); document.getElementById('searchLocationBtn').click(); }
        });

        // The ranked matches, right under Find, each one tap from a referral. The colleague list
        // further down re-sorts the same way, but it starts well below the fold - acting on a
        // result used to mean scrolling to it and opening the card first. Capped at 5; anyone
        // past that is still ranked in the list below.
        function renderSearchMatches(matched, estimated) {
            const list = document.getElementById('searchMatches');
            const rows = [
                ...matched.map((r, idx) => ({ r, label: `#${idx + 1}`, note: `${r.distanceMiles.toFixed(1)} mi` })),
                ...estimated.map(r => ({ r, label: '≈', note: `${r.distanceMiles.toFixed(1)} mi, estimated` }))
            ].slice(0, 5);
            list.innerHTML = rows.map(({ r, label, note }) => `
                <li class="flex items-center gap-2 text-left text-[11px] p-1.5 rounded-md bg-black/10 dark:bg-gray-900/60 border border-gray-300/20 dark:border-gray-800">
                    <span class="font-black font-mono w-6 text-center flex-shrink-0">${label}</span>
                    <span class="flex-1 min-w-0"><strong class="block truncate">${escapeHtml(r.partner.name)}</strong><span class="opacity-60">${note}</span></span>
                    <button data-action="openReferralModal" data-arg0="${escapeHtml(String(r.partner.id))}" class="flex-shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold px-3 min-h-[36px] rounded-md active:scale-95 transition-all" aria-label="Refer this lead to ${escapeHtml(r.partner.name)}">📤 Refer</button>
                </li>`).join('');
            list.classList.toggle('hidden', rows.length === 0);
        }

        document.getElementById('clearSearchBtn').addEventListener('click', () => {
            if (searchTargetMarker) map.removeLayer(searchTargetMarker);
            document.getElementById('searchLocationInput').value = '';
            document.getElementById('searchFeedback').classList.add('hidden');
            document.getElementById('clearSearchBtn').classList.add('hidden');
            document.getElementById('searchMatches').classList.add('hidden');
            lastLeadAddress = '';

            document.querySelectorAll('.partner-card').forEach(card => {
                card.className = "text-[11px] p-2.5 rounded-lg border transition-all duration-200 partner-card sidebar-card shadow-sm";
            });
            savedPartners.forEach(p => {
                const badge = document.getElementById(`rank-badge-${p.id}`);
                const distLabel = document.getElementById(`distance-label-${p.id}`);
                if (badge) badge.classList.add('hidden');
                if (distLabel) distLabel.classList.add('hidden');
            });

            // Back to the list's own chosen order (the lead search re-ranked it by distance).
            resortPartnerList();

            applyFilters();
            map.setView([userProfile.lat, userProfile.lng], 9.5);
        });

        // --- SHARED ADD-PARTNER LOGIC: used by the manual "Build Boundary & Save" button, the
        // automatic share-link import, and bulk CSV import, so all three paths geocode/save
        // identically. `silent` (used only by CSV import, which adds many rows in a loop) swaps
        // the alert() popups for a `_importWarning` string on the returned partner, and turns the
        // two failure cases that would otherwise alert+return null into { error } instead. ---
        async function addPartnerFromFields({ name, brokerage, addressOrCoord, address2, phone, email, social, notes, tags, radius, photoUrl, silent }) {
            if (!name || !addressOrCoord) {
                if (silent) return { error: 'Missing name or address' };
                alert("Enter your colleague's name and address above before saving. (Your own info goes in Your Profile above instead.)");
                return null;
            }

            let lat, lng, displayLabel, warning = null;

            if (addressOrCoord.match(/^-?\d+\.\d+,\s*-?\d+\.\d+$/)) {
                const parts = addressOrCoord.split(',');
                lat = parseFloat(parts[0]);
                lng = parseFloat(parts[1]);
                displayLabel = `Map Selection [${lat.toFixed(3)}, ${lng.toFixed(3)}]`;
            } else {
                try {
                    const geo = await geocodeWithFallback(addressOrCoord);
                    if (geo) {
                        lat = geo.lat;
                        lng = geo.lng;
                        displayLabel = addressOrCoord;  // always exactly what was typed/selected, never silently reformatted
                        if (geo.usedFallback) {
                            warning = `Couldn't find that exact street, so the pin was placed near ${geo.fallbackLabel}.`;
                            if (!silent) alert(`${warning} Drag it to the exact spot - the address text stays exactly as typed.`);
                        }
                    } else {
                        const mapCenter = map.getCenter();
                        lat = mapCenter.lat;
                        lng = mapCenter.lng;
                        displayLabel = addressOrCoord;  // preserve the real typed address even when we can't place it precisely
                        warning = "Couldn't find that location at all - pin placed near the map center.";
                        if (!silent) alert("Couldn't find that location at all. A pin was placed near the map center. Drag it to the proper location.");
                    }
                } catch (err) {
                    if (silent) return { error: err.rateLimited ? 'RATE_LIMITED' : 'Network error while geocoding' };
                    alert("Network error processing coordinates. Try picking on the map.");
                    return null;
                }
            }

            // Second base is best-effort: a failed geocode never blocks the import itself
            let base2Name = '', lat2 = null, lng2 = null;
            if (address2) {
                try {
                    const geo2 = await geocodeWithFallback(address2);
                    if (geo2) {
                        base2Name = address2;
                        lat2 = geo2.lat;
                        lng2 = geo2.lng;
                    }
                } catch (err) { /* skip the second base, keep the partner */ }
            }

            const partnerPayload = {
                id: 'p-' + Date.now(),
                name: name,
                brokerage: brokerage,
                baseName: displayLabel,
                phone: phone,
                email: email,
                social: social,
                notes: notes,
                groups: tags,
                radius: radius,
                lat: lat,
                lng: lng,
                geoJsonData: null,
                base2Name: base2Name,
                lat2: lat2,
                lng2: lng2,
                geoJson2Data: null,
                photoUrl: photoUrl || '',
                createdAt: Date.now(),
                updatedAt: Date.now()
            };

            if (isLoggedIn) {
                try {
                    const res = await apiPost('/api/partners', partnerPayload);
                    if (res.ok) {
                        const created = await res.json();
                        partnerPayload.id = created.id;
                    }
                } catch (err) { console.error('Partner create sync failed', err); }
            }

            savedPartners.push(partnerPayload);
            persistPartnersLocal();
            renderPartnerAndCoverage(partnerPayload);
            if (warning) partnerPayload._importWarning = warning;
            return partnerPayload;
        }

        // --- SUBMISSION HANDLER WITH RICH CRM DATA ---
        document.getElementById('submitPartnerBtn').addEventListener('click', async () => {
            const groupsString = document.getElementById('partnerGroupInput').value.trim();
            const partner = await addPartnerFromFields({
                name: document.getElementById('partnerNameInput').value.trim(),
                brokerage: document.getElementById('partnerBrokerageInput').value.trim(),
                addressOrCoord: document.getElementById('partnerAddressInput').value.trim(),
                phone: document.getElementById('partnerPhoneInput').value.trim(),
                email: document.getElementById('partnerEmailInput').value.trim(),
                social: document.getElementById('partnerSocialInput').value.trim(),
                notes: document.getElementById('partnerNotesInput').value.trim(),
                tags: groupsString.split(',').map(t => t.trim()).filter(t => t !== ''),
                radius: parseInt(document.getElementById('partnerRadiusInput').value)
            });
            if (!partner) return;

            document.getElementById('firstVisitPrompt').classList.add('hidden');
            map.panTo([partner.lat, partner.lng]);

            document.getElementById('partnerNameInput').value = '';
            document.getElementById('partnerBrokerageInput').value = '';
            document.getElementById('partnerAddressInput').value = '';
            document.getElementById('partnerPhoneInput').value = '';
            document.getElementById('partnerEmailInput').value = '';
            document.getElementById('partnerSocialInput').value = '';
            document.getElementById('partnerNotesInput').value = '';
            document.getElementById('partnerGroupInput').value = '';
            // Clearing a field programmatically fires no input event, so the hint would otherwise
            // linger over an empty form after saving a colleague.
            updateTagSeparatorHint(document.getElementById('partnerGroupInput'));
            clearAddPartnerDraft();
        });

        // --- ADD-COLLEAGUE DRAFT: filling the form usually means leaving the app to look
        // someone up (see prepareAgentLookupLink), and phones - iOS home-screen apps especially -
        // routinely reload a backgrounded web app, wiping whatever was half-typed. The draft lives
        // in localStorage until the colleague is saved. Saved on input/change, and again on
        // pagehide/hidden to catch values set by code (📍 Pick fills the address with no input
        // event). Per-device convenience only, so every access is wrapped - storage can throw. ---
        const ADD_PARTNER_DRAFT_KEY = 'add_partner_draft';
        const ADD_PARTNER_DRAFT_FIELDS = ['partnerNameInput', 'partnerBrokerageInput', 'partnerAddressInput', 'partnerPhoneInput', 'partnerEmailInput', 'partnerSocialInput', 'partnerNotesInput', 'partnerGroupInput', 'partnerRadiusInput'];
        function saveAddPartnerDraft() {
            const draft = {};
            let hasText = false;
            ADD_PARTNER_DRAFT_FIELDS.forEach(id => {
                let value = document.getElementById(id).value;
                // 📍 Pick's placeholder while it reverse-geocodes - never worth restoring.
                if (value === 'Loading location...') value = '';
                draft[id] = value;
                // The radius select always has a value, so it alone doesn't make a draft.
                if (id !== 'partnerRadiusInput' && value.trim()) hasText = true;
            });
            try {
                if (hasText) localStorage.setItem(ADD_PARTNER_DRAFT_KEY, JSON.stringify(draft));
                else localStorage.removeItem(ADD_PARTNER_DRAFT_KEY);
            } catch (err) { /* storage unavailable - the form just isn't remembered */ }
        }
        function clearAddPartnerDraft() {
            try { localStorage.removeItem(ADD_PARTNER_DRAFT_KEY); } catch (err) { /* see above */ }
        }
        (function restoreAddPartnerDraft() {
            let draft = null;
            try { draft = JSON.parse(localStorage.getItem(ADD_PARTNER_DRAFT_KEY) || 'null'); } catch (err) { draft = null; }
            if (!draft || typeof draft !== 'object') return;
            ADD_PARTNER_DRAFT_FIELDS.forEach(id => {
                if (typeof draft[id] === 'string' && draft[id]) document.getElementById(id).value = draft[id];
            });
            updateTagSeparatorHint(document.getElementById('partnerGroupInput'));
        })();
        if (isAndroid() && isStandaloneDisplay()) document.getElementById('agentLookupAndroidTip').classList.remove('hidden');
        const addPartnerSection = document.getElementById('addPartnerSection');
        addPartnerSection.addEventListener('input', saveAddPartnerDraft);
        addPartnerSection.addEventListener('change', saveAddPartnerDraft);
        window.addEventListener('pagehide', saveAddPartnerDraft);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') saveAddPartnerDraft();
        });

        // Single toggle for BOTH bases together - it used to only ever clear the home polygon,
        // leaving the office pin and its coverage shape permanently on the map with no way to
        // hide them. Showing again re-draws both polygons and re-adds the office pin (removed,
        // not just hidden, on the way out - renderOfficeNode() rebuilds it from userProfile).
        window.toggleHomeArea = function() {
            isHomeAreaVisible = !isHomeAreaVisible;
            const badge = document.getElementById('sidebarHubBadge');
            if (isHomeAreaVisible) {
                badge.className = "text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded font-bold hover:bg-blue-500/20 transition-colors cursor-pointer select-none";
                badge.innerHTML = "VISIBLE";
                drawMyServiceArea(driveSlider.value);
                renderOfficeNode();
            } else {
                badge.className = "text-[9px] bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded font-bold hover:bg-red-500/20 transition-colors cursor-pointer select-none";
                badge.innerHTML = "HIDDEN";
                if (currentServicePolygon) { map.removeLayer(currentServicePolygon); currentServicePolygon = null; }
                if (officePolygon) { map.removeLayer(officePolygon); officePolygon = null; }
                if (officeMarker) { map.removeLayer(officeMarker); officeMarker = null; }
            }
            updateHomePopup();
            if (homeMarker) homeMarker.openPopup();
        };

        window.toggleHomeAreaFromSidebar = function() {
            window.toggleHomeArea();
            map.panTo([userProfile.lat, userProfile.lng]);
        };

        // Single source of truth for the mobile drawer's open/closed state - keeps the class,
        // the backdrop, and the header icon (hamburger vs. close) all in sync regardless of
        // which entry point triggered it (the toggle button, the backdrop, or a share-link import
        // via revealSidebar below).
        function setMobileSidebarOpen(isOpen) {
            document.getElementById('sidebar').classList.toggle('mobile-open', isOpen);
            // The next-step card floats over the map; it has no business on top of the drawer.
            if (isOpen) setMobileNextStepVisible(false);
            const backdrop = document.getElementById('sidebarBackdrop');
            if (backdrop) backdrop.classList.toggle('hidden', !isOpen);
            const toggleBtn = document.getElementById('mobileSidebarToggle');
            if (toggleBtn) {
                toggleBtn.textContent = isOpen ? '✕' : '☰';
                toggleBtn.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
                toggleBtn.title = isOpen ? 'Close the sidebar' : 'Open the sidebar';
            }
        }

        window.toggleMobileSidebar = function() {
            setMobileSidebarOpen(!document.getElementById('sidebar').classList.contains('mobile-open'));
        };

        // See #mobileNextStep in index.html.
        function setMobileNextStepVisible(show) {
            const card = document.getElementById('mobileNextStep');
            card.classList.toggle('hidden', !show);
            card.classList.toggle('flex', show);
        }
        function showMobileNextStep() {
            setMobileSidebarOpen(false);
            setMobileNextStepVisible(true);
        }
        window.dismissMobileNextStep = function() { setMobileNextStepVisible(false); };
        window.openAddColleague = function() {
            setMobileNextStepVisible(false);
            setMobileSidebarOpen(true);
            const section = document.getElementById('addPartnerSection');
            if (!section.classList.contains('accordion-visible')) toggleAccordion('addPartnerSection', 'addPartnerArrow');
            const nameInput = document.getElementById('partnerNameInput');
            nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            nameInput.focus({ preventScroll: true });
        };

        // Desktop collapse: slides the sidebar fully off-screen and reveals the map underneath
        // (the sidebar is an absolutely-positioned overlay, not a flex sibling, so the map is
        // already full-bleed the whole time - collapsing just uncovers it, nothing to resize).
        window.toggleSidebarCollapse = function() {
            const sidebar = document.getElementById('sidebar');
            const btn = document.getElementById('sidebarCollapseBtn');
            const arrow = document.getElementById('sidebarCollapseArrow');
            const collapsed = sidebar.classList.toggle('sidebar-collapsed');
            if (btn) btn.classList.toggle('sidebar-collapsed', collapsed);
            if (arrow) arrow.textContent = collapsed ? '▶' : '◀';
            if (btn) btn.setAttribute('aria-label', collapsed ? 'Expand sidebar' : 'Collapse sidebar');
            if (btn) btn.title = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
            localStorage.setItem('sidebar_collapsed_pref', collapsed ? 'true' : 'false');
            setTimeout(() => map.invalidateSize(), 320);
        };

        // --- SHARE CARD: a deep link (?name=...&brokerage=...) automatically geocodes and
        // saves the colleague to your network (skipping anyone already saved under that name)
        // - same result whether the link was generated from Your Profile (someone sharing
        // themselves) or from an existing colleague's card (forwarding someone else's info). ---
        // Class names are full literals here, not built from `${color}` - Tailwind's compiled
        // build only generates CSS for classes it can find as literal text in the source, unlike
        // the old Play CDN which JIT-compiled whatever showed up in the live DOM. A per-color
        // template string worked under Play and silently renders unstyled under the real build.
        const SHARE_BANNER_COLOR_CLASSES = {
            amber: 'bg-amber-500/10 border border-amber-500/20 text-amber-400',
            blue: 'bg-blue-500/10 border border-blue-500/20 text-blue-400',
            emerald: 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400',
            gray: 'bg-gray-500/10 border border-gray-500/20 text-gray-400'
        };
        function setShareBanner(text, color) {
            const banner = document.getElementById('shareCardBanner');
            banner.textContent = text;
            banner.className = `text-[11px] font-semibold leading-relaxed p-2.5 rounded-lg ${SHARE_BANNER_COLOR_CLASSES[color] || SHARE_BANNER_COLOR_CLASSES.gray}`;
        }

        // On phones the sidebar is hidden behind the hamburger by default, so any share-link
        // outcome (imported / updated / kept) would be completely invisible - the original
        // "imported on mobile but nothing appeared" bug. Pop the drawer open so the banner and
        // the colleague's card are actually seen.
        //
        // Desktop has the same hole by a different route: the sidebar can be collapsed fully
        // off-screen via the edge tab, and that choice persists across loads. A prompt rendered
        // into a collapsed sidebar is exactly as unseen as one rendered into a closed drawer, so
        // both need the panel brought back - hence this covers either viewport, not just mobile.
        function revealSidebar() {
            if (window.innerWidth < 768) {
                setMobileSidebarOpen(true);
                return;
            }
            const sidebar = document.getElementById('sidebar');
            if (sidebar && sidebar.classList.contains('sidebar-collapsed')) toggleSidebarCollapse();
        }

        // Set when a personal "add me" link was used to get here (never for a forwarded
        // colleague-card share) - see functions/s/[id].js, which only appends ?shareId= for
        // links minted with an owner attached. Read once by the reciprocate button's handler.
        let pendingReciprocateShareId = null;
        function showReciprocatePrompt(shareId) {
            if (!shareId) return;
            pendingReciprocateShareId = shareId;
            const prompt = document.getElementById('reciprocatePrompt');
            document.getElementById('reciprocatePromptText').textContent = '🔁 Send your info back so they can reach you too.';
            document.getElementById('reciprocateBtn').classList.remove('hidden');
            document.getElementById('reciprocateBtn').textContent = 'Share My Info';
            prompt.classList.remove('hidden');
        }

        async function importFromShareLink() {
            const params = new URLSearchParams(window.location.search);
            if (!params.has('name')) return;

            // Fires once here regardless of which branch below runs (found/updated/kept/failed)
            // - arriving via someone else's link is the trigger, not the outcome.
            maybeShowAddHomeScreenBanner();

            const name = params.get('name');
            const shareId = params.get('shareId');
            const existing = savedPartners.find(p => p.name.trim().toLowerCase() === name.trim().toLowerCase());

            if (existing) {
                const wantsUpdate = confirm(`${name} is already in your referral network.\n\nUpdate their card with the info from this link? (Cancel keeps your existing card unchanged.)`);
                if (!wantsUpdate) {
                    setShareBanner(`ℹ️ ${name} is already in your network — kept your existing card.`, 'gray');
                    map.panTo([existing.lat, existing.lng]);
                    showReciprocatePrompt(shareId);
                    revealSidebar();
                    return;
                }

                // Merge: only overwrite fields the link actually provides, so a sparse share
                // link can't blank out notes/tags you've added yourself.
                [['brokerage', 'brokerage'], ['phone', 'phone'], ['email', 'email'], ['social', 'social'], ['notes', 'notes'], ['photo', 'photoUrl']].forEach(([param, key]) => {
                    const v = params.get(param);
                    if (v) existing[key] = v;
                });
                const tagsParam = params.get('tags');
                if (tagsParam) existing.groups = tagsParam.split(',').map(t => t.trim()).filter(t => t !== '');
                const radiusParam = parseInt(params.get('radius'));
                if (radiusParam) existing.radius = radiusParam;

                const newAddress = params.get('address');
                if (newAddress && newAddress !== existing.baseName) {
                    try {
                        const geo = await geocodeWithFallback(newAddress);
                        if (geo) {
                            existing.lat = geo.lat;
                            existing.lng = geo.lng;
                            existing.baseName = newAddress;
                            existing.geoJsonData = null;  // stale isochrone - refetched on render
                        }
                    } catch (err) { /* keep the old pin if the new address won't geocode */ }
                }

                const newAddress2 = params.get('address2');
                if (newAddress2 && newAddress2 !== existing.base2Name) {
                    try {
                        const geo2 = await geocodeWithFallback(newAddress2);
                        if (geo2) {
                            existing.lat2 = geo2.lat;
                            existing.lng2 = geo2.lng;
                            existing.base2Name = newAddress2;
                            existing.geoJson2Data = null;
                        }
                    } catch (err) { /* keep whatever second base they had */ }
                }

                persistPartnersLocal();
                syncPartnerUpdate(existing);

                // Re-render their marker, coverage shape, and sidebar card with the fresh info
                if (mapMarkers[existing.id]) map.removeLayer(mapMarkers[existing.id]);
                if (mapPolygons[existing.id]) map.removeLayer(mapPolygons[existing.id]);
                await renderPartnerAndCoverage(existing);

                setShareBanner(`✅ ${name}'s card was updated with the latest info from their link.`, 'emerald');
                map.panTo([existing.lat, existing.lng]);
                showReciprocatePrompt(shareId);
                revealSidebar();
                return;
            }

            setShareBanner(`📇 Adding ${name} to your network from a shared link...`, 'blue');

            const tagsString = params.get('tags') || '';
            // Same "Imported" tag CSV import gets - a card arriving via someone else's forwarded
            // share link wasn't hand-typed here either, and should be just as findable.
            const shareTags = tagsString.split(',').map(t => t.trim()).filter(t => t !== '');
            const partner = await addPartnerFromFields({
                name: name,
                brokerage: params.get('brokerage') || '',
                addressOrCoord: params.get('address') || '',
                address2: params.get('address2') || '',
                phone: params.get('phone') || '',
                email: params.get('email') || '',
                social: params.get('social') || '',
                notes: params.get('notes') || '',
                tags: [...new Set([...shareTags, 'Imported'])],
                radius: parseInt(params.get('radius')) || 45,
                photoUrl: params.get('photo') || ''
            });

            if (partner) {
                setShareBanner(`✅ ${name} was added to your network from a shared link!`, 'emerald');
                map.panTo([partner.lat, partner.lng]);
                if (mapMarkers[partner.id]) mapMarkers[partner.id].openPopup();
                showReciprocatePrompt(shareId);
            } else {
                setShareBanner(`⚠️ Couldn't automatically add ${name} — open "Add Referral Colleague" below to add them manually.`, 'amber');
            }
            revealSidebar();
        }
        importFromShareLink();

        document.getElementById('reciprocateBtn').addEventListener('click', async () => {
            const btn = document.getElementById('reciprocateBtn');
            const promptText = document.getElementById('reciprocatePromptText');

            if (!userProfile.hubName || !userProfile.addressText) {
                promptText.textContent = "🔁 Fill out your profile above first, then click below again to send your info back.";
                if (document.getElementById('profileSection').classList.contains('accordion-hidden')) {
                    toggleAccordion('profileSection', 'profileArrow');
                }
                document.getElementById('userHubName').scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            btn.disabled = true;
            btn.textContent = 'Sending...';
            try {
                const res = await apiPost('/api/reciprocate', {
                    shareId: pendingReciprocateShareId,
                    name: userProfile.hubName,
                    brokerage: userProfile.brokerage || '',
                    baseName: userProfile.addressText,
                    phone: userProfile.phone || '',
                    email: userProfile.email || '',
                    social: userProfile.social || '',
                    notes: '',
                    groups: (userProfile.tags || '').split(',').map(t => t.trim()).filter(Boolean),
                    radius: parseInt(driveSlider.value),
                    lat: userProfile.lat,
                    lng: userProfile.lng,
                    photoUrl: userProfile.photoUrl || ''
                });

                if (res.ok) {
                    const data = await res.json();
                    // `pending` means they already had a card under this name, so the info is
                    // waiting on their review rather than applied. Say that plainly - the old
                    // copy here ("you were already in their network") read like a no-op success
                    // and hid the fact that an update had been sent at all.
                    promptText.textContent = data.pending
                        ? '✅ Sent! They already had a card for you, so your info is waiting for them to review.'
                        : '✅ Sent! You\'re now in their network too.';
                    btn.classList.add('hidden');
                } else {
                    const msg = await res.text();
                    promptText.textContent = `⚠️ ${msg || "Couldn't send your info - try again later."}`;
                    btn.disabled = false;
                    btn.textContent = 'Share My Info';
                }
            } catch (err) {
                promptText.textContent = '⚠️ Network error - try again later.';
                btn.disabled = false;
                btn.textContent = 'Share My Info';
            }
        });

        // --- ACCOUNT STATUS: reflects the login state already resolved during boot above;
        // profile/partner data is now server-backed for logged-in accounts. ---
        // Initials behind the Google photo, so a failed or missing picture still shows something
        // that belongs to this account rather than a generic silhouette. First two words, not
        // first-and-last: real profile names here carry suffixes ("Adam Grenville, REALTOR"), and
        // first-and-last would read those as the surname.
        function accountInitials() {
            const words = (userProfile.hubName || '').trim().split(/\s+/).filter(w => /[A-Za-z]/.test(w));
            if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
            if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
            const local = (userEmail || '').split('@')[0];
            return local ? local.slice(0, 2).toUpperCase() : '·';
        }

        function refreshAccountStatus() {
            if (isLoggedIn) {
                document.getElementById('signInBtn').classList.add('hidden');
                document.getElementById('accountStatus').classList.remove('hidden');
                document.getElementById('accountStatus').classList.add('flex');
                document.getElementById('accountEmail').textContent = userEmail;
                document.getElementById('accountName').textContent = (userProfile.hubName || '').trim() || 'Signed in';

                const initials = document.getElementById('accountInitials');
                const img = document.getElementById('accountAvatarImg');
                initials.textContent = accountInitials();
                if (userPicture) {
                    // Only swap to the photo once it has actually decoded - a broken or blocked
                    // Google URL otherwise leaves an empty circle where a face should be.
                    img.onload = () => { img.classList.remove('hidden'); initials.classList.add('hidden'); };
                    img.onerror = () => { img.classList.add('hidden'); initials.classList.remove('hidden'); };
                    img.src = userPicture;
                }
            } else {
                document.getElementById('signInBtn').classList.remove('hidden');
                document.getElementById('accountStatus').classList.add('hidden');
                document.getElementById('accountStatus').classList.remove('flex');
            }
        }
        refreshAccountStatus();

        // --- ACCOUNT MENU ---
        const accountMenuBtn = document.getElementById('accountMenuBtn');
        const accountMenu = document.getElementById('accountMenu');
        function setAccountMenuOpen(open) {
            accountMenu.classList.toggle('hidden', !open);
            accountMenuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        accountMenuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            setAccountMenuOpen(accountMenu.classList.contains('hidden'));
        });
        // Clicks inside don't close it: the theme picker and the three text sizes live in here, and
        // people try a size, look at the map, then try the next one. Snapping shut after each pick
        // would make the control worse than it was in the bar.
        accountMenu.addEventListener('click', (e) => e.stopPropagation());
        document.addEventListener('click', () => setAccountMenuOpen(false));
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setAccountMenuOpen(false); });
        // The two rows that open something of their own do close it, or the menu would sit on top
        // of the modal it just launched.
        document.getElementById('whatsNewBtn').addEventListener('click', () => setAccountMenuOpen(false));

        document.getElementById('signInBtn').addEventListener('click', () => window.startGoogleSignIn());

        document.getElementById('signOutBtn').addEventListener('click', async () => {
            await fetch('/api/auth/logout', { method: 'POST', headers: { 'X-Requested-With': 'fetch' } });
            window.location.reload();
        });

        // --- SHARE CARD: the link-preview image for your own share link - photo, name, brokerage
        // and drive-time range on the brand card (same look as brand/og/og-card.html). Drawn
        // here on a canvas and stored via /api/share-card, because rendering it server-side
        // doesn't fit a Worker's CPU limits; _middleware.js then points og:image at it.
        // Only with a photo - without one the generic branded image is the better preview.
        // Reused until anything on it changes, so repeat shares never re-upload. Any failure
        // (cross-origin photo tainting the canvas, offline, rate limit) just means no card:
        // sharing must never break over a preview image. ---
        const SHARE_CARD_CACHE_KEY = 'share_card_cache';
        const SHARE_CARD_VERSION = 1; // bump when the drawing changes, to re-render cached cards

        function fitCanvasText(ctx, text, maxWidth, size, minSize, weight) {
            let s = size;
            ctx.font = `${weight} ${s}px Manrope, sans-serif`;
            while (ctx.measureText(text).width > maxWidth && s > minSize) {
                s -= 2;
                ctx.font = `${weight} ${s}px Manrope, sans-serif`;
            }
            let out = text;
            while (ctx.measureText(out).width > maxWidth && out.length > 1) out = out.slice(0, -2) + '…';
            return out;
        }

        async function buildShareCardImage({ name, brokerage, radius, photo }) {
            await Promise.all(['800 64px Manrope', '700 32px Manrope', '500 34px Manrope', '700 20px "JetBrains Mono"']
                .map(f => document.fonts.load(f)));
            // onload rather than img.decode(): decode() was seen never settling in a backgrounded
            // tab, and getShareCardUrl's timeout is the backstop either way.
            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => reject(new Error('photo failed to load'));
                img.src = photo;
            });

            const W = 1200, H = 630;
            const canvas = document.createElement('canvas');
            canvas.width = W; canvas.height = H;
            const ctx = canvas.getContext('2d');

            const bg = ctx.createRadialGradient(1000, 315, 0, 1000, 315, 760);
            bg.addColorStop(0, '#131c36'); bg.addColorStop(1, '#0b1120');
            ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

            // Faint drive-time shapes on the right, the same three as the generic card.
            ctx.save();
            ctx.translate(640, 0);
            ctx.lineJoin = 'round';
            [['M170 150 L250 118 L318 140 L350 205 L405 228 L392 300 L420 362 L360 410 L300 470 L238 452 L180 470 L140 410 L95 360 L112 290 L88 230 Z', '56,189,248'],
             ['M330 170 L410 150 L470 182 L540 170 L565 240 L600 300 L560 350 L575 420 L505 470 L440 455 L380 480 L330 420 L300 360 L318 300 L285 240 Z', '168,85,247'],
             ['M270 380 L330 360 L385 392 L430 380 L455 440 L420 500 L440 560 L370 590 L310 570 L255 585 L225 525 L240 470 L215 420 Z', '99,102,241']
            ].forEach(([d, rgb]) => {
                const shape = new Path2D(d);
                ctx.fillStyle = `rgba(${rgb},0.10)`; ctx.fill(shape);
                ctx.strokeStyle = `rgba(${rgb},0.45)`; ctx.lineWidth = 2.5; ctx.stroke(shape);
            });
            ctx.restore();
            // Fade the shapes out behind the text.
            const fade = ctx.createLinearGradient(560, 0, 980, 0);
            fade.addColorStop(0, 'rgba(11,17,32,1)'); fade.addColorStop(1, 'rgba(11,17,32,0)');
            ctx.fillStyle = fade; ctx.fillRect(560, 0, 420, H);

            // Logo mark + wordmark, top left (mark paths from brand/svg, 64-unit box).
            ctx.save();
            ctx.translate(72, 48); ctx.scale(0.75, 0.75);
            const markGrad = ctx.createLinearGradient(0, 0, 64, 64);
            markGrad.addColorStop(0, '#38bdf8'); markGrad.addColorStop(0.58, '#6366f1'); markGrad.addColorStop(1, '#a855f7');
            ctx.strokeStyle = markGrad; ctx.fillStyle = markGrad; ctx.lineWidth = 5.6; ctx.lineCap = 'round';
            ctx.stroke(new Path2D('M8 44A26 26 0 0 1 32 12'));
            ctx.stroke(new Path2D('M56 44A26 26 0 0 0 32 12'));
            ctx.fill(new Path2D('M32 24c-5.4 0-9.8 4.4-9.8 9.8 0 7.3 9.8 16.5 9.8 16.5s9.8-9.2 9.8-16.5c0-5.4-4.4-9.8-9.8-9.8Zm0 13.3a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Z'), 'evenodd');
            ctx.restore();
            ctx.font = '800 32px Manrope, sans-serif';
            const wordGrad = ctx.createLinearGradient(132, 0, 132 + ctx.measureText('MapMyReferrals').width, 0);
            wordGrad.addColorStop(0, '#38bdf8'); wordGrad.addColorStop(0.58, '#6366f1'); wordGrad.addColorStop(1, '#a855f7');
            ctx.fillStyle = wordGrad; ctx.textBaseline = 'middle';
            ctx.fillText('MapMyReferrals', 132, 74);

            // Photo, cover-cropped into a circle with a brand-gradient ring.
            const cx = 230, cy = 340, r = 140;
            const ring = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
            ring.addColorStop(0, '#38bdf8'); ring.addColorStop(0.58, '#6366f1'); ring.addColorStop(1, '#a855f7');
            ctx.beginPath(); ctx.arc(cx, cy, r + 8, 0, Math.PI * 2); ctx.fillStyle = ring; ctx.fill();
            ctx.save();
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
            const scale = Math.max((2 * r) / img.naturalWidth, (2 * r) / img.naturalHeight);
            const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
            ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
            ctx.restore();

            // Name, brokerage, range pill.
            const tx = 430, maxW = W - tx - 72;
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#f8fafc';
            ctx.fillText(fitCanvasText(ctx, name, maxW, 64, 40, 800), tx, brokerage ? 300 : 330);
            if (brokerage) {
                ctx.fillStyle = '#94a3b8';
                ctx.fillText(fitCanvasText(ctx, brokerage, maxW, 34, 24, 500), tx, 352);
            }
            const minutes = parseInt(radius, 10);
            const range = minutes > 0 ? (minutes % 60 === 0 ? `${minutes / 60}-hour` : `${minutes}-minute`) + ' drive-time coverage' : 'Real drive-time coverage';
            ctx.font = '700 26px Manrope, sans-serif';
            const pillW = ctx.measureText(range).width + 48, pillY = brokerage ? 392 : 372;
            ctx.beginPath(); ctx.roundRect(tx, pillY, pillW, 52, 26);
            ctx.fillStyle = 'rgba(56,189,248,0.12)'; ctx.fill();
            ctx.strokeStyle = 'rgba(56,189,248,0.45)'; ctx.lineWidth = 2; ctx.stroke();
            ctx.fillStyle = '#38bdf8'; ctx.textBaseline = 'middle';
            ctx.fillText(range, tx + 24, pillY + 27);

            ctx.font = '700 20px "JetBrains Mono", monospace';
            ctx.fillStyle = '#38bdf8'; ctx.textBaseline = 'alphabetic';
            ctx.fillText('mapmyreferrals.com', 72, H - 48);
            ctx.font = '700 22px Manrope, sans-serif';
            ctx.fillStyle = '#cbd5e1'; ctx.textAlign = 'right';
            ctx.fillText('Add me to your referral map →', W - 72, H - 48);
            ctx.textAlign = 'left';

            return new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.88));
        }

        // Returns the stored card's /photos/... path, or '' for "no card" - never throws.
        async function getShareCardUrl(fields) {
            if (!fields.photo || !fields.name) return '';
            const sig = JSON.stringify({ v: SHARE_CARD_VERSION, name: fields.name, brokerage: fields.brokerage || '', radius: String(fields.radius || ''), photo: fields.photo });
            try {
                const cached = JSON.parse(localStorage.getItem(SHARE_CARD_CACHE_KEY) || 'null');
                if (cached && cached.sig === sig && typeof cached.url === 'string') return cached.url;
            } catch (err) { /* no usable cache - render below */ }
            try {
                // The copy button waits on this, so it gets a hard cap - a slow card is skipped,
                // never allowed to hold up the link itself.
                const timeout = (ms) => new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), ms));
                const blob = await Promise.race([buildShareCardImage(fields), timeout(6000)]);
                const res = await Promise.race([
                    fetch('/api/share-card', { method: 'POST', headers: { 'Content-Type': 'image/jpeg', 'X-Requested-With': 'fetch' }, body: blob }),
                    timeout(8000)
                ]);
                if (!res.ok) return '';
                const { url } = await res.json();
                if (typeof url !== 'string') return '';
                try { localStorage.setItem(SHARE_CARD_CACHE_KEY, JSON.stringify({ sig, url })); } catch (err) { /* fine - re-renders next time */ }
                return url;
            } catch (err) {
                console.warn('Share card skipped:', err && err.message);
                return '';
            }
        }

        function buildShareUrl(fields) {
            const params = new URLSearchParams();
            Object.entries(fields).forEach(([key, value]) => { if (value) params.set(key, value); });
            return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        }

        // Tries to shrink a share link down to /s/<id> via the Pages Function + KV store.
        // Falls back to the full long URL if that ever fails, so sharing never breaks.
        // selfShare=true marks this as "my own add-me link" (vs. forwarding a colleague's card)
        // so the backend can attach the owner id needed for reciprocal auto-import - see
        // functions/api/shorten.js. Harmless to pass while logged out; the backend just won't
        // attach an owner since there's no session to attribute it to.
        async function shortenUrl(fullUrl, selfShare, kind) {
            try {
                const url = new URL(fullUrl);
                const response = await fetch('/api/shorten', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: url.pathname + url.search, selfShare: !!selfShare, kind: kind || null })
                });
                if (!response.ok) return fullUrl;
                const data = await response.json();
                return `${url.origin}/s/${data.id}`;
            } catch (err) {
                return fullUrl;
            }
        }

        // Your own link is minted once per version of your profile and reused, instead of a fresh
        // id on every tap. A convention floor is exactly where a fresh mint fails - no signal, or
        // a whole venue's Wi-Fi sharing one IP's shorten budget - and the fallback there is the
        // long URL: a denser QR, and no shareId, so nobody who scans it can send their info back.
        // Keyed on the account too, since a link minted signed-out carries no owner. Re-minted
        // well before the KV entry's 365-day expiry so a saved QR never goes dead.
        const SELF_SHORT_LINK_CACHE_KEY = 'self_short_links';
        const SELF_SHORT_LINK_MAX_AGE = 300 * 24 * 60 * 60 * 1000;
        async function getSelfShortUrl(fullUrl, kind) {
            const sig = `${userEmail || ''}|${fullUrl}`;
            let cache = {};
            try { cache = JSON.parse(localStorage.getItem(SELF_SHORT_LINK_CACHE_KEY) || '{}') || {}; } catch (err) { /* start fresh */ }
            const hit = cache[kind];
            if (hit && hit.sig === sig && Date.now() - hit.at < SELF_SHORT_LINK_MAX_AGE) return hit.url;

            const shortUrl = await shortenUrl(fullUrl, true, kind);
            // Only a real short link is worth keeping - a fallback long URL gets retried next time.
            if (shortUrl !== fullUrl) {
                cache[kind] = { sig, url: shortUrl, at: Date.now() };
                try { localStorage.setItem(SELF_SHORT_LINK_CACHE_KEY, JSON.stringify(cache)); } catch (err) { /* fine - re-mints next time */ }
            }
            return shortUrl;
        }

        function copyShareUrl(shareUrl, btn, revertText) {
            const original = btn.innerHTML;
            navigator.clipboard.writeText(shareUrl).then(() => {
                btn.innerHTML = revertText;
                setTimeout(() => { btn.innerHTML = original; }, 2000);
            }).catch(() => { prompt('Copy this link:', shareUrl); });
        }

        // Bound to both the full card's button and the collapsed summary's, with the "Copied!"
        // feedback landing on whichever one was actually tapped.
        async function copyMyShareLink(feedbackBtn) {
            const name = document.getElementById('userHubName').value.trim();
            const address = document.getElementById('userHubAddress').value.trim();
            if (!name || !address) return alert('Fill out your profile above before generating a share link.');

            const fields = {
                name: name,
                brokerage: document.getElementById('userHubBrokerage').value.trim(),
                address: address,
                address2: userProfile.hub2AddressText || '',
                phone: document.getElementById('userHubPhone').value.trim(),
                email: document.getElementById('userHubEmail').value.trim(),
                social: document.getElementById('userHubSocial').value.trim(),
                tags: document.getElementById('userHubTags').value.trim(),
                radius: driveSlider.value,
                photo: userProfile.photoUrl || ''
            };
            // Only the copied link gets the photo card: it's the one pasted into chats, where the
            // preview shows. A scanned QR code opens the page directly, so showMyQrCode skips it.
            fields.card = await getShareCardUrl(fields);
            const shortUrl = await getSelfShortUrl(buildShareUrl(fields), 'copy');
            copyShareUrl(shortUrl, feedbackBtn, '✅ Copied! Paste it anywhere.');
        }
        ['copyMyShareLinkBtn', 'summaryShareLinkBtn'].forEach(id => {
            const btn = document.getElementById(id);
            btn.addEventListener('click', () => copyMyShareLink(btn));
        });

        // --- IN-PERSON QR SHARING: encodes the same share link as the copy button, but as a
        // scannable code for face-to-face moments (conferences, open houses, closings). Uses the
        // SHORTENED url on purpose - fewer characters means a coarser, more forgiving QR pattern
        // that scans reliably from a laptop or phone screen at arm's length. ---
        async function showMyQrCode() {
            const name = document.getElementById('userHubName').value.trim();
            const address = document.getElementById('userHubAddress').value.trim();
            if (!name || !address) return alert('Fill out your profile above before generating your QR code.');

            const shareUrl = buildShareUrl({
                name: name,
                brokerage: document.getElementById('userHubBrokerage').value.trim(),
                address: address,
                address2: userProfile.hub2AddressText || '',
                phone: document.getElementById('userHubPhone').value.trim(),
                email: document.getElementById('userHubEmail').value.trim(),
                social: document.getElementById('userHubSocial').value.trim(),
                tags: document.getElementById('userHubTags').value.trim(),
                radius: driveSlider.value,
                photo: userProfile.photoUrl || ''
            });
            const shortUrl = await getSelfShortUrl(shareUrl, 'qr');

            document.getElementById('qrName').textContent = name;
            document.getElementById('qrBrokerage').textContent = document.getElementById('userHubBrokerage').value.trim();
            const qrPhoto = document.getElementById('qrPhoto');
            if (userProfile.photoUrl) {
                qrPhoto.src = userProfile.photoUrl;
                qrPhoto.classList.remove('hidden');
            } else {
                qrPhoto.classList.add('hidden');
            }

            const container = document.getElementById('qrCodeContainer');
            container.innerHTML = '';
            new QRCode(container, { text: shortUrl, width: 240, height: 240, correctLevel: QRCode.CorrectLevel.M });

            const modal = document.getElementById('qrModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
        }
        document.getElementById('showQrCodeBtn').addEventListener('click', showMyQrCode);
        document.getElementById('summaryQrBtn').addEventListener('click', showMyQrCode);

        function closeQrModal() {
            const modal = document.getElementById('qrModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
        }
        document.getElementById('qrCloseBtn').addEventListener('click', closeQrModal);
        document.getElementById('qrModal').addEventListener('click', (e) => {
            if (e.target === document.getElementById('qrModal')) closeQrModal();
        });

        window.sharePartnerCard = async function(id) {
            const partner = savedPartners.find(p => p.id === id);
            if (!partner) return;

            const shareUrl = buildShareUrl({
                name: partner.name,
                brokerage: partner.brokerage,
                address: partner.baseName,
                address2: partner.base2Name || '',
                phone: partner.phone,
                email: partner.email,
                social: partner.social,
                notes: partner.notes,
                tags: (partner.groups || []).join(', '),
                radius: partner.radius,
                photo: partner.photoUrl || ''
            });
            const shortUrl = await shortenUrl(shareUrl);
            copyShareUrl(shortUrl, document.getElementById(`share-btn-${id}`), '✅');
        };

        // --- REFERRAL TRACKER: the send-and-track layer. A referral is a money event (fees run
        // ~25% of gross commission), so records are denormalized (partnerName stored on the
        // referral) and never cascade-deleted when a partner is removed from the network. ---
        const REFERRAL_STATUSES = {
            sent:           { label: 'Sent',              color: '#3b82f6' },
            in_contact:     { label: 'In Contact',        color: '#06b6d4' },
            under_contract: { label: 'Under Contract',    color: '#f59e0b' },
            closed:         { label: 'Closed - Fee Due',  color: '#10b981' },
            paid:           { label: 'Fee Paid ✓',        color: '#22c55e' },
            fell_through:   { label: 'Fell Through',      color: '#ef4444' }
        };

        function referralExpectedFee(r) {
            if (!r.estPrice) return null;
            return r.estPrice * ((r.commissionPct || 3) / 100) * ((r.feePct || 25) / 100);
        }

        function formatMoney(v) {
            return '$' + Math.round(v).toLocaleString();
        }

        function updatePipelineBadge() {
            const badge = document.getElementById('referralPipelineBadge');
            const active = savedReferrals.filter(r => !['paid', 'fell_through'].includes(r.status));
            if (!active.length) { badge.classList.add('hidden'); return; }
            const total = active.reduce((sum, r) => sum + (referralExpectedFee(r) || 0), 0);
            badge.textContent = total > 0 ? `${formatMoney(total)} pending` : `${active.length} active`;
            badge.classList.remove('hidden');
        }

        function renderReferralList() {
            const list = document.getElementById('referralList');
            if (!savedReferrals.length) {
                list.innerHTML = '<li class="text-[10px] opacity-50 italic leading-relaxed">No referrals tracked yet. Open a colleague\'s card below and hit 📤 to send your first.</li>';
                updatePipelineBadge();
                return;
            }
            list.innerHTML = '';
            [...savedReferrals].sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0)).forEach(r => {
                const meta = REFERRAL_STATUSES[r.status] || REFERRAL_STATUSES.sent;
                const fee = referralExpectedFee(r);
                const options = Object.entries(REFERRAL_STATUSES)
                    .map(([key, m]) => `<option value="${key}" ${key === r.status ? 'selected' : ''}>${m.label}</option>`).join('');
                const li = document.createElement('li');
                li.className = 'text-[11px] p-2.5 rounded-lg border sidebar-card shadow-sm space-y-1.5';
                li.innerHTML = `
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-1.5 truncate">
                            <span class="w-2 h-2 rounded-full flex-shrink-0" style="background:${meta.color}"></span>
                            <strong class="text-xs truncate">${escapeHtml(r.clientName)}</strong>
                        </div>
                        <button data-action="deleteReferral" data-arg0="${r.id}" aria-label="Delete referral for ${escapeHtml(r.clientName)}" class="opacity-50 hover:opacity-100 text-red-400 text-[10px] flex-shrink-0" title="Delete referral">🗑️</button>
                    </div>
                    <div class="text-[10px] opacity-70 truncate">→ ${escapeHtml(r.partnerName)}${r.locationText ? ' · ' + escapeHtml(r.locationText) : ''}</div>
                    <div class="flex items-center justify-between gap-1.5">
                        <select data-action-change="updateReferralStatus" data-arg0="${r.id}" aria-label="Status of referral for ${escapeHtml(r.clientName)}" class="text-[10px] p-1 bg-black/10 dark:bg-gray-950 border border-gray-300/40 dark:border-gray-800 rounded-md text-current flex-1 cursor-pointer focus:outline-none">${options}</select>
                        ${fee ? `<span class="text-[10px] font-mono font-bold text-emerald-400 whitespace-nowrap" title="Estimated referral fee">${formatMoney(fee)}</span>` : ''}
                    </div>`;
                list.appendChild(li);
            });
            updatePipelineBadge();
        }

        window.updateReferralStatus = function(id, status) {
            const index = savedReferrals.findIndex(r => r.id === id);
            if (index === -1) return;
            savedReferrals[index].status = status;
            savedReferrals[index].updatedAt = Date.now();
            persistReferralsLocal();
            syncReferralUpdate(savedReferrals[index]);
            renderReferralList();
        };

        // Confirmed, because this was previously a single unguarded tap on a 🗑️ that permanently
        // destroyed the record. The wording does real work: most deletions are actually "this one
        // didn't work out", which is what the Fell Through status is for and which keeps the
        // client's details - so the alternative is offered before the destructive path, not after.
        window.deleteReferral = function(id) {
            const referral = savedReferrals.find(r => r.id === id);
            if (!referral) return;

            const ok = confirm(
                `Delete the referral for ${referral.clientName}?\n\n` +
                `Their name, contact details and notes are erased and can't be recovered. ` +
                `The fact that you sent a referral to ${referral.partnerName} stays in your totals.\n\n` +
                `If it simply didn't work out, close this and set the status to "Fell Through" instead — ` +
                `that keeps the details.`
            );
            if (!ok) return;

            savedReferrals = savedReferrals.filter(r => r.id !== id);
            persistReferralsLocal();
            syncReferralDelete(id);
            renderReferralList();
        };

        let currentReferralPartner = null;

        window.openReferralModal = function(pId) {
            const partner = savedPartners.find(p => p.id === pId);
            if (!partner) return;
            currentReferralPartner = partner;
            document.getElementById('referralPartnerLabel').innerHTML = `Sending to <strong>${escapeHtml(partner.name)}</strong>${partner.brokerage ? ' · ' + escapeHtml(partner.brokerage) : ''}`;
            ['refClientName', 'refClientContact', 'refLocation', 'refEstPrice', 'refNotes'].forEach(id => document.getElementById(id).value = '');
            document.getElementById('refLocation').value = lastLeadAddress;
            document.getElementById('refFeePct').value = 25;
            updateFeePreview();
            document.getElementById('referralFormStep').classList.remove('hidden');
            document.getElementById('referralSentStep').classList.add('hidden');
            const modal = document.getElementById('referralModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
            document.getElementById('refClientName').focus();
        };

        function closeReferralModal() {
            const modal = document.getElementById('referralModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
        }
        window.closeReferralModal = closeReferralModal;

        // Second step of the referral modal: the intro goes straight into Messages / Mail,
        // addressed to the colleague. Text only on a touch device - a desktop browser with no
        // sms: handler just does nothing when clicked, which reads as broken.
        function showReferralSentStep(partner, message, copied) {
            const firstName = partner.name.split(/[ ,]/)[0];
            const textLink = document.getElementById('referralTextLink');
            const phone = (partner.phone || '').replace(/[^\d+]/g, '');
            const canText = phone.length >= 7 && window.matchMedia('(pointer: coarse)').matches;
            textLink.classList.toggle('hidden', !canText);
            if (canText) {
                textLink.href = `sms:${phone}?&body=${encodeURIComponent(message)}`;
                textLink.textContent = `💬 Text ${firstName}`;
            }
            const emailLink = document.getElementById('referralEmailLink');
            // Kept as typed apart from the characters that would break out of the mailto: address.
            const email = (partner.email || '').replace(/[?&#\s]/g, '');
            emailLink.classList.toggle('hidden', !email);
            if (email) {
                emailLink.href = `mailto:${email}?subject=${encodeURIComponent('Referral for you')}&body=${encodeURIComponent(message)}`;
                emailLink.textContent = `✉️ Email ${firstName}`;
            }
            const canSend = canText || !!email;
            document.getElementById('referralSentText').textContent =
                canSend ? `✅ Saved${copied ? ', and the intro is copied' : ''}. Send it to ${firstName}:`
                : copied ? `✅ Saved, and the intro is copied. Paste it into a text or email to ${firstName}.`
                : `✅ Saved to your tracker. Copy the intro below and send it to ${firstName}.`;
            // Without the clipboard there was no way to get at the message at all.
            const introText = document.getElementById('referralIntroText');
            introText.value = message;
            introText.classList.toggle('hidden', copied);
            document.getElementById('referralFormStep').classList.add('hidden');
            document.getElementById('referralSentStep').classList.remove('hidden');
        }
        document.getElementById('referralCloseBtn').addEventListener('click', closeReferralModal);
        document.getElementById('referralModal').addEventListener('click', (e) => {
            if (e.target === document.getElementById('referralModal')) closeReferralModal();
        });

        // --- FEEDBACK WIDGET: see matching HTML comment above for the full file list ---
        function closeFeedbackModal() {
            const modal = document.getElementById('feedbackModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
            document.getElementById('feedbackStatus').classList.add('hidden');
        }
        document.getElementById('feedbackFab').addEventListener('click', () => {
            const modal = document.getElementById('feedbackModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
            document.getElementById('feedbackMessageInput').focus();
        });
        document.getElementById('feedbackCloseBtn').addEventListener('click', closeFeedbackModal);
        document.getElementById('feedbackModal').addEventListener('click', (e) => {
            if (e.target === document.getElementById('feedbackModal')) closeFeedbackModal();
        });
        document.getElementById('feedbackSubmitBtn').addEventListener('click', async () => {
            const input = document.getElementById('feedbackMessageInput');
            const status = document.getElementById('feedbackStatus');
            const message = input.value.trim();
            if (!message) return;

            const btn = document.getElementById('feedbackSubmitBtn');
            btn.disabled = true;
            try {
                const res = await apiPost('/api/feedback', { message });
                status.textContent = res.ok ? '✅ Thanks - got it!' : '⚠️ Something went wrong, try again.';
                status.className = `text-[11px] font-semibold mt-2 text-center ${res.ok ? 'text-emerald-400' : 'text-red-400'}`;
                status.classList.remove('hidden');
                if (res.ok) {
                    input.value = '';
                    setTimeout(closeFeedbackModal, 1400);
                }
            } catch (err) {
                status.textContent = '⚠️ Something went wrong, try again.';
                status.className = 'text-[11px] font-semibold mt-2 text-center text-red-400';
                status.classList.remove('hidden');
            } finally {
                btn.disabled = false;
            }
        });
        // --- /FEEDBACK WIDGET ---

        // --- WHAT'S NEW: curated, user-facing subset of CHANGELOG.md - see the comment on
        // #whatsNewModal above for why these are maintained as two separate lists. ---
        const WHATS_NEW_ENTRIES = [
            { date: '2026-10-12', title: "🔎 Look up colleagues, and share links that show who you are", body: "Add Referral Colleague has a \"Look them up on Realtor.com\" link that searches for the name you've typed. In the Android app, tap ⋮ → Open in Chrome once it opens so you can switch back and forth. The form now keeps what you've typed until you save, even if you leave the app. When you text your share link, the preview now shows your name, brokerage and drive-time range instead of a generic card - and if you've added a profile photo, a branded card with your photo on it." },
            { date: '2026-10-03', title: "🔁 See who added themselves, and sort your network", body: "Filter Tags now opens with two chips - 🔁 Added Themselves and 🔗 Connected - each with a count, so checking whether anyone you shared your link with has added you is one tap. Typing \"added themselves\" in the contact search works too. Next to the search there's now a sort: A-Z, Newest added, or Last updated, and the list remembers your choice." },
            { date: '2026-10-03', title: "📱 Tidier header and sidebar", body: "On phones, the header is one row again with Sign in (or your account) pinned to the right. Once you're signed in, the menu button shows a ⚙ gear (and says \"Settings\" on bigger screens), so it's clear that's where theme, text size and What's New live. Your Profile stays open or closed the way you last left it, and even closed it shows your name, range, share link and QR code. Opening several sections at once no longer makes them overlap, and Filter Tags and the network list now sit in the same boxes as everything else." },
            { date: '2026-09-06', title: "🔀 Merge duplicate colleagues, and CSV imports catch them automatically", body: "A big CSV import could add the same person twice under slightly different spellings. Import now checks names against your existing network (and against the file itself) and skips a match instead of creating a duplicate. If you already have two records for the same person, select exactly two colleagues (via \"Select\" in the Active Network Index) and hit the new \"🔀 Merge\" button - pick which value wins for each field, and the two become one." },
            { date: '2026-09-06', title: "🐛 Fixed a deleted colleague reappearing on the map", body: "Deleting a colleague could leave their pin on the map (though correctly gone from the list) until the page was reloaded - a background check for updates from other devices could occasionally recreate the pin from a request that was already in flight when the delete happened. Fixed at the source: a deleted colleague is now remembered for the rest of the session so that check can never resurrect them, and deleting properly forgets the old pin instead of just hiding it." },
            { date: '2026-09-05', title: "🗺️ Clearer heads-up on drive-time shapes during a big import", body: "Coverage shapes load in the background after an import, a few at a time - for a large file that can take a while, and occasionally bump into our map provider's own limit. The import screen now says so up front, the finish banner reminds you for big batches, and if any colleague's shape still couldn't load after everything settles, a message says so plainly and confirms it'll fill in on its own - a blank pin right after a big import is expected, not broken." },
            { date: '2026-09-05', title: "✏️ Manage a colleague straight from their map pin", body: "Clicking a pin used to only open a read-only popup - editing or deleting meant closing it and hunting for them in the sidebar list by hand. A new \"Edit / Delete in Sidebar →\" button jumps straight there: opens the sidebar, clears any filter hiding them, expands their card, and briefly highlights it so it's obvious which one - landing right on the existing ✏️ and 🗑️ icons." },
            { date: '2026-09-05', title: "🔎 Isolate a colleague on the map", body: "With a lot of colleagues saved, overlapping coverage shapes can make it hard to tell whose territory actually covers a spot. Tap \"🔎 Isolate\" on a colleague's card or map popup to hide everyone else and see just theirs - tap it again, or \"Show All\" on the banner that appears, to bring everyone back." },
            { date: '2026-09-05', title: "🗺️ Fixed missing coverage shapes on large networks", body: "Loading a big network used to request every colleague's drive-time shape at once, which could overwhelm our map provider and leave many colleagues with no shape at all. Requests are now spaced out a few at a time - everything still loads, just without the pile-up, and a colleague's shape is remembered afterward so it won't need re-fetching next time." },
            { date: '2026-09-04', title: "☑️ Bulk-remove colleagues, and a safer big CSV import", body: "New \"Select\" link atop the Active Network Index turns on checkboxes so you can remove a whole batch of colleagues at once instead of one card at a time. Separately, a large CSV import used to silently dump every colleague past our address-lookup limit at the same spot on the map once that limit was hit. Import now stops the moment that happens, tells you exactly how many made it in, and hands you a ready-to-reimport CSV of whatever's left for an hour later." },
            { date: '2026-08-21', title: "👤 A clearer Your Profile, especially on phones", body: "Right after signing in on a phone, the Your Profile form now opens automatically instead of hiding behind the hamburger menu with no hint it's there. Phone and Email got their own full-width lines instead of being squeezed onto one. And every field now saves through a single \"💾 Save Profile\" button that confirms with \"✅ Saved!\" — no more silently saving on blur with zero feedback." },
            { date: '2026-08-21', title: "📲 A way back, for iPhone visitors", body: "iOS never shows its own \"install this\" prompt the way Chrome does, so anyone who found this through a colleague's share link had no way to get back except being told in person. Right after a share-link import, iPhone/iPad visitors now see a one-line, dismiss-once tip pointing at Share → Add to Home Screen. Doesn't show on a first cold visit (that's already covered by the how-it-works banner) and never appears again once dismissed." },
            { date: '2026-08-21', title: "⬆️⬇️ Bulk import & export (CSV)", body: "Add your whole sphere at once from a spreadsheet — new Import CSV button in the Active Network Index matches common column names (Name, Address, Phone, ...) automatically, then geocodes and builds each colleague's coverage shape as it goes. \"Need the template?\" right below it downloads a blank starter file with the right headers and one filled-in example row, so there's no guessing what to type where. Export CSV buttons there and in the Referral Tracker download your colleague list or referral history, ready for Excel/Sheets or a re-import later." },
            { date: '2026-08-02', title: "📐 Coverage over 1 hour is now marked as an estimate", body: "Real drive-time shapes can only be calculated up to 60 minutes. Past that, the app draws a dotted straight-line estimate and labels it with ≈ — on the map, on the colleague's card, and in lead search results. Anything without that mark is a measured drive time. If a shape can't be fetched at 60 minutes or under, nothing is drawn at all rather than a circle standing in for it." },
            { date: '2026-08-01', title: "🗑️ Deleting a referral now asks first", body: "The trash icon in the Referral Tracker used to wipe a referral instantly, with no confirmation and no way back. It now confirms, and it points out that \"Fell Through\" is usually what you actually want — that keeps the client's details and the history. If you do delete, the client's name, contact and notes are erased for good; only the bare record that you sent a referral remains." },
            { date: '2026-08-01', title: "🔎 Bigger text, and pinch-to-zoom works again", body: "New A A A buttons in the header enlarge the sidebar, the popups and the names on the map — pick a size once and it sticks. Zooming the map never resized those names (no map does), so if you'd given up squinting at them, that's why. Pinch-zoom on the page itself also works again; it had been switched off site-wide." },
            { date: '2026-08-01', title: "🔁 Reciprocated info no longer gets lost", body: "If a colleague sends their info back but you'd already saved them by hand, their card used to stay exactly as you typed it - wrong phone or old address included - and nobody was told. Now you get a review prompt showing exactly what they sent versus what you have, with a checkbox on every field: take their phone number without letting their tags overwrite yours." },
            { date: '2026-07-18', title: "◀ Collapsible sidebar", body: "Collapse the sidebar on desktop with the edge tab to see more map, then bring it back any time. The mobile menu got a rework too - tap the ✕ or anywhere outside it to close, no more hunting for a tiny close button." },
            { date: '2026-07-17', title: "💡 New: Why MapMyReferrals page", body: "A shareable page explaining the case for real drive-time routing over guessing - handy to send a colleague who's on the fence. Find it via \"Why This?\" in the header, or share it straight from the page itself." },
            { date: '2026-07-17', title: "🏷️ Tag autocomplete", body: "Typing a tag now suggests ones you've already used elsewhere in your network, so \"Luxury\" doesn't end up duplicated as \"luxury\" or \"Lux\" on different cards." },
            { date: '2026-07-17', title: "📋 Collapsible Filter Tags & Active Network Index", body: "Both sections in the sidebar can now be expanded or collapsed independently, same as the other panels." },
            { date: '2026-07-16', title: "🎉 New-contact notifications", body: "The app now checks for new contacts when you come back to the tab, and lets you know the moment a colleague reciprocates - no more needing to guess and reload." },
            { date: '2026-07-16', title: "🔒 Base pins locked by default", body: "Your home and office pins won't move by accident anymore - unlock \"Reposition\" from the pin's popup when you actually want to drag it." },
            { date: '2026-07-14', title: "🔁 Reciprocal sharing", body: "When a colleague imports your share link, they can send their info straight back to you with one tap - no more one-way invites." },
            { date: '2026-07-14', title: "🔤 Sort & tag search", body: "Your colleague list now sorts alphabetically by last name, and you can search tags directly in the Filter Tags row." },
            { date: '2026-07-13', title: "📤 Share a colleague's contact", body: "Tap \"Full Details & Share\" on any colleague's map pin for a readable card, and send their contact straight to a client as a vCard." },
            { date: '2026-07-13', title: "💬 Feedback button", body: "See the 💬 button in the corner? Suggestions and bug reports go straight to development." },
            { date: '2026-07-05', title: "🏢 Second base / office", body: "Add an office location alongside home - your coverage becomes the combined area around both." }
        ];

        function renderWhatsNew() {
            document.getElementById('whatsNewList').innerHTML = WHATS_NEW_ENTRIES.map(e => `
                <div class="border-b border-gray-300/20 dark:border-gray-800 pb-2.5 last:border-0 last:pb-0">
                    <div class="flex items-center justify-between mb-0.5 gap-2">
                        <span class="text-xs font-bold">${e.title}</span>
                        <span class="text-[9px] font-mono opacity-40 flex-shrink-0">${e.date}</span>
                    </div>
                    <p class="text-[11px] opacity-70 leading-relaxed">${e.body}</p>
                </div>
            `).join('');
        }
        renderWhatsNew();

        const WHATS_NEW_SEEN_KEY = 'whats_new_last_seen';
        function updateWhatsNewDot() {
            const lastSeen = localStorage.getItem(WHATS_NEW_SEEN_KEY) || '';
            const hasUnseen = WHATS_NEW_ENTRIES.some(e => e.date > lastSeen);
            document.getElementById('whatsNewDot').classList.toggle('hidden', !hasUnseen);
            // Mirror it onto the closed avatar. What's New moved inside the account menu, so its
            // own dot is now only visible to someone who already opened the menu - precisely the
            // person who didn't need telling. The badge has to live on the closed state.
            const badge = document.getElementById('avatarNewsBadge');
            if (badge) badge.classList.toggle('hidden', !hasUnseen);
        }
        updateWhatsNewDot();

        document.getElementById('whatsNewBtn').addEventListener('click', () => {
            const modal = document.getElementById('whatsNewModal');
            modal.classList.remove('hidden');
            modal.classList.add('flex');
            localStorage.setItem(WHATS_NEW_SEEN_KEY, WHATS_NEW_ENTRIES[0].date);
            // Recompute rather than hiding the dot by hand: there are two indicators now (the row
            // inside the menu and the badge on the closed avatar), and hiding one directly is how
            // they drift out of sync.
            updateWhatsNewDot();
        });
        function closeWhatsNewModal() {
            const modal = document.getElementById('whatsNewModal');
            modal.classList.add('hidden');
            modal.classList.remove('flex');
        }
        document.getElementById('whatsNewCloseBtn').addEventListener('click', closeWhatsNewModal);
        document.getElementById('whatsNewModal').addEventListener('click', (e) => {
            if (e.target === document.getElementById('whatsNewModal')) closeWhatsNewModal();
        });
        // --- /WHAT'S NEW ---

        function updateFeePreview() {
            const price = parseFloat(document.getElementById('refEstPrice').value);
            const feePct = parseFloat(document.getElementById('refFeePct').value) || 25;
            const preview = document.getElementById('refFeePreview');
            preview.textContent = price > 0 ? `Est. referral fee: ${formatMoney(price * 0.03 * feePct / 100)} (at 3% commission)` : '';
        }
        document.getElementById('refEstPrice').addEventListener('input', updateFeePreview);
        document.getElementById('refFeePct').addEventListener('input', updateFeePreview);

        document.getElementById('saveReferralBtn').addEventListener('click', async () => {
            if (!currentReferralPartner) return;
            const clientName = document.getElementById('refClientName').value.trim();
            if (!clientName) return alert('Client name is required.');

            const referral = {
                id: 'r-' + Date.now(),
                partnerId: currentReferralPartner.id,
                partnerName: currentReferralPartner.name,
                clientName: clientName,
                clientContact: document.getElementById('refClientContact').value.trim(),
                locationText: document.getElementById('refLocation').value.trim(),
                notes: document.getElementById('refNotes').value.trim(),
                feePct: parseFloat(document.getElementById('refFeePct').value) || 25,
                estPrice: parseFloat(document.getElementById('refEstPrice').value) || null,
                commissionPct: 3,
                status: 'sent',
                sentAt: Date.now(),
                updatedAt: Date.now()
            };

            if (isLoggedIn) {
                try {
                    const res = await apiPost('/api/referrals', referral);
                    if (res.ok) {
                        const created = await res.json();
                        referral.id = created.id;
                    }
                } catch (err) { console.error('Referral create sync failed', err); }
            }

            savedReferrals.push(referral);
            persistReferralsLocal();
            renderReferralList();

            // Prep the intro message the sender can text/email to the colleague right now
            const firstName = currentReferralPartner.name.split(/[ ,]/)[0];
            const message = `Hi ${firstName}, I've got a referral for you!\n\n` +
                `Client: ${clientName}${referral.clientContact ? ' (' + referral.clientContact + ')' : ''}` +
                `${referral.locationText ? '\nLooking in: ' + referral.locationText : ''}` +
                `${referral.notes ? '\nDetails: ' + referral.notes : ''}` +
                `\n\nReferral fee: ${referral.feePct}% as agreed - I'll follow up with the referral agreement. Thanks!` +
                `${userProfile.hubName ? '\n— ' + userProfile.hubName : ''}${userProfile.phone ? ' · ' + userProfile.phone : ''}`;

            let copied = false;
            try {
                await navigator.clipboard.writeText(message);
                copied = true;
            } catch (err) { /* clipboard blocked - referral is still saved, just no auto-copy */ }

            showReferralSentStep(currentReferralPartner, message, copied);
            setShareBanner(copied
                ? `📤 Referral for ${clientName} saved! Intro message copied - paste it into a text or email to ${firstName}.`
                : `📤 Referral for ${clientName} saved to your tracker.`, 'emerald');

            const section = document.getElementById('referralTrackerSection');
            if (section.classList.contains('accordion-hidden')) toggleAccordion('referralTrackerSection', 'referralTrackerArrow');
        });

        renderReferralList();
    })();
